<template>
  <div class="conference-room-layout" :class="{ 'caption-theme-light': isLightCaptionTheme }">
  <div class="room-overlay" v-show="roomOverlayed" :class="{ transparenthidden: roomSettingsPopup, 'fixed inset-0 z-[10] bg-[#121419]': secureSessionPopup }" @click="closePopups" ></div>
  <div class="room flex-column overflow-hidden mx-auto relative" ref="roomwrap" :style="{ maxWidth: mode.mode === 'default' ? '100%' : '100%'}"  :class="[{ anyPopupShown: anyPopupShown }, mode.mode]">
    <!-- if cc -->
    <div v-if="mode.mode === 'default'" class="session-title-bar absolute left-0 top-0 flex items-center gap-4 z-[5] w-full px-4 md:px-6 py-[10px] h-[82px]" :class="isLightCaptionTheme ? 'bg-[#F3F6FB]' : 'bg-layer-1'">
      <!-- mobile: title over a smaller host name; desktop: "Title | Host" on one line -->
      <div class="flex min-w-0 flex-col md:flex-row md:items-center md:gap-[5px]">
        <div class="session-title-text truncate text-[18px] text-text-neutral-medium font-semibold leading-[26px] md:leading-[28px]" data-testid="session-title">{{ event.title }}</div>
        <div v-if="event.speaker_name" class="session-title-text hidden md:block text-[18px] leading-[28px] text-text-neutral-medium font-medium">|</div>
        <div v-if="event.speaker_name" class="session-title-text truncate text-[14px] leading-[20px] md:text-[19px] md:leading-[30px] text-text-neutral-medium font-normal" data-testid="session-host">{{ event.speaker_name }}</div>
      </div>
      <button
          v-if="mode.mode === 'default'"
          type="button"
          class="session-title-action ml-auto shrink-0 text-[16px] leading-[24px] font-medium text-text-neutral-low hover:text-text-neutral-high cursor-pointer"
          :aria-pressed="!shouldShowOriginalWithTranslation()"
          data-testid="toggle-original-button"
          @click="toggleShowOriginal"
      >{{ showOriginalToggleLabel }}</button>
    </div>

    <!-- connection quality indicator -->
    <div
        v-if="mode.mode === 'default'"
        class="connection-indicator fixed left-4 z-[30] flex items-end gap-[3px] rounded-[10px] bg-layer-2/80 px-3 py-2"
        :class="{ 'opacity-40': connectionBars === 0 }"
        :style="{ '--toolbar-offset': `${connectionIndicatorOffset}px` }"
        :title="connectionLabel"
        :aria-label="connectionLabel"
        data-testid="connection-indicator"
    >
      <span
          v-for="bar in 4"
          :key="bar"
          class="connection-indicator__bar w-[4px] rounded-full bg-text-neutral-medium transition-opacity"
          :class="[
            bar <= connectionBars ? 'opacity-100' : 'opacity-25',
            bar === 1 ? 'h-[6px]' : '',
            bar === 2 ? 'h-[10px]' : '',
            bar === 3 ? 'h-[14px]' : '',
            bar === 4 ? 'h-[18px]' : '',
          ]"
      />
      <span v-if="connectionState === 'reconnecting' || connectionState === 'offline'" class="connection-indicator__label ml-2 text-[12px] leading-[18px] text-text-neutral-medium">{{ connectionLabel }}</span>
    </div>

    <!-- Green Screen top bar: tiles with menus on desktop and tablet, one scrolling row on phones.
         Hides with the Regular view's bars. -->
    <component
        :is="isMobile ? 'green-screen-bar-mobile' : 'green-screen-bar'"
        v-if="mode.mode === 'chromakey'"
        ref="ccToolbar"
        class="session-bar session-bar--top"
        :class="{ 'session-bar--hidden pointer-events-none': !barsVisible }"
        :font-size="fontSize"
        :min-font-size="minFontSize"
        :max-font-size="maxFontSize"
        :lines="chromakeyMaxLines"
        :width="chromakeyMargin === 'narrower' ? 'narrow' : 'wide'"
        :align="mode.align"
        :font="fontFamily === 'Noto Serif' ? 'serif' : 'sans'"
        @view="switchMode"
        @leave="closeConference"
        @step-font-size="changeFontSize"
        @set-font-size="setFontSize"
        @lines="chromakeyMaxLines = $event"
        @width="mode.margin = $event === 'narrow' ? 'narrower' : 'wider'"
        @align="mode.align = $event"
        @font="fontFamily = $event === 'serif' ? 'Noto Serif' : 'Inter'"
        @menu-state="chromakeyMenuOpen = $event"
        @shortcuts="shortcutsModal = true"
    >
      <template #background>
        <chromakey-color-picker v-model="settings.chroma_overlay_color" :swatches="swatches" />
      </template>
    </component>

    <!-- font size after a + / − key press -->
    <transition enter-active-class="transition-opacity duration-150" enter-from-class="opacity-0"
                leave-active-class="transition-opacity duration-300" leave-to-class="opacity-0">
      <div v-if="fontSizeIndicatorVisible" class="room-key-indicator" role="status" data-testid="font-size-indicator">
        {{ fontSizeIndicatorLabel }} {{ fontSize }}
      </div>
    </transition>

    <!-- Green Screen caption drag: the line the captions snapped to, and how to use the drag -->
    <template v-if="mode.mode === 'chromakey' && captionDragging">
      <div v-if="captionSnapGuides.x !== null" class="chromakey-snap-guide chromakey-snap-guide--vertical" :style="{ left: `${captionSnapGuides.x}px` }"></div>
      <div v-if="captionSnapGuides.y !== null" class="chromakey-snap-guide chromakey-snap-guide--horizontal" :style="{ top: `${captionSnapGuides.y}px` }"></div>
      <div class="room-key-indicator" data-testid="green-screen-drag-hint">{{ captionDragHint }}</div>
    </template>

    <!-- default top bar -->
    <default-bar :language="participant.language" :people="room.count" :view="mode.mode"
                 @view="switchMode" @settings="showRoomSettingsPopup" @session-settings="showSessionSettings"
                 @caption-settings="showCaptionSettings" @caption-settings-bar="showCaptionSettingsBar"
           @language="showLanguagePicker" @people="showParticipantsDrawer" @leave="closeConference" @views-menu-state="viewsMenuOpen = $event" @shortcuts="shortcutsModal = true" v-if="mode.mode === 'default'" class="session-bar session-bar--top" :class="{'session-bar--hidden pointer-events-none': !barsVisible, invisible: captionSettingsBar}"/>

    <!-- mobile Caption Settings: laid over the top bar (which keeps its place in the layout,
         so the captions don't jump) until it is closed -->
    <caption-settings-bar
        v-if="mode.mode === 'default' && captionSettingsBar"
        :font-size="fontSize"
        :min-font-size="minFontSize"
        :max-font-size="maxFontSize"
        :align="defaultTranscriptAlign"
        :font="defaultTranscriptFont"
        :theme="defaultTranscriptTheme"
        @close="captionSettingsBar = false"
        @step-font-size="changeFontSize"
        @set-font-size="setFontSize"
        @align="setDefaultTranscriptAlign"
        @font="setDefaultTranscriptFont"
        @theme="setDefaultTranscriptTheme"
    />


    <!-- session code & qr code popup (new Participants UI, behind the `session-participants-list` LD flag) -->
    <sheet-modal v-if="participantsListEnabled" v-model="roomCodePopup" exact-width="w-[432px]" scrollable>
      <session-info v-if="roomCodePopup" :room_code="event.room_code" @back="showMuteAudiencePopup"></session-info>
    </sheet-modal>

    <sheet-modal exact-width="w-[400px]" v-model="welcomePopup" @closed="handleWelcomePopupClosed" scrollable>
      <welcome-popup :participant="participant" :participants_number="room.count" @saved="handleWelcomePopupSaved"></welcome-popup>
    </sheet-modal>

    <!-- audience participation: new Participants popup (LD flag on) -->
    <sheet-modal v-if="participantsListEnabled" v-model="muteAudiencePopup" exact-width="w-[432px]" has-confirm scrollable>
      <host-controls @invite="closeHostControls(true)"
                     :key="`mute_toggle_${isParticipationEnabled}`"
                     :participants="sessionParticipants"
                     :participants_number="room.count"
                     :participants_limit="event.guests_limit"
                     :user-role="participant.session_role"
                     :participation_enabled="isParticipationEnabled"
                     @commit="emitMuteAudience"
      />
    </sheet-modal>

    <!-- audience participation: previous People popup (LD flag off) -->
    <sheet-modal v-else v-model="muteAudiencePopup" :has-confirm="false">
      <host-controls-legacy @closed="closeHostControls"
                            :key="`mute_toggle_legacy_${isParticipationEnabled}`"
                            :participants_number="room.count"
                            :participants_limit="event.guests_limit"
                            :user-role="participant.session_role"
                            :participation_enabled="isParticipationEnabled"
                            @commit="emitMuteAudience"
      />
    </sheet-modal>

    <!-- separate language selector -->
    <sheet-modal v-model="languagePickerPopup" exact-width="w-[400px]" :title="$t('languagepicker_title')" scrollable>
      <language-picker :initial="participant.language" @select="updateParticipantLanguage"></language-picker>
    </sheet-modal>

    <!-- password if secure session -->
    <!-- one-way binding: only a successful socket connection (or Leave) may close it -->
    <sheet-modal :model-value="secureSessionPopup" exact-width="w-[400px]" :blocking="true" :has-close="false" :scrollable="false">
      <session-secured v-if="secureSessionPopup" @provided="setPasswordProvided" @leave="leaveSecuredSession"></session-secured>
    </sheet-modal>

    <sheet-modal v-model="roomSettingsPopup" exact-width="w-[500px]" scrollable>
      <session-settings
          v-if="roomSettingsPopup"
          @closed="closePopups"
          @saved="setSettings"
          :participant="participant"
          :title="event.title"
          :event="event"
          :isHost="is_room_owner"
          :defaultSettings="settings"
          :min-font-size="minFontSize"
          :max-font-size="maxFontSize"
          salt="popup"
      >
      </session-settings>
    </sheet-modal>

    <!-- all settings popup -->
    <sheet-modal v-model="preferencesModal" scrollable :title="$t('settings_title')">

    </sheet-modal>

    <!-- signup popup -->
    <signup-popup v-show="!authorized && roomSignupPopup" @closed="closePopups"></signup-popup>

    <!-- end / leave session popup -->
    <leave-session-modal v-model="leaveSessionPopup" :is-host="isHost"></leave-session-modal>

    <!-- keyboard shortcuts cheat sheet: ? or the Settings / Views menus -->
    <keyboard-shortcuts-modal v-model="shortcutsModal" :mode="mode.mode" />

    <!-- tip shown on each entry into Green Screen -->
    <green-screen-shortcuts-tip v-if="mode.mode === 'chromakey'" @open-shortcuts="shortcutsModal = true" />

    <div
        ref="transcriptsWrap"
        class="transcripts-wrap"
        :style="{ width: transcriptsWidth}"
        :class="{
          'scrollable-default': mode.mode !== 'chromakey',
          'transcripts-wrap--review': mode.mode === 'default' && transcriptFollowMode === 'review',
        }"
    >
      <!-- default mode -->
      <div ref="defaultTranscriptsTrack" class="transcripts grow mt-auto" v-if="mode.mode === 'default'" :style="{ fontFamily: defaultTranscriptFontFamilyCss }">
        <div class="transcripts__feed-list" :class="{ 'transcripts__feed-list--narrow': effectiveTranscriptWidth === 'narrow' }">
          <div
              v-for="group in defaultTranscriptGroups"
              :key="group.key"
              class="transcripts__speaker-group"
              :class="{ 'transcripts__speaker-group--welcome': group.kind === 'welcome' }"
          >
            <div
                v-if="group.name || group.language || group.kind === 'welcome'"
                class="transcript__sendername transcript__sendername--marker"
                :class="{ 'transcript__sendername--welcome': group.kind === 'welcome' }"
                :data-speaker-language="group.language"
                :data-speaker-kind="group.kind"
            >
              <img v-if="group.kind === 'welcome'" src="@/assets/sorenson-forum-mark.svg" class="transcript__sender-flag transcript__sender-flag--welcome" alt="" />
              <img v-else-if="getFlagSrc(group.language)" :src="getFlagSrc(group.language)" class="transcript__sender-flag" alt="" />
              <span>{{ group.name }}</span>
            </div>

            <div
                v-for="item in group.items"
                :key="item.key"
                class="transcripts__item"
                :data-message-id="item.message && item.message.message_id ? item.message.message_id : null"
                :class="{
                  'transcripts__item--tip': item.type === 'welcome',
                  'transcripts__item--final': item.type === 'final',
                  'transcripts__item--partial': item.type === 'partial',
                  'transcripts__item--partial-new': item.type === 'partial' && shouldAnimateNewPartial(item.message),
                  'transcripts__item--continuation': isAsrContinuationMessage(item.message),
                }"
            >
              <template v-if="item.type === 'welcome'">
                <div class="transcript__original transcript__original--welcome">
                  <div class="transcript__text transcript__text--welcome">
                    <p v-for="(paragraph, index) in getDefaultTipMessageParagraphs()" :key="'tip_' + index">{{ paragraph }}</p>
                  </div>
                </div>
              </template>
              <template v-else>
                <div
                    v-if="shouldShowCaptionOriginal(item.message, item.type)"
                    class="transcript__original"
                    :class="{
                      'transcript__original--partial': item.type === 'partial',
                      noTranslations: shouldUseTranslationOriginalStyle(item.message),
                    }"
                >
                  <div class="transcript__text">{{ getCaptionOriginalText(item.message, item.type) }}</div>
                </div>
                <div
                    v-if="shouldShowTranslationSlot(item.message)"
                    class="transcript__translation"
                    :class="{
                      'transcript__translation--pending': shouldShowPendingTranslation(item.message),
                      'transcript__translation--reserved': shouldReserveTranslationSlot(item.message),
                      'transcript__translation--revealing': isTranslationRevealActive(item.message),
                    }"
                    :style="getTranslationRevealStyle(item.message)"
                >
                  <div v-if="hasTranslationForUser(item.message)" class="transcript__text transcript__text--translation">{{ getTranslationForUser(item.message).text }}</div>
                  <span v-else-if="shouldShowPendingTranslation(item.message)" class="transcript__typing-indicator" role="status" aria-label="Waiting for translation">
                    <span aria-hidden="true"></span>
                    <span aria-hidden="true"></span>
                    <span aria-hidden="true"></span>
                  </span>
                  <div v-else class="transcript__text transcript__text--translation-placeholder" aria-hidden="true">
                    {{ getTranslationPlaceholderText(item.message, item.type) }}
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
        <!--          <div class="transcripts__item transcripts__item&#45;&#45;partial" v-if="partial && hasOwnMessage() && awsIsRecording">
                    <div class="transcript__flag">
                      <img :src="'/data/flags/' + participant.language + '.png'" />
                    </div>
                    <div class="transcript__text">{{ partial.original.text }} {{ partial.concat }}</div>
                  </div>-->
      </div>

      <!-- chromakey mode -->
      <div class="transcripts grow mt-auto" v-else-if="mode.mode === 'chromakey'" :class="[ chromakeyMargin ]" :style="{ transform: `translate(${positionOffsetX}px, ${positionOffset}px)`, fontFamily: fontFamily }">
        <!-- Combined final messages into one single captions block -->
        <div class="transcripts__item transcripts__item--final">
          <div class="captions-wrapper" v-if="!chromakeyHideCaptions">
            <!-- While dragging, a faded copy of the captions outside the window shows the lines
                 the line count hides. It wraps exactly like the real track (same width, same
                 bottom anchor), and the window paints over it, so only the hidden lines show. -->
            <div
                v-if="captionDragging"
                class="chromakey-track chromakey-track--ghost"
                :class="{ 'chromakey-track--sliding': captionSliding }"
                :style="{ transform: `translateY(${captionSlideOffset}px)` }"
                aria-hidden="true"
            >
              <span class="chromakey-caption">{{ chromakeyCaptionText }}</span>
            </div>
            <!-- Fixed-height window onto the captions: the track is pinned to its bottom, so the
                 browser wraps the text at the real width and older lines slide up out of view.
                 It is also the drag handle for placing the captions on screen. -->
            <div
                ref="chromakeyViewport"
                class="chromakey-viewport"
                :class="{ 'chromakey-viewport--dragging': captionDragging }"
                data-testid="green-screen-captions"
                @pointerdown="startCaptionDrag"
                @pointermove="moveCaptionDrag"
                @pointerup="endCaptionDrag"
                @pointercancel="endCaptionDrag"
                @lostpointercapture="endCaptionDrag"
            >
              <div
                  ref="chromakeyTrack"
                  class="chromakey-track"
                  :class="{ 'chromakey-track--sliding': captionSliding }"
                  :style="{ transform: `translateY(${captionSlideOffset}px)` }"
              >
                <span class="chromakey-caption">{{ chromakeyCaptionText }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- scroller arrow -->
    <a href="javascript:;" class="chevron-to-bottom-scroller" v-show="scroller.scrolled" @click="resetScroller" v-if="mode.mode === 'default'">
      <svg width="22" height="13" viewBox="0 0 22 13" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.8348 13C10.5498 13.0016 10.2674 12.947 10.0036 12.8392C9.73975 12.7315 9.49981 12.5727 9.2975 12.3721L0.636763 3.71132C0.434884 3.50944 0.274746 3.26978 0.16549 3.00601C0.0562339 2.74225 4.25427e-09 2.45954 0 2.17404C-4.25427e-09 1.88854 0.056234 1.60584 0.16549 1.34207C0.274746 1.0783 0.434884 0.83864 0.636763 0.636762C0.838641 0.434883 1.07831 0.274744 1.34207 0.165488C1.60584 0.0562319 1.88854 -4.25427e-09 2.17404 0C2.45954 4.25428e-09 2.74225 0.0562319 3.00602 0.165488C3.26978 0.274744 3.50945 0.434883 3.71132 0.636762L10.8348 7.80352L17.9799 0.918236C18.1791 0.69676 18.4218 0.518763 18.6929 0.395377C18.964 0.271992 19.2577 0.205873 19.5555 0.201154C19.8533 0.196436 20.1489 0.253218 20.4238 0.367953C20.6987 0.482689 20.9469 0.652909 21.153 0.867964C21.3591 1.08302 21.5186 1.33829 21.6216 1.6178C21.7245 1.89731 21.7687 2.19506 21.7513 2.49242C21.7339 2.78977 21.6554 3.08035 21.5206 3.34596C21.3857 3.61158 21.1976 3.84652 20.9678 4.0361L12.3071 12.3937C11.9111 12.7755 11.3847 12.9922 10.8348 13Z" fill="#6CBFFF"/>
      </svg>
      <!-- scroller new dot -->
      <span class="new-indicator" v-show="scroller.hasNew"></span>
    </a>

  </div>

  <!-- keyboard input bar: lives outside the scrollable captions block (.transcripts-wrap)
       so it can't be clipped by its overflow or affected by its internal scroll -->
  <div class="keyboard-input-bar" ref="keyboardinputbar" v-show="mode.mode === 'default' && typing">
    <div class="transcripts__item transcripts__item--partial keyboard-input clear">
      <div class="transcript__flag">
        <img :src="'/data/flags/' + participant.language + '.png'" />
      </div>
      <contenteditable tag="div" ref="keyboard_input_field" id="keyboard_input_field" class="transcript__text" v-model="keyboardInputValue" :no-nl="true" :no-html="true" @returned="onEnterPressed" />
      <a href="javascript:;" class="keyboard-input__send" @click="sendKeyboardMessage(true)">
        <img src="@/assets/icons/paperplane.svg" />
      </a>
    </div>
  </div>

  <div
    class="listening-indicator"
    :aria-label="$t('common_listening')"
    v-if="mode.mode === 'default' && awsIsRecording && !barsVisible"
  ></div>

  <div class="toolbar session-bar session-bar--bottom md:py-4 md:px-4 lg:pb-10 justify-center w-full flex" ref="toolbarwrap" :class="{ transparenthidden: roomSettingsPopup, 'session-bar--hidden pointer-events-none touch-none': !barsVisible }">
    <div class="session-toolbar-panel flex flex-1 md:flex-none  bg-layer-2 dropshadow-heavy p-3 gap-3 md:rounded-[28px] justify-evenly md:justify-center" :class="{ notAllowed: !isAllowedToSpeak }">
      <asr-timer v-if="enableAsrTimer && asrTimer" :days="asrTimer.days" :hours="asrTimer.hours" :minutes="asrTimer.minutes" :seconds="asrTimer.seconds"></asr-timer>

      <div class="flex flex-[1_1_20%] md:flex-none">
        <TooltipWrap hover :content="$t('type_button_tooltip')" :arrow="false" side="top">
          <button class="bottom-bar-button min-w-[96px] flex-1" :class="{ 'bottom-bar-button--active': typing }" type="button" @click="keyboardInput">
            <img src="@/assets/icons/sorenson/keyboard.svg" alt="Type">
            <span>{{ $t('session_button_type')}}</span>
          </button>
        </TooltipWrap>
      </div>

      <div class="flex flex-[1_1_45%] min-w-[157px] xl:flex-none">
        <Popover class="flex flex-row w-full relative" v-slot="{ open }">
          <div class="flex flex-row rounded-[16px] overflow-hidden w-full">
            <button class="bottom-bar-button-split bottom-bar-button-split--main flex-1" type="button" :class="{ pressed: awsIsRecording }" :id="recordButtonId"
                    v-on="{
                    click: decideClickRecording,
                    contextmenu: preventContextMenu,
                  }"
            >
              <img src="@/assets/icons/sorenson/microphone-off-01.svg" alt="Record" v-if="!awsIsRecording">
              <img src="@/assets/icons/sorenson/microphone-01.svg" alt="Stop Recording" v-else>

              <span>{{
                  awsIsRecording ? $t('session_button_listening') + '...' : $t('session_button_mic')
                }}
                    </span>
            </button>

            <PopoverButton class="bottom-bar-button-split bottom-bar-button-split--secondary" :class="{ pressed: open }">
              <img src="@/assets/icons/chevron-up-white.svg" alt="Microphone Menu" :class="{ 'rotate-180': open }">
              <span>{{ $t('session_views') }}</span>
            </PopoverButton>
          </div>

          <transition enter-active-class="transition ease-out duration-200" enter-from-class="opacity-0 translate-y-1" enter-to-class="translate-y-0" leave-active-class="transition ease-in duration-150" leave-from-class="translate-y-0" leave-to-class="opacity-0 translate-y-1" @before-enter="microphoneMenuOpen = true" @after-leave="microphoneMenuOpen = false">
            <PopoverPanel class="absolute bottom-full z-[40] mb-2 flex bg-transparent" :class="isMobile ? 'left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)]' : 'left-0 w-screen max-w-sm'">
              <div class="w-full flex-auto rounded-[16px] bg-layer-3 p-4 max-h-[70vh] overflow-auto scrollbar-thumb-rounded-full scrollbar-thin scrollbar-track-rounded-full scrollbar scrollbar-thumb-[#f0f6ff57] scrollbar-track-transparent">
                <!-- Microphone Selection Section -->
                <h3 class="text-[14px] font-semibold text-text-neutral-low mb-3">{{ $t('select_microphone') !== 'select_microphone' ? $t('select_microphone') : 'Microphone' }}</h3>
                <div class="h-[1px] bg-white/10 mb-3 -mx-4"></div>

                <div class="flex flex-col gap-3">
                  <button
                      v-for="mic in availableMicrophones"
                      :key="mic.deviceId"
                      @click="selectMicrophone(mic.deviceId)"
                      class="flex items-start gap-1 text-left p-2 rounded-lg hover:bg-elements-neutral-3 transition-colors cursor-pointer"
                  >
                    <div class="w-5 h-5 flex-shrink-0">
                      <dynamic-svg
                          v-if="selectedMicrophoneId === mic.deviceId"
                          :src="'/svg/checkmark.svg'"
                          width="16"
                          height="16"
                          fill-opacity="1"
                      />
                    </div>
                    <div class="flex-1">
                      <div
                          :class="selectedMicrophoneId === mic.deviceId ? 'text-primary' : 'text-text-neutral-high'"
                          class="text-[16px] font-medium"
                          :title="mic.label"
                      >
                        {{ getMicrophoneName(mic) }}
                      </div>
                    </div>
                  </button>

                  <div v-if="availableMicrophones.length === 0" class="text-[14px] text-text-neutral-medium p-2">
                    {{ $t('no_microphones_found') !== 'no_microphones_found' ? $t('no_microphones_found') : 'No microphones detected' }}
                  </div>
                </div>
              </div>
            </PopoverPanel>
          </transition>
        </Popover>
      </div>


      <div class="flex flex-[1_1_20%] min-w-[96px] md:flex-none">
        <div class="flex flex-row w-full">
          <TooltipWrap hover :content="$t('read_button_tooltip')" :arrow="false" side="top">
            <button class="bottom-bar-button-small-split bottom-bar-button-small-split--main lg:min-w-[96px] flex-1 rounded-tl-[16px] rounded-bl-[16px]"
                    :class="{ pressed: !tts_muted}"
                    type="button"
                    :id="tts_muted ? 'unmute_button' : 'mute_button'"
                    @click="toggleTTS">
              <img v-if="!tts_muted" src="@/assets/icons/sorenson/volume-max.svg" alt="Unmute">
              <img v-else src="@/assets/icons/sorenson/volume-x.svg" alt="Mute">
              <span>{{ $t('session_read_btn') }}</span>
            </button>
          </TooltipWrap>

          <Popover class="flex flex-row w-full relative" v-slot="{ open }">
            <PopoverButton class="bottom-bar-button-small-split bottom-bar-button-small-split--secondary rounded-tr-[16px] rounded-br-[16px]" :class="{ pressed: open }">
              <img src="@/assets/icons/chevron-up-white.svg" alt="Speaker Menu" :class="{ 'rotate-180': open }">
              <span>{{ $t('session_views') }}</span>
            </PopoverButton>
            <transition enter-active-class="transition ease-out duration-200" enter-from-class="opacity-0 translate-y-1" enter-to-class="translate-y-0" leave-active-class="transition ease-in duration-150" leave-from-class="translate-y-0" leave-to-class="opacity-0 translate-y-1" @before-enter="speakerMenuOpen = true" @after-leave="speakerMenuOpen = false">
              <PopoverPanel class="absolute bottom-full z-[40] mb-2 flex bg-transparent" :class="isMobile ? 'right-0 w-[calc(100vw-2rem)]' : 'left-0 w-screen max-w-sm'">
                <div class="w-full flex-auto rounded-[16px] bg-layer-3 p-4 max-h-[70vh] overflow-auto scrollbar-thumb-rounded-full scrollbar-thin scrollbar-track-rounded-full scrollbar scrollbar-thumb-[#f0f6ff57] scrollbar-track-transparent">
                  <!-- Read Settings Section -->
                  <h3 class="text-[14px] font-semibold text-text-neutral-low mb-3">{{ $t('read_settings') !== 'read_settings' ? $t('read_settings') : 'Read Settings' }}</h3>
                  <div class="h-[1px] bg-white/10 mb-3 -mx-4"></div>

                  <div class="min-h-[46px] flex items-center justify-between mb-3">
                    <div class="text-[16px] leading-[1.375rem] text-text-neutral-high">{{ $t('speech_speed') !== 'speech_speed' ? $t('speech_speed') : 'Reading speed' }}</div>
                    <div>
                      <div class="flex items-center">
                        <div class="flex flex-row rounded-[10px] overflow-hidden">
                          <button :disabled="settings.tts_speed <= constraints.tts_speed_min" class="bg-button-secondary-mono-resting h-10 w-10 px-2 py-1 flex justify-center items-center cursor-pointer"
                                  @click="decrementSpeed" aria-label="Decrease Tts Speed">
                            <img width="28" height="28" src="@/assets/icons/minus.svg" alt="-" />
                          </button>
                          <button :disabled="settings.tts_speed >= constraints.tts_speed_max" class="bg-button-secondary-mono-resting h-10 w-10 px-2 py-1 flex justify-center items-center border-l-[2px] border-layer-3 cursor-pointer"
                                  @click="incrementSpeed" aria-label="Increase Tts Speed">
                            <img width="28" height="28" src="@/assets/icons/plus.svg" alt="+" />
                          </button>
                        </div>
                        <span class="text-[16px] leading-[1.375rem] text-text-neutral-high ml-1 min-w-[40px] text-right">{{ ttsSpeedDisplay }}</span>
                      </div>
                    </div>
                  </div>

                  <SwitchGroup as="div" class="flex justify-between mb-[14px]">
                      <span class="flex grow flex-col">
                        <SwitchLabel as="span" class="text-[16px] font-medium text-text-neutral-high" passive>{{ $t('session_setting_same_mute') }}</SwitchLabel>
                        <SwitchDescription as="span" class="text-[14px] text-white opacity-75 max-w-[95%] md:max-w-72">{{ $t('session_setting_same_mute_description') !== 'session_setting_same_mute_description' ? $t('session_setting_same_mute_description') : 'Only other languages will be read aloud.' }}</SwitchDescription>
                      </span>
                    <Switch v-model="settings.tts_same_muted" :class="[settings.tts_same_muted ? 'bg-primary' : 'bg-gray-700', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-gray-600 focus:ring-offset-2 focus:outline-hidden']">
                      <span aria-hidden="true" :class="[settings.tts_same_muted ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out']" />
                    </Switch>
                  </SwitchGroup>

                  <!-- Speaker Selection Section -->
                  <h3 class="text-[14px] font-semibold text-text-neutral-low mb-3">{{ $t('select_speaker') !== 'select_speaker' ? $t('select_speaker') : 'Speaker' }}</h3>
                  <div class="h-[1px] bg-white/10 mb-3 -mx-4"></div>

                  <div class="flex flex-col gap-3">
                    <button
                        v-for="speaker in availableSpeakers"
                        :key="speaker.deviceId"
                        @click="selectSpeaker(speaker.deviceId)"
                        class="flex items-start gap-1 text-left p-2 rounded-lg hover:bg-elements-neutral-3 transition-colors cursor-pointer"
                    >
                      <div class="w-5 h-5 flex-shrink-0">
                        <dynamic-svg
                            v-if="selectedSpeakerId === speaker.deviceId"
                            :src="'/svg/checkmark.svg'"
                            width="16"
                            height="16"
                            fill-opacity="1"
                        />
                      </div>
                      <div class="flex-1">
                        <div
                            :class="selectedSpeakerId === speaker.deviceId ? 'text-primary' : 'text-text-neutral-high'"
                            class="text-[16px] font-medium"
                            :title="speaker.label"
                        >
                          {{ getSpeakerName(speaker) }}
                        </div>
                      </div>
                    </button>
                    <div v-if="availableSpeakers.length === 0" class="text-[14px] text-text-neutral-medium p-2">
                      {{ $t('no_speakers_found') !== 'no_speakers_found' ? $t('no_speakers_found') : 'No speakers detected' }}
                    </div>

                  </div>
                </div>
              </PopoverPanel>
            </transition>
          </Popover>
        </div>
      </div>
    </div>
  </div>

  <!-- one drawer for both panels: two DrawerShift instances swapping in the same tick would
       emit open/close to the layout in an order that depends on template position -->
  <drawer-shift :open="sessionSettings || captionSettings" :title="settingsDrawerTitle" @closed="closeSettingsDrawer">
    <caption-settings
        v-if="captionSettings"
        :font-size="fontSize"
        :min-font-size="minFontSize"
        :max-font-size="maxFontSize"
        :align="defaultTranscriptAlign"
        :width="defaultTranscriptWidth"
        :font="defaultTranscriptFont"
        :theme="defaultTranscriptTheme"
        :show-header="false"
        salt="drawer"
        @step-font-size="changeFontSize"
        @set-font-size="setFontSize"
        @align="setDefaultTranscriptAlign"
        @width="setDefaultTranscriptWidth"
        @font="setDefaultTranscriptFont"
        @theme="setDefaultTranscriptTheme"
    />
    <session-settings
        v-else-if="sessionSettings"
        @closed="closePopups"
        @saved="setSettings"
        :participant="participant"
        :title="event.title"
        :event="event"
        :isHost="is_room_owner"
        :defaultSettings="settings"
        :min-font-size="minFontSize"
        :max-font-size="maxFontSize"
        :show-header="false"
        salt="drawer"
    >
    </session-settings>
  </drawer-shift>

  <!-- participants on mobile: bottom sheet instead of the side drawer -->
  <sheet-modal v-model="participantsModalOpen" :title="$te('participants_title') ? $t('participants_title') : 'Participants'" :has-confirm="false" scrollable>
    <session-participants-panel
        v-if="participantsModalOpen"
        :participants="sessionParticipants"
        :participants-count="room.count"
        :participants-limit="event.guests_limit"
        :can-invite="isHost"
        :available-actions="availableActions"
        :active-actions="activeActions"
        @invite="openInviteFromParticipantsDrawer"
        @action="handleParticipantsAction"
    />
  </sheet-modal>

  <!-- participants on desktop: right-side drawer -->
  <drawer-shift :open="participantsDrawerOpen" :title="$te('participants_title') ? $t('participants_title') : 'Participants'" @closed="participantsDrawerOpen = false">
    <session-participants-panel
        v-if="participantsDrawerOpen"
        :participants="sessionParticipants"
        :participants-count="room.count"
        :participants-limit="event.guests_limit"
        :can-invite="isHost"
        :available-actions="availableActions"
        :active-actions="activeActions"
        @invite="openInviteFromParticipantsDrawer"
        @action="handleParticipantsAction"
    />
  </drawer-shift>

  <div class="hidden-audio-players">
    <audio ref="start_rec_audio" src="/media/rec_start.opus.mp3"></audio>
    <audio ref="stop_rec_audio" src="/media/rec_stop.opus.mp3"></audio>
    <audio ref="tts_rec_audio" src="/media/rec_start.opus.mp3"></audio>
  </div>
  </div>
</template>

<script>
// @ is an alias to /src
import { mapState, mapGetters, mapMutations } from 'vuex';
import NoSleep from 'nosleep.js';
import MediaRecorder from 'opus-media-recorder';
// Use worker-loader
import EncoderWorker from 'worker-loader!opus-media-recorder/encoderWorker.js';
// You should use file-loader in webpack.config.js.
// See webpack example link in the above section for more detail.
//import OggOpusWasm from 'opus-media-recorder/OggOpusEncoder.wasm';
//import WebMOpusWasm from 'opus-media-recorder/WebMOpusEncoder.wasm';
import { io } from "socket.io-client";
import ss from "socket.io-stream";
import { v4 as uuidv4 } from 'uuid';
import {Howler} from 'howler';
import emitter from "@/services/emitter.service";
import permissions from "@/services/permissions.service";

import SignupPopup from '@/components/SignupPopup.vue';
import { useTimer } from 'vue-timer-hook';
import AsrTimer from '@/components/AsrTimer.vue';
import contenteditable from 'vue-contenteditable';
import GlossaryService from "@/services/glossary.service";

import SessionSecured from "@/components/SessionSecured.vue";
//import BaseButton from "@/components/ui/BaseButton.vue";
import SheetModal from "@/components/ui/SheetModal.vue";
import LeaveSessionModal from "@/components/session/LeaveSessionModal.vue";
import HostControls from "@/components/session/HostControls.vue";
import HostControlsLegacy from "@/components/session/HostControlsLegacy.vue";
import SessionInfo from "@/components/session/SessionInfo.vue";
import { useLDFlag } from 'launchdarkly-vue-client-sdk';
import WelcomePopup from '@/components/session/WelcomePopup.vue';
import LanguagePicker from "@/components/session/LanguagePicker.vue";
import DefaultBar from "@/components/session/DefaultBar.vue";
import GreenScreenBar from "@/components/session/GreenScreenBar.vue";
import GreenScreenBarMobile from "@/components/session/GreenScreenBarMobile.vue";
import ChromakeyColorPicker from "@/components/session/ChromakeyColorPicker.vue";
import KeyboardShortcutsModal from "@/components/session/KeyboardShortcutsModal.vue";
import GreenScreenShortcutsTip from "@/components/session/GreenScreenShortcutsTip.vue";
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import DynamicSvg from "@/components/ui/DynamicSvg.vue";
import { Switch, SwitchDescription, SwitchGroup, SwitchLabel } from '@headlessui/vue'
import { useTranscribe } from '@/composables/useTranscribe'
import { useConnectionQuality } from '@/composables/useConnectionQuality'
import { getPendo } from '@/services/pendo.service';
import { createSessionLogger } from '@/services/session-logger.service';
import { hasJoinedRoom, markRoomJoined, forgetRoom } from '@/utils/guestSession';
import { watch, toRefs } from 'vue'
import { Constants } from '@/cfg/constants'
import { translate } from '@/composables/useCaptionSettings';
import { ALT_KEY } from '@/composables/useKeyboardShortcuts';
import SessionSettings from "@/components/session/SessionSettings.vue";
import CaptionSettings from "@/components/session/CaptionSettings.vue";
import CaptionSettingsBar from "@/components/session/CaptionSettingsBar.vue";
import DrawerShift from "@/components/ui/DrawerShift.vue";
import SessionParticipantsPanel from '@/components/session/SessionParticipantsPanel.vue';
import TooltipWrap from "@/components/ui/TooltipWrap.vue";
import hotkeys from 'hotkeys-js';

Howler.volume(1);

// Non-standard options
const workerOptions = {
  encoderWorkerFactory: _ => new EncoderWorker(), // eslint-disable-line no-unused-vars
  OggOpusEncoderWasmPath: 'https://cdn.jsdelivr.net/npm/opus-media-recorder@latest/OggOpusEncoder.wasm',
  WebMOpusEncoderWasmPath: 'https://cdn.jsdelivr.net/npm/opus-media-recorder@latest/WebMOpusEncoder.wasm'
};

const noSleep = new NoSleep();

const TRANSCRIPT_ALIGNS = ['left', 'center', 'right'];
const TRANSCRIPT_WIDTHS = ['wide', 'narrow'];
const TRANSCRIPT_FONTS = Object.keys(Constants.CAPTION_FONTS);
const TRANSCRIPT_THEMES = ['dark', 'light'];
const CAPTION_NUDGE_KEYS = 'up, down, left, right, shift+up, shift+down, shift+left, shift+right';

export default {
  name: 'ConferenceRoom',
  setup() {
    // Env value is the fallback until LaunchDarkly responds (or when it is not configured).
    return {
      participantsListEnabled: useLDFlag('session-participants-list', Constants.PARTICIPANTS_LIST_ENABLED),
    };
  },
  components: {
    TooltipWrap,
    DrawerShift,
    DefaultBar,
    SessionParticipantsPanel,
    LanguagePicker,
    SheetModal,
    LeaveSessionModal,
    //BaseButton,
    SessionSecured,
    SignupPopup,
    AsrTimer,
    contenteditable,
    HostControls,
    HostControlsLegacy,
    SessionInfo,
    WelcomePopup,
    GreenScreenBar,
    GreenScreenBarMobile,
    ChromakeyColorPicker,
    KeyboardShortcutsModal,
    GreenScreenShortcutsTip,
    Popover,
    PopoverButton,
    PopoverPanel,
    DynamicSvg,
    Switch,
    SwitchGroup,
    SwitchDescription,
    SwitchLabel,
    SessionSettings,
    CaptionSettings,
    CaptionSettingsBar,
  },
  data() {
    return {
      isMobile: window.innerWidth <= 800,
      swatches:  ['#00FF00', '#4CBB17', '#228B22', '#0000FF', '#00FFFF'],
      asrTimer: {},
      idleTimer: {},
      user_id: false,
      recording: false,
      typing: false,
      recorder: false,
      apiSocket: false,
      userSocketActions: [],
      availableActions: ['raiseHand', 'thumbsUp', 'thumbsDown', 'clap', 'laugh', 'confused', 'heart'],
      userActionStateByUser: {},
      wakeLock: null,
      noSleepEnabled: false,
      pageWasHidden: false,
      sessionLogger: null,
      socketConnectedOnce: false,
      resumeCheckInFlight: false,
      connectionState: 'connected',
      connectionLatency: null,
      connectionBars: 4,
      connectionLabel: 'Good connection',
      connectionDisconnectReason: null,
      lastLoggedConnectionBars: null,
      scroller: {
        scrolled: false,
        hasNew: false,
        scrollTop: 0
      },
      transcriptTeleprompterFrame: null,
      transcriptTeleprompterTarget: 0,
      transcriptTeleprompterOffset: 0,
      transcriptTeleprompterLastFrameAt: 0,
      transcriptTeleprompterResizeFrame: null,
      transcriptTeleprompterObserver: null,
      transcriptTopSpacerFrame: null,
      transcriptTopSpacerTarget: 0,
      transcriptTopSpacerVisualOffset: 0,
      transcriptTopSpacerLastFrameAt: 0,
      transcriptFollowMode: 'live',
      transcriptAutoScrollActive: false,
      transcriptManualScrollHoldUntil: 0,
      transcriptReviewTouchY: null,
      mode: {
        mode: "default", // "default" | "chromakey"
        margin: "wider",
        align: "left",
      },
      holding: false,
      //asrSocketStream: false,
      room_token: false,
      transcripts: [],
      partials: [],
      partial: {
        message_id: false,
        original: {
          text: ""
        },
        concat: ""
      },
      partialIncoming: false,
      keyboardInputValue: "",
      started: false,
      speaked: false,
      room: {
        count: 0
      },
      participant: {},
      participants: [],
      custom_terms: [],
      isGuest: false,
      settingsMenuOpen: false,
      roomOverlayed: false,
      roomCodePopup: false,
      muteAudiencePopup: false,
      participantsDrawerOpen: false,
      participantsModalOpen: false,
      secureSessionPopup: false,
      roomSettingsPopup: false,
      sessionSettings: false,
      captionSettings: false,
      captionSettingsBar: false,
      roomSignupPopup: false,
      welcomePopup: false,
      fontSize: 20,
      fontDim: "px",
      maxFontSize: 150,
      minFontSize: 12,
      micActivated: false,
      openLanguageSelector: false,
      languagePickerPopup: false,
      ttsQueue: [],
      ttsPlaying: false,
      finalMessages: [],
      deletedMessages: [],
      socketRoomOwner: false,
      sessionPassword: "",
      messagesCount: 0,
      sessionStartTime: 0,
      sessionEndTime: 0,
      eventData: {},
      conference: {},
      customVocabularyList: {},
      sessionFeatures: {
        asr: {
          allowed: false,
          reason: "session_participation_is_disabled"
        }
      }, // new session object, TODO: will be refactored to SessionFeatures type with Typescript
      leaveSessionPopup: false,
      sessionActive: true, // simulates session state
      chromakeyMaxLines: 2,
      positionOffset: 0,
      positionOffsetX: 0,
      captionDragging: false,
      // Snap lines to draw while dragging, in screen coordinates; null when not snapped.
      captionSnapGuides: { x: null, y: null },
      captionDragPointer: 'mouse',
      fontFamily: 'Inter',
      chromakeyMenuOpen: false,
      shortcutsModal: false,
      fontSizeIndicatorVisible: false,
      fontSizeIndicatorTimer: null,
      tts_muted: true,
      preferencesModal: false,
      selectedMicrophoneId: null,
      availableMicrophones: [],
      availableSpeakers: [],
      selectedSpeakerId: null,
      constraints: {
        tts_speed_max: 200,
        tts_speed_min: 50,
        tts_speed_step: 10
      },
      barsVisible: true,
      barsManuallyHidden: false,
      toolbarHeight: 0,
      keyboardBarHeight: 0,
      toolbarResizeObserver: null,
      inactivityTimer: null,
      inactivityTimeout: 6000, // 6 seconds
      microphoneMenuOpen: false,
      speakerMenuOpen: false,
      viewsMenuOpen: false,
      readHoverActive: true,
      awsIsRecording: false,

      chromakeyIdleTimeoutMs: 10000,
      chromakeyHideCaptions: false,
      chromakeyIdleTimer: null,
      captionSlideOffset: 0,
      captionSliding: false,
      chromakeyTrackHeight: 0,
      chromakeyPillOverhang: 1,
      chromakeyLineBox: 0,
      currentTranscriptSpeakerName: '',
      currentTranscriptSpeakerLanguage: '',
      finalTranscriptDelayMs: 0,
      pendingFinalMessages: {},
      // Partials of a sender that were superseded by a newer message id but may still get
      // their final (the segmenter emits final X and the first partial of Y back-to-back).
      stalePartialTimers: {},
      stalePartialTimeoutMs: 6000,
      newPartialMessageIds: {},
      translationRevealStates: {},
      partialPlaybackStates: {},
      partialBufferWordThreshold: 3,
      partialBufferDelayMs: 0,
      partialTypeIntervalMs: 22,
      // Green Screen reveals whole words instead of characters, so it needs a slower tick:
      // at the character interval a word-sized unit would drain the buffer almost instantly.
      chromakeyTypeIntervalMs: 90,
      defaultTranscriptAlign: 'left',
      defaultTranscriptWidth: 'wide',
      defaultTranscriptFont: 'sans',
      defaultTranscriptTheme: 'dark',
      // AWS partial results are cumulative per ResultId; this cursor prevents re-emitting consumed items.
      asrSegmentState: {
        resultId: null,
        consumedItemCount: 0,
        lastItems: [],
        lastResult: null,
        lastEmittedMessageId: null,
        lastBoundaryReason: null,
        lastBoundaryEndsSentence: false,
      },
      // Segmenter modes, switchable from session settings (asr_segmenter_mode) so they can be
      // compared side by side. Every emitted caption carries the mode in asr_segment.
      asrSegmenterProfiles: {
        // Current behaviour: forced cuts land wherever the stable prefix happens to end.
        stability: {
          maxWords: 32,
          maxChars: 220,
          maxDurationSec: 10,
          speechOnlyDuration: false,
          softBoundaries: false,
          functionWordBackoff: 0,
        },
        // Forced cuts prefer commas/conjunctions and step back off function words ("have to |").
        phrase: {
          maxWords: 40,
          maxChars: 280,
          maxDurationSec: 12,
          speechOnlyDuration: true,
          softBoundaries: true,
          functionWordBackoff: 3,
        },
        // Cut on sentence terminators and AWS finals only; forced limits are a safety net.
        sentence: {
          maxWords: 80,
          maxChars: 600,
          maxDurationSec: 30,
          speechOnlyDuration: true,
          softBoundaries: false,
          functionWordBackoff: 0,
        },
        // Once the minimum word count is reached, cut at the first punctuation of any kind
        // (comma, semicolon, colon, dash, sentence terminator); forced limits are a safety net.
        punctuation: {
          maxWords: 40,
          maxChars: 280,
          maxDurationSec: 12,
          speechOnlyDuration: true,
          softBoundaries: false,
          functionWordBackoff: 0,
          cutOnAnyPunctuation: true,
        },
      },
      // Gaps longer than this are treated as pauses and excluded from speech-only duration.
      asrSpeechGapMaxSec: 1,
      // How many words back from the stable edge a forced cut may move to a comma/conjunction.
      asrSoftBoundaryLookbackWords: 8,
      asrNoSpaceLanguageMinChars: 18,
    }
  },
  methods: {
    ...mapMutations('nonpersist', ['setShowGuide']),
    closeConference() {
      this.leaveSessionPopup = true;
    },
    onResize(){
      this.isMobile = window.innerWidth <= 800;
      this.scheduleTranscriptTeleprompter(true);
      this.clampChromakeyPosition();
    },
    setRecording(status) {
      this.awsIsRecording = status;
    },
    setMicActivated(isActivated) {
      this.micActivated = isActivated;
    },
    mergeStringsIf(string1, string2) {
      let returnString = string1;
      if (string2) {
        returnString += ` ${string2}`;
      }
      if (this.partialIncoming) {
        returnString += ` ...`;
      }
      return returnString;
    },
    keyboardInput() {
      if (!this.isAllowedToSpeak) {
        this.$toast.info(this.sessionFeatures.asr.reason);
        return false;
      }
      if (this.typing) {
        this.typing = false;
        this.clearCurrenPartial(true);
      } else {
        this.typing = true;
        if (this.recording) {
          this.stopRecording();
        }

        this.startRoom();

        // $nextTick, not setTimeout: a timer is a fresh macrotask and iOS Safari drops the
        // user-gesture flag on it, so focus() lands but the virtual keyboard never opens.
        // A microtask stays inside the click's task, which keeps the gesture alive.
        this.$nextTick(this.focusKeyboardInput);
        this.resetScroller();
      }

    },
    focusKeyboardInput() {
      const field = document.querySelector('#keyboard_input_field');
      if (!field) {
        return;
      }

      field.focus();

      // focus() alone drops the caret at the start of a contenteditable in some browsers
      const selection = window.getSelection();
      if (selection && field.firstChild) {
        const range = document.createRange();
        range.selectNodeContents(field);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    },
    onEnterPressed() {
      this.sendKeyboardMessage(true);
    },
    decideClickRecording() {
      if (this.awsIsRecording) {
        this.stopRecording();
      } else {
        this.startRecording();
      }
      /*
      if (!this.isHoldToTalk) {
        if (this.recording) {
          this.stopRecording();
        } else {
          this.startRecording()
        }
      } else {
        return false;
      }*/
    },
    buildAsrMessage(messageId, text, isFinal, result) {
      return {
        message_id: messageId,
        original: {
          text: text,
        },
        is_final: isFinal,
        sender: {
          language: this.participant.language,
          name: this.participant.name,
          sender_id: this.user_id,
        },
        start_time: result.StartTime,
        end_time: result.EndTime,
      };
    },
    resetAsrSegmenter(options = {}) {
      const preserveCaptionContext = options && options.preserveCaptionContext === true;
      const previousState = this.asrSegmentState || {};

      this.asrSegmentState = {
        resultId: null,
        consumedItemCount: 0,
        lastItems: [],
        lastResult: null,
        lastEmittedMessageId: preserveCaptionContext ? previousState.lastEmittedMessageId : null,
        lastBoundaryReason: preserveCaptionContext ? previousState.lastBoundaryReason : null,
        lastBoundaryEndsSentence: preserveCaptionContext ? previousState.lastBoundaryEndsSentence : false,
      };
    },
    getAsrPartialResultsStabilityValue() {
      const value = this.settings && this.settings.asr_partial_results_stability;
      return ['low', 'medium', 'high'].includes(value) ? value : 'medium';
    },
    getAsrSegmentMinWordsValue() {
      const value = Number(this.settings && this.settings.asr_segment_min_words);
      if (Number.isFinite(value) && value >= 1) {
        return Math.floor(value);
      }

      return 5;
    },
    getAsrSegmenterMode() {
      const value = this.settings && this.settings.asr_segmenter_mode;
      return this.asrSegmenterProfiles[value] ? value : 'punctuation';
    },
    getAsrSegmentMaxDurationValue() {
      const value = Number(this.settings && this.settings.asr_segment_max_duration_sec);
      if (Number.isFinite(value) && value >= 1) {
        return value;
      }

      return this.asrSegmenterProfiles.stability.maxDurationSec;
    },
    getAsrSegmenterProfile() {
      const mode = this.getAsrSegmenterMode();
      const profile = this.asrSegmenterProfiles[mode];

      // Only the default mode exposes its duration limit as a session setting.
      if (mode === 'stability') {
        return { ...profile, maxDurationSec: this.getAsrSegmentMaxDurationValue() };
      }

      return profile;
    },
    isAsrSoftBoundaryPunctuation(content) {
      return [',', ';', ':', '—', '–', '、', '，', '；', '：'].includes(content);
    },
    isAsrConjunctionWord(content) {
      const word = String(content || '').toLowerCase();
      return [
        'and', 'but', 'or', 'so', 'because', 'while', 'whereas', 'although', 'though',
        'however', 'which', 'where', 'when', 'then', 'unless', 'until', 'since',
      ].includes(word);
    },
    isAsrFunctionWord(content) {
      const word = String(content || '').toLowerCase();
      return [
        'a', 'an', 'the', 'to', 'of', 'in', 'on', 'at', 'for', 'with', 'from', 'by', 'as',
        'into', 'about', 'over', 'and', 'or', 'but', 'so', 'nor', 'that', 'which', 'who',
        'whom', 'whose', 'if', 'than', 'then', 'is', 'are', 'was', 'were', 'be', 'been',
        'being', 'am', 'do', 'does', 'did', 'have', 'has', 'had', 'will', 'would', 'can',
        'could', 'should', 'shall', 'may', 'might', 'must', 'not', 'no', 'i', 'you', 'he',
        'she', 'it', 'we', 'they', 'my', 'your', 'his', 'her', 'its', 'our', 'their',
        'this', 'these', 'those', 'there', 'here', 'very', 'also', 'just', 'more', 'most',
        'some', 'any', 'what', 'how', 'why',
      ].includes(word);
    },
    normalizeAsrItems(items = [], isFinal = false) {
      if (!Array.isArray(items)) return [];

      return items
        .map((item) => ({
          content: String(item.Content || ''),
          type: String(item.Type || '').toLowerCase(),
          stable: isFinal || item.Stable === true,
          startTime: Number(item.StartTime),
          endTime: Number(item.EndTime),
        }))
        .filter((item) => item.content);
    },
    isNoSpaceAsrLanguage() {
      const language = String(this.participant.language || '').toLowerCase();
      return language.startsWith('zh') || language.startsWith('ja') || language.startsWith('th');
    },
    isAsrPunctuationItem(item) {
      return item && item.type === 'punctuation';
    },
    isAsrSentenceTerminator(content) {
      return ['.', '!', '?', '。', '！', '？', '؟', '۔', '…'].includes(content);
    },
    isAsrForcedBoundaryReason(reason) {
      return typeof reason === 'string' && reason.startsWith('forced_');
    },
    // Boundaries that split a sentence in the middle: the next segment continues it.
    isAsrMidSentenceBoundaryReason(reason) {
      return this.isAsrForcedBoundaryReason(reason) || reason === 'punctuation';
    },
    hasLeadingAsrSentenceTerminator(items = []) {
      for (const item of items) {
        if (!this.isAsrPunctuationItem(item)) {
          return false;
        }

        if (this.isAsrSentenceTerminator(item.content)) {
          return true;
        }
      }

      return false;
    },
    buildAsrTextFromItems(items = []) {
      const useSpaces = !this.isNoSpaceAsrLanguage();
      let text = '';

      items.forEach((item) => {
        const content = item.content;
        if (!content) return;

        if (this.isAsrPunctuationItem(item)) {
          text += content;
          return;
        }

        if (text && useSpaces) {
          text += ' ';
        }
        text += content;
      });

      return text.trim();
    },
    splitLeadingAsrPunctuationItems(items = []) {
      let firstContentIndex = 0;

      while (firstContentIndex < items.length && this.isAsrPunctuationItem(items[firstContentIndex])) {
        firstContentIndex++;
      }

      return {
        leadingItems: items.slice(0, firstContentIndex),
        contentItems: items.slice(firstContentIndex),
      };
    },
    buildAsrPunctuationText(items = []) {
      return items
        .filter((item) => this.isAsrPunctuationItem(item))
        .map((item) => item.content)
        .join('')
        .trim();
    },
    hasAsrLexicalText(text = '') {
      return /[\p{L}\p{N}]/u.test(String(text || ''));
    },
    isAsrPunctuationCharacter(character) {
      return /^[\p{P}\p{S}]$/u.test(String(character || ''));
    },
    getLastAsrCaptionMessage() {
      const messageId = this.asrSegmentState && this.asrSegmentState.lastEmittedMessageId;
      if (!messageId) {
        return null;
      }

      return this.transcripts.find((transcript) => transcript.message_id === messageId) ||
        this.partials.find((partial) => partial.message_id === messageId) ||
        (this.partial && this.partial.message_id === messageId ? this.partial : null);
    },
    appendAsrPunctuationToText(text, punctuationText) {
      const baseText = String(text || '').trimEnd();
      const cleanPunctuation = String(punctuationText || '').replace(/\s+/g, '');

      if (!baseText || !cleanPunctuation) {
        return baseText;
      }

      const baseCharacters = Array.from(baseText);
      const punctuationCharacters = Array.from(cleanPunctuation);
      const lastCharacter = baseCharacters[baseCharacters.length - 1];
      const firstPunctuation = punctuationCharacters[0];

      if (this.isAsrPunctuationCharacter(lastCharacter)) {
        if (this.isAsrSentenceTerminator(firstPunctuation) && !this.isAsrSentenceTerminator(lastCharacter)) {
          baseCharacters[baseCharacters.length - 1] = cleanPunctuation;
          return baseCharacters.join('');
        }

        return baseText;
      }

      return `${baseText}${cleanPunctuation}`;
    },
    async appendAsrPunctuationToPreviousCaption(punctuationItems = [], result = {}) {
      const punctuationText = this.buildAsrPunctuationText(punctuationItems);
      if (!punctuationText) {
        return false;
      }

      const previousMessage = this.getLastAsrCaptionMessage();
      if (!previousMessage || !previousMessage.message_id || !previousMessage.original) {
        return false;
      }

      const nextText = this.appendAsrPunctuationToText(previousMessage.original.text, punctuationText);
      if (!nextText || nextText === previousMessage.original.text) {
        return true;
      }

      const lastItem = [...punctuationItems].reverse().find((item) => Number.isFinite(item.endTime));
      const timing = {
        ...result,
        StartTime: previousMessage.start_time ?? result.StartTime,
        EndTime: lastItem ? lastItem.endTime : (previousMessage.end_time ?? result.EndTime),
      };
      const message = {
        ...this.buildAsrMessage(previousMessage.message_id, nextText, previousMessage.is_final !== false, timing),
        concat: '',
      };

      await this.sendMessage(message);
      this.handleIncomingCaptionMessage(message);
      return true;
    },
    countAsrWords(items = []) {
      return items.filter((item) => !this.isAsrPunctuationItem(item)).length;
    },
    getAsrSegmentDuration(items = []) {
      const firstItem = items.find((item) => Number.isFinite(item.startTime));
      const lastItem = [...items].reverse().find((item) => Number.isFinite(item.endTime));

      if (!firstItem || !lastItem) {
        return 0;
      }

      return Math.max(0, lastItem.endTime - firstItem.startTime);
    },
    getAsrSegmentSpeechDuration(items = []) {
      // Sum of word durations plus short gaps between them; long pauses are not speech.
      let total = 0;
      let previousEnd = null;

      items.forEach((item) => {
        if (!Number.isFinite(item.startTime) || !Number.isFinite(item.endTime)) {
          return;
        }

        if (previousEnd !== null) {
          const gap = item.startTime - previousEnd;
          if (gap > 0 && gap <= this.asrSpeechGapMaxSec) {
            total += gap;
          }
        }

        total += Math.max(0, item.endTime - item.startTime);
        previousEnd = Math.max(previousEnd ?? item.endTime, item.endTime);
      });

      return total;
    },
    getAsrSegmentEffectiveDuration(items = []) {
      return this.getAsrSegmenterProfile().speechOnlyDuration
        ? this.getAsrSegmentSpeechDuration(items)
        : this.getAsrSegmentDuration(items);
    },
    hasMinAsrSegmentLength(items = []) {
      const minWords = this.getAsrSegmentMinWordsValue();
      if (this.countAsrWords(items) >= minWords) {
        return true;
      }

      if (this.isNoSpaceAsrLanguage()) {
        return this.buildAsrTextFromItems(items).length >= this.asrNoSpaceLanguageMinChars;
      }

      return false;
    },
    getAsrForcedBoundaryReason(items = []) {
      if (!this.hasMinAsrSegmentLength(items)) {
        return null;
      }

      const profile = this.getAsrSegmenterProfile();

      if (this.countAsrWords(items) >= profile.maxWords) {
        return 'forced_word_limit';
      }

      if (this.buildAsrTextFromItems(items).length >= profile.maxChars) {
        return 'forced_char_limit';
      }

      if (this.getAsrSegmentEffectiveDuration(items) >= profile.maxDurationSec) {
        return 'forced_duration_limit';
      }

      return null;
    },
    refineAsrForcedBoundary(items = [], endIndex = -1) {
      // A forced cut lands at the edge of the stable prefix, which is grammatically random.
      // In phrase mode move it to the last soft boundary (comma, conjunction) or at least
      // step back off function words so the next segment does not start with "do something".
      const profile = this.getAsrSegmenterProfile();
      const fallback = { index: endIndex, refinement: null };

      if (endIndex < 0 || this.isNoSpaceAsrLanguage()) {
        return fallback;
      }

      const fits = (index) => index >= 0 && this.hasMinAsrSegmentLength(items.slice(0, index + 1));

      // Step back while the segment would end on a function word ("have to", "and then").
      // Stops at punctuation so a comma stays attached to the word before it.
      const backOffFunctionWords = (startIndex) => {
        let index = startIndex;
        let steps = 0;

        while (steps < profile.functionWordBackoff && index > 0) {
          const item = items[index];

          if (this.isAsrPunctuationItem(item) || !this.isAsrFunctionWord(item.content) || !fits(index - 1)) {
            break;
          }

          index--;
          steps++;
        }

        return index;
      };

      if (profile.softBoundaries) {
        // Only look a few words back from the edge, otherwise a comma early in a long
        // sentence would leave most of the text waiting for the next forced cut.
        let wordsAfter = 0;

        for (let i = endIndex; i >= 0 && wordsAfter <= this.asrSoftBoundaryLookbackWords; i--) {
          const item = items[i];

          if (this.isAsrPunctuationItem(item)) {
            if (this.isAsrSoftBoundaryPunctuation(item.content) && fits(i)) {
              return { index: i, refinement: 'soft_punctuation' };
            }
            continue;
          }

          // Cut before the conjunction so it opens the next segment.
          if (this.isAsrConjunctionWord(item.content) && i > 0 && fits(i - 1)) {
            return { index: backOffFunctionWords(i - 1), refinement: 'conjunction' };
          }

          wordsAfter++;
        }
      }

      if (profile.functionWordBackoff > 0) {
        const index = backOffFunctionWords(endIndex);

        if (index !== endIndex) {
          return { index, refinement: 'function_word_backoff' };
        }
      }

      return fallback;
    },
    // Punctuation that will become a segment boundary as soon as it stabilizes. The partial
    // is trimmed there so words after it are not typed into this caption only to jump into
    // the next one a moment later.
    findAsrPendingBoundary(items = []) {
      const cutOnAnyPunctuation = !!this.getAsrSegmenterProfile().cutOnAnyPunctuation;

      for (let i = 0; i < items.length; i++) {
        if (!this.isAsrPunctuationItem(items[i])) {
          continue;
        }

        const endsSentence = this.isAsrSentenceTerminator(items[i].content);
        const isSoftBoundary = cutOnAnyPunctuation && this.isAsrSoftBoundaryPunctuation(items[i].content);

        if ((endsSentence || isSoftBoundary) && this.hasMinAsrSegmentLength(items.slice(0, i + 1))) {
          return {
            index: i,
            reason: endsSentence ? 'pending_sentence_terminator' : 'pending_punctuation',
            endsSentence,
          };
        }
      }

      return null;
    },
    findAsrSegmentBoundary(items = [], isAwsFinal = false) {
      if (!items.length) return null;

      let stablePrefixEnd = -1;
      for (let i = 0; i < items.length; i++) {
        if (isAwsFinal || items[i].stable) {
          stablePrefixEnd = i;
        } else {
          break;
        }
      }

      if (stablePrefixEnd < 0) {
        return null;
      }

      const profile = this.getAsrSegmenterProfile();

      for (let i = 0; i <= stablePrefixEnd; i++) {
        if (!this.isAsrPunctuationItem(items[i])) {
          continue;
        }

        if (this.isAsrSentenceTerminator(items[i].content)) {
          if (isAwsFinal || this.hasMinAsrSegmentLength(items.slice(0, i + 1))) {
            return {
              index: i,
              reason: 'sentence_terminator',
              endsSentence: true,
            };
          }
          continue;
        }

        // Punctuation mode: any clause punctuation is a boundary once the segment is long enough.
        if (profile.cutOnAnyPunctuation &&
          this.isAsrSoftBoundaryPunctuation(items[i].content) &&
          this.hasMinAsrSegmentLength(items.slice(0, i + 1))) {
          return {
            index: i,
            reason: 'punctuation',
            endsSentence: false,
          };
        }
      }

      const stablePrefix = items.slice(0, stablePrefixEnd + 1);
      const forcedBoundaryReason = this.getAsrForcedBoundaryReason(stablePrefix);
      if (forcedBoundaryReason) {
        if (this.isAsrPunctuationItem(items[stablePrefixEnd + 1])) {
          return null;
        }

        const refined = this.refineAsrForcedBoundary(stablePrefix, stablePrefixEnd);
        let boundaryIndex = refined.index;

        // Keep trailing punctuation attached to the word it follows.
        if (boundaryIndex < stablePrefixEnd && this.isAsrPunctuationItem(items[boundaryIndex + 1])) {
          boundaryIndex++;
        }

        return {
          index: boundaryIndex,
          reason: forcedBoundaryReason,
          refinement: refined.refinement,
          endsSentence: false,
        };
      }

      if (isAwsFinal) {
        return {
          index: items.length - 1,
          reason: 'aws_final',
          endsSentence: false,
        };
      }

      return null;
    },
    buildAsrSegmentMetadata(result = {}, startItemIndex = 0, endItemIndex = null, boundary = null, items = []) {
      const awsResultId = result.resultId || '__default__';
      const activePartialSegment = this.partial?.asr_segment;
      const activePartialStartDecision = activePartialSegment &&
        activePartialSegment.aws_result_id === awsResultId &&
        activePartialSegment.item_start_index === startItemIndex
          ? activePartialSegment
          : null;
      const previousBoundaryReason = this.asrSegmentState?.lastBoundaryReason || null;
      const previousBoundaryEndsSentence = !!this.asrSegmentState?.lastBoundaryEndsSentence;
      const hasPreviousCaption = !!this.asrSegmentState?.lastEmittedMessageId;
      const startsAfterSentencePunctuation = this.hasLeadingAsrSentenceTerminator(items);
      const continuesAfterSegmenterCut = startItemIndex > 0 &&
        this.isAsrMidSentenceBoundaryReason(previousBoundaryReason);
      const continuesAfterAwsResult = startItemIndex === 0 &&
        hasPreviousCaption &&
        !previousBoundaryEndsSentence;
      const continuesPreviousSegment = activePartialStartDecision
        ? !!activePartialStartDecision.continues_previous_segment
        : continuesAfterSegmenterCut || continuesAfterAwsResult;
      const startsNewAwsResult = startItemIndex === 0;
      const startsNewSentence = activePartialStartDecision
        ? !!activePartialStartDecision.starts_new_sentence
        : !hasPreviousCaption || !continuesPreviousSegment;
      let startReason = 'segment_boundary';

      if (activePartialStartDecision?.start_reason) {
        startReason = activePartialStartDecision.start_reason;
      } else if (continuesAfterSegmenterCut) {
        startReason = 'segmenter_continuation';
      } else if (continuesAfterAwsResult) {
        startReason = 'aws_result_continuation';
      } else if (!hasPreviousCaption) {
        startReason = 'first_caption';
      } else if (previousBoundaryEndsSentence || startsAfterSentencePunctuation) {
        startReason = 'sentence_boundary';
      } else if (startsNewAwsResult) {
        startReason = 'aws_result_start';
      }

      const lastWordItem = [...items].reverse().find((item) => !this.isAsrPunctuationItem(item));

      return {
        aws_result_id: awsResultId,
        aws_is_final: !!result.isFinal,
        item_start_index: startItemIndex,
        item_end_index: Number.isFinite(endItemIndex) ? endItemIndex : startItemIndex,
        starts_new_aws_result: startsNewAwsResult,
        start_reason: startReason,
        boundary_reason: boundary?.reason || 'partial',
        boundary_refinement: boundary?.refinement || null,
        segmenter_mode: this.getAsrSegmenterMode(),
        word_count: this.countAsrWords(items),
        duration_sec: Number(this.getAsrSegmentDuration(items).toFixed(2)),
        speech_duration_sec: Number(this.getAsrSegmentSpeechDuration(items).toFixed(2)),
        last_word: lastWordItem ? lastWordItem.content : null,
        previous_boundary_reason: previousBoundaryReason,
        previous_boundary_ends_sentence: previousBoundaryEndsSentence,
        starts_after_sentence_punctuation: startsAfterSentencePunctuation,
        continues_previous_segment: continuesPreviousSegment,
        starts_new_sentence: startsNewSentence,
        ends_sentence: !!boundary?.endsSentence,
      };
    },
    updateAsrSegmentBoundaryState(boundary = null) {
      if (!boundary) {
        return;
      }

      this.asrSegmentState.lastBoundaryReason = boundary.reason || null;
      this.asrSegmentState.lastBoundaryEndsSentence = !!boundary.endsSentence;
    },
    capitalizeFirstAsrSentenceLetter(text = '') {
      return String(text || '').replace(/^(\P{L}*)(\p{Ll})/u, (match, prefix, letter, offset, value) => {
        const remainingCharacters = Array.from(value.slice(prefix.length));
        const nextCharacter = remainingCharacters[1] || '';

        if (nextCharacter && /\p{Lu}/u.test(nextCharacter)) {
          return match;
        }

        return `${prefix}${letter.toLocaleUpperCase()}`;
      });
    },
    lowerFirstAsrSentenceLetter(text = '') {
      return String(text || '').replace(/^(\P{L}*)(\p{Lu})/u, (match, prefix, letter, offset, value) => {
        const remainingCharacters = Array.from(value.slice(prefix.length));
        const nextCharacter = remainingCharacters[1] || '';

        if (nextCharacter && /\p{Lu}/u.test(nextCharacter)) {
          return match;
        }

        return `${prefix}${letter.toLocaleLowerCase()}`;
      });
    },
    applyAsrSegmentCasing(text = '', asrSegment = null) {
      const cleanText = String(text || '').trim();
      if (!cleanText || !asrSegment || this.isNoSpaceAsrLanguage()) {
        return cleanText;
      }

      if (!asrSegment.starts_new_sentence || asrSegment.continues_previous_segment) {
        return this.lowerFirstAsrSentenceLetter(cleanText);
      }

      return this.capitalizeFirstAsrSentenceLetter(cleanText);
    },
    async emitAsrCaptionMessage(text, isFinal, result, items = [], asrSegment = null) {
      let segmentItems = Array.isArray(items) ? items : [];
      let cleanText = '';

      if (segmentItems.length) {
        const { leadingItems, contentItems } = this.splitLeadingAsrPunctuationItems(segmentItems);
        if (leadingItems.length) {
          await this.appendAsrPunctuationToPreviousCaption(leadingItems, result);
        }

        segmentItems = contentItems;
        cleanText = this.buildAsrTextFromItems(segmentItems);
      } else {
        cleanText = (text || '').trim();
      }

      cleanText = this.applyAsrSegmentCasing(cleanText, asrSegment);
      if (!cleanText || !this.hasAsrLexicalText(cleanText)) return;

      this.setMessageId();

      const firstItem = segmentItems.find((item) => Number.isFinite(item.startTime));
      const lastItem = [...segmentItems].reverse().find((item) => Number.isFinite(item.endTime));
      const timing = {
        ...result,
        StartTime: firstItem ? firstItem.startTime : result.StartTime,
        EndTime: lastItem ? lastItem.endTime : result.EndTime,
      };
      const message = {
        ...this.buildAsrMessage(this.partial.message_id, cleanText, isFinal, timing),
        concat: '',
        ...(asrSegment ? { asr_segment: asrSegment } : {}),
      };

      this.partial = message;
      await this.sendMessage(message);
      this.handleIncomingCaptionMessage(message);
      this.asrSegmentState.lastEmittedMessageId = message.message_id;

      if (isFinal) {
        this.partial = { message_id: false, original: { text: '' }, concat: '' };
      }
    },
    async streamAsrResultDirect(result) {
      const text = (result.text || '').trim();
      if (!text) {
        return;
      }

      await this.emitAsrCaptionMessage(text, result.isFinal, result);
    },
    async processSegmentedAsrResult(result) {
      const resultId = result.resultId || '__default__';
      if (this.asrSegmentState.resultId !== resultId) {
        if (this.asrSegmentState.resultId && this.partial.message_id && this.partial.original?.text) {
          await this.flushCurrentAsrPartialOnStop();
          this.partial = { message_id: false, original: { text: '' }, concat: '' };
        }

        const previousState = this.asrSegmentState || {};
        this.asrSegmentState = {
          resultId,
          consumedItemCount: 0,
          lastItems: [],
          lastResult: null,
          lastEmittedMessageId: previousState.lastEmittedMessageId || null,
          lastBoundaryReason: previousState.lastBoundaryReason || null,
          lastBoundaryEndsSentence: !!previousState.lastBoundaryEndsSentence,
        };
      }

      const items = this.normalizeAsrItems(result.items, result.isFinal);
      if (!items.length) {
        await this.streamAsrResultDirect(result);
        if (result.isFinal) {
          this.resetAsrSegmenter({ preserveCaptionContext: true });
        }
        return;
      }
      this.asrSegmentState.lastItems = items;
      this.asrSegmentState.lastResult = result;

      let tail = items.slice(this.asrSegmentState.consumedItemCount);
      if (!tail.length) {
        if (result.isFinal) {
          this.resetAsrSegmenter({ preserveCaptionContext: true });
        }
        return;
      }

      while (tail.length) {
        const boundary = this.findAsrSegmentBoundary(tail, result.isFinal);
        if (!boundary) {
          break;
        }

        const segmentStartItemIndex = this.asrSegmentState.consumedItemCount;
        const segmentEndItemIndex = segmentStartItemIndex + boundary.index;
        const segmentItems = tail.slice(0, boundary.index + 1);
        const segmentText = this.buildAsrTextFromItems(segmentItems);
        const asrSegment = this.buildAsrSegmentMetadata(
          result,
          segmentStartItemIndex,
          segmentEndItemIndex,
          boundary,
          segmentItems,
        );
        await this.emitAsrCaptionMessage(segmentText, true, result, segmentItems, asrSegment);
        this.updateAsrSegmentBoundaryState(boundary);

        this.asrSegmentState.consumedItemCount += boundary.index + 1;
        tail = items.slice(this.asrSegmentState.consumedItemCount);
      }

      if (tail.length) {
        const pendingBoundary = this.findAsrPendingBoundary(tail);
        const partialItems = pendingBoundary
          ? tail.slice(0, pendingBoundary.index + 1)
          : tail;
        const partialText = this.buildAsrTextFromItems(partialItems);
        const partialStartItemIndex = this.asrSegmentState.consumedItemCount;
        const partialBoundary = {
          reason: pendingBoundary ? pendingBoundary.reason : 'partial',
          endsSentence: !!(pendingBoundary && pendingBoundary.endsSentence),
        };
        const asrSegment = this.buildAsrSegmentMetadata(
          result,
          partialStartItemIndex,
          partialStartItemIndex + partialItems.length - 1,
          partialBoundary,
          partialItems,
        );
        await this.emitAsrCaptionMessage(partialText, false, result, partialItems, asrSegment);
      }

      if (result.isFinal) {
        this.resetAsrSegmenter({ preserveCaptionContext: true });
      }
    },
    async startRecording(sound = true) {
      console.log("starting");

      if (!this.isAllowedToSpeak) {
        this.$toast.info(this.sessionFeatures.asr.reason);
        this.speaked = true;
        return false;
      } else if (this.enableAsrTimer && (this.asrTimer.isExpired || this.asrTimeLeft <= 0) && !this.participant.features.asr.overdraft){
        this.$toast.info('error_no_asr_left');
        this.speaked = true;
        return false;
      }

      this.startRoom();
      if (this.typing) {
        this.typing = false;
        this.clearCurrenPartial(true);
      }
      this.stopTTS();
      if (sound) {
        this.soundPlayerRecStart.play();
      }
      this.resetAsrSegmenter();
      console.log('[CustomVocabulary] resolving vocabulary for session start', {
        language: this.getLanguageAsrCode,
        vocabularyName: this.customVocabularyName,
        list: this.customVocabularyList,
      });
      await this.startFn(this.room_token, this.getLanguageAsrCode, this.event.id, async (result) => {
        this.chromakeyTouch();
        await this.processSegmentedAsrResult(result);
      }, {
        deviceId: this.selectedMicrophoneId,
        partialResultsStability: this.getAsrPartialResultsStabilityValue(),
        vocabularyName: this.customVocabularyName,
      })

      this.ttsPlaying = false;
      this.ttsQueue = [];

      if(this.settings.is_autostop_enabled && !this.isHoldToTalk && sound) {
        this.restartIdleTimer();
      }
    },
    async stopRecording(forced = true) {
      if (forced) {
        this.soundPlayerRecStop.play()
        this.holding = false
      }

      const didFlushPartial = await this.flushCurrentAsrPartialOnStop();

      await this.stopFn()

      if (this.enableAsrTimer) this.asrTimer.pause()
      this.clearCurrenPartial(!didFlushPartial)

      this.setMicActivated(false)
      this.setRecording(false)
      this.resetAsrSegmenter()

      if (this.settings.is_autostop_enabled && !this.isHoldToTalk && this.idleTimer) {
        this.restartIdleTimer(false)
      }
    },
    async flushCurrentAsrPartialOnStop() {
      const lastItems = this.asrSegmentState?.lastItems || [];
      const consumedItemCount = this.asrSegmentState?.consumedItemCount || 0;

      if (lastItems.length > consumedItemCount) {
        const result = this.asrSegmentState.lastResult || {};
        let tail = lastItems.slice(consumedItemCount);
        let flushedAny = false;

        while (tail.length) {
          const boundary = this.findAsrSegmentBoundary(tail, true);
          if (!boundary) {
            break;
          }

          const segmentStartItemIndex = this.asrSegmentState.consumedItemCount;
          const segmentEndItemIndex = segmentStartItemIndex + boundary.index;
          const segmentItems = tail.slice(0, boundary.index + 1);
          const segmentText = this.buildAsrTextFromItems(segmentItems);
          const asrSegment = this.buildAsrSegmentMetadata(
            result,
            segmentStartItemIndex,
            segmentEndItemIndex,
            boundary,
            segmentItems,
          );
          await this.emitAsrCaptionMessage(segmentText, true, result, segmentItems, asrSegment);
          this.updateAsrSegmentBoundaryState(boundary);

          this.asrSegmentState.consumedItemCount += boundary.index + 1;
          tail = lastItems.slice(this.asrSegmentState.consumedItemCount);
          flushedAny = true;
        }

        if (flushedAny) {
          return true;
        }
      }

      const messageId = this.partial?.message_id;
      const text = (this.partial?.original?.text || '').trim();
      if (!messageId || !text) {
        return false;
      }

      const finalMsg = {
        ...this.partial,
        is_final: true,
        concat: '',
        original: {
          ...(this.partial.original || {}),
          text,
        },
        sender: this.partial.sender || {
          language: this.participant.language,
          name: this.participant.name,
          sender_id: this.user_id,
        },
      };

      await this.sendMessage(finalMsg);
      this.handleIncomingCaptionMessage(finalMsg);
      return true;
    },

    async restartRecording(forced = false) {
      if (!this.awsIsRecording) return

      if (this._restartInFlight) return
      this._restartInFlight = true

      try {
        await this.stopRecording(forced)

        await new Promise(r => setTimeout(r, 150))

        await this.startRecording(false)
      } finally {
        this._restartInFlight = false
      }
    },

    setRoomToken() {
      if (this.user && this.token) {
        this.room_token = this.token;
      } else if (this.event && this.event.guest_token) {
        this.room_token = this.event.guest_token;
      }
    },
    setSettings(roomSettings) {
      if (roomSettings.settings) {
        this.$store.dispatch("event/saveSettings", roomSettings.settings);
        this.fontSize = roomSettings.settings.fontSize || this.fontSize;
      }

      if (roomSettings.profile) {
        this.updateParticipant(roomSettings.profile);
      }

      if (roomSettings.title) {
        this.$store.commit("event/setEventTitle", roomSettings.title);
      }
    },
    toggleTTS() {
      this.tts_muted = !this.tts_muted;
      if (this.tts_muted) {
        this.stopTTS();
      }
    },
    showRoomCodePopup() {
      this.participantsDrawerOpen = false;
      this.participantsModalOpen = false;
      this.sessionSettings = false;
      this.roomCodePopup = true;
      this.roomOverlayed = true;
      this.settingsMenuOpen = true;
    },
    showWelcomePopup() {
      this.welcomePopup = true;
      this.roomOverlayed = true;
    },
    handleWelcomePopupClosed() {
      // Outside-click close should still propagate updated guest profile to other participants.
      if (this.participant && typeof this.participant.name === 'string') {
        const trimmedName = this.participant.name.trim();
        if (trimmedName) {
          this.updateParticipant({
            language: this.participant.language,
            name: trimmedName,
            voice: this.participant.voice,
          });
        }
      }

      markRoomJoined(this._roomCode);
      this.closePopups();
    },
    handleWelcomePopupSaved(roomSettings) {
      markRoomJoined(this._roomCode);
      this.setSettings(roomSettings);
    },
    showRoomSettingsPopup() {
      this.participantsDrawerOpen = false;
      this.participantsModalOpen = false;
      this.sessionSettings = false;
      this.roomCodePopup = false;
      this.roomSettingsPopup = true;
      this.settingsMenuOpen = true;
    },
    // Mobile: Caption Settings open as a bar over the top bar and stay until closed.
    showCaptionSettingsBar() {
      this.closePopups();
      this.closeSettingsDrawer();
      this.captionSettingsBar = true;
    },
    showSessionSettings() {
      this.participantsDrawerOpen = false;
      this.participantsModalOpen = false;
      this.roomCodePopup = false;
      this.roomSettingsPopup = false;

      this.captionSettings = false;
      this.sessionSettings = !this.sessionSettings;
    },
    showParticipantsDrawer() {
      // LD flag off: previous People popup on both mobile and desktop.
      if (!this.participantsListEnabled) {
        this.showMuteAudiencePopup();
        return;
      }

      // Mobile: bottom sheet (as before). Desktop: right-side drawer.
      const openModal = this.isMobile && !this.participantsModalOpen;
      const openDrawer = !this.isMobile && !this.participantsDrawerOpen;

      this.closePopups();
      this.closeSettingsDrawer();
      this.participantsModalOpen = openModal;
      this.participantsDrawerOpen = openDrawer;
    },
    showCaptionSettings() {
      this.participantsDrawerOpen = false;
      this.participantsModalOpen = false;
      this.roomCodePopup = false;
      this.roomSettingsPopup = false;

      this.sessionSettings = false;
      this.captionSettings = !this.captionSettings;
    },
    closeSettingsDrawer() {
      this.sessionSettings = false;
      this.captionSettings = false;
    },
    showMuteAudiencePopup() {
      this.participantsDrawerOpen = false;
      this.participantsModalOpen = false;
      this.sessionSettings = false;
      this.muteAudiencePopup = true;
      this.roomCodePopup = false;
      this.roomSettingsPopup = false;
    },
    openInviteFromParticipantsDrawer() {
      this.participantsDrawerOpen = false;
      this.participantsModalOpen = false;
      this.showRoomCodePopup();
    },
    showLanguagePicker() {
      this.languagePickerPopup = true;
    },
    closePopups() {
      this.roomCodePopup = false;
      this.roomSettingsPopup = false;
      this.roomSignupPopup = false;
      this.muteAudiencePopup = false;

      this.settingsMenuOpen = false;
      if (!this.secureSessionPopup) {
        this.roomOverlayed = false;
      }
    },
    closeHostControls(openInvite) {
      this.muteAudiencePopup = false;

      if (openInvite) {
        this.roomCodePopup = true;
      } else {
        this.closePopups();
      }
    },
    switchMode(mode) {
      this.resetScroller();
      this.resetBarMenuState();
      this.captionSettingsBar = false;
      this.barsVisible = !this.barsManuallyHidden;
      this.mode.mode = mode;

      this.resetChromakeySlide();

      if (mode === 'chromakey') {
        this.chromakeyTouch();
        this.$nextTick(() => {
          this.updateChromakeySlide();
          this.clampChromakeyPosition();
        });
      } else {
        this.chromakeyIdleStop();
      }

      // Both views' top bars slide away after a while without activity.
      if (!this.barsManuallyHidden) {
        this.startInactivityTimer();
      } else {
        this.clearInactivityTimer();
      }
      this.closePopups();
      this.closeSettingsDrawer();
      this.participantsDrawerOpen = false;
      this.participantsModalOpen = false;
    },
    setDefaultTranscriptAlign(align) {
      if (!TRANSCRIPT_ALIGNS.includes(align)) {
        return;
      }

      this.defaultTranscriptAlign = align;
      // Persisted with the event settings so the choice survives into the next session.
      this.$store.commit('event/setTranscriptAlign', align);
    },
    setDefaultTranscriptWidth(width) {
      if (!TRANSCRIPT_WIDTHS.includes(width)) {
        return;
      }

      this.defaultTranscriptWidth = width;
      this.$store.commit('event/setTranscriptWidth', width);
    },
    setDefaultTranscriptFont(font) {
      if (!TRANSCRIPT_FONTS.includes(font)) {
        return;
      }

      this.defaultTranscriptFont = font;
      this.$store.commit('event/setTranscriptFont', font);
    },
    setDefaultTranscriptTheme(theme) {
      if (!TRANSCRIPT_THEMES.includes(theme)) {
        return;
      }

      this.defaultTranscriptTheme = theme;
      this.$store.commit('event/setTranscriptTheme', theme);
    },
    applyStoredCaptionSettings() {
      const settings = this.settings || {};
      if (TRANSCRIPT_ALIGNS.includes(settings.transcriptAlign)) {
        this.defaultTranscriptAlign = settings.transcriptAlign;
      }
      if (TRANSCRIPT_WIDTHS.includes(settings.transcriptWidth)) {
        this.defaultTranscriptWidth = settings.transcriptWidth;
      }
      if (TRANSCRIPT_FONTS.includes(settings.transcriptFont)) {
        this.defaultTranscriptFont = settings.transcriptFont;
      }
      if (TRANSCRIPT_THEMES.includes(settings.transcriptTheme)) {
        this.defaultTranscriptTheme = settings.transcriptTheme;
      }
    },
    changeFontSize(direction) {
      return this.setFontSize(parseInt(this.fontSize) + (direction * 4));
    },
    // Briefly shows the font size after a keyboard change: the toolbar with the size control
    // is often hidden, so the key press needs its own feedback. Also shows at the limits.
    showFontSizeIndicator() {
      this.fontSizeIndicatorVisible = true;
      clearTimeout(this.fontSizeIndicatorTimer);
      this.fontSizeIndicatorTimer = setTimeout(() => {
        this.fontSizeIndicatorVisible = false;
      }, 1200);
    },
    setFontSize(value) {
      const size = Math.round(Number(value));
      if (!Number.isFinite(size)) {
        return false;
      }

      // Clamped rather than rejected, so a typed-in value or a step from just below the
      // limit still lands on the boundary.
      const newFontSize = Math.min(this.maxFontSize, Math.max(this.minFontSize, size));
      if (newFontSize === this.fontSize) {
        return false;
      }

      this.fontSize = newFontSize;
      // Persisted with the event settings so the choice survives into the next session.
      this.$store.commit('event/setFontSize', newFontSize);
      if(this.mode.mode === 'chromakey'){
        this.resetScroller();
      }
    },
    applyStoredFontSize() {
      const defaults = (this.$store.state.event && this.$store.state.event.defaults) || {};
      const storedFontSize = Number(this.settings && this.settings.fontSize);
      const hasStoredChoice = Number.isFinite(storedFontSize) && storedFontSize !== Number(defaults.fontSize);

      // The store wins over the legacy localStorage value once the user has changed the size.
      // While the store still holds the default, the legacy value (already applied by
      // loadChromakeySettings) is kept and copied into the store below.
      if (hasStoredChoice) {
        this.fontSize = Math.min(this.maxFontSize, Math.max(this.minFontSize, storedFontSize));
      }

      if (this.settings && this.settings.fontSize !== this.fontSize) {
        this.$store.commit('event/setFontSize', this.fontSize);
      }
    },
    // Allowed range for the Green Screen caption offsets: the caption window stays inside the
    // room, below the toolbar and a little clear of the bottom edge. Measured live, since it
    // depends on the window size and on the caption window's size.
    getChromakeyPositionBounds() {
      const x = this.positionOffsetX;
      const y = this.positionOffset;
      const bounds = { minX: x, maxX: x, minY: y, maxY: y };

      const viewportEl = this.$refs.chromakeyViewport;
      const roomEl = this.$refs.roomwrap;
      if (!viewportEl || !roomEl) {
        return bounds;
      }

      const viewportRect = viewportEl.getBoundingClientRect();
      const roomRect = roomEl.getBoundingClientRect();
      // The toolbar is see-through until hovered but still takes the pointer, so the captions
      // can't go under it at all. 82px is its CSS height, for when it isn't rendered yet.
      const toolbarHeight = this.$refs.ccToolbar?.$el?.offsetHeight || 82;
      const bottomGap = 30;

      bounds.minX = x - (viewportRect.left - roomRect.left);
      bounds.maxX = x + (roomRect.right - viewportRect.right);
      // Wider than the room (e.g. Wide on a phone): no sideways room, so keep it centred.
      if (bounds.minX > bounds.maxX) {
        bounds.minX = bounds.maxX = (bounds.minX + bounds.maxX) / 2;
      }

      bounds.minY = y - (viewportRect.top - roomRect.top - toolbarHeight);
      bounds.maxY = y + (roomRect.bottom - bottomGap - viewportRect.bottom);
      // Taller than the space left: staying clear of the toolbar wins over the bottom gap.
      if (bounds.minY > bounds.maxY) {
        bounds.maxY = bounds.minY;
      }
      return bounds;
    },
    setChromakeyPosition(x, y, bounds) {
      this.positionOffsetX = Math.round(Math.min(Math.max(x, bounds.minX), bounds.maxX));
      this.positionOffset = Math.round(Math.min(Math.max(y, bounds.minY), bounds.maxY));
    },
    // Pulls a saved or previous position back on screen after the window or the caption
    // window changed size.
    clampChromakeyPosition() {
      if (this.mode.mode !== 'chromakey' || this._captionDrag) return;
      const { positionOffsetX, positionOffset } = this;
      this.setChromakeyPosition(positionOffsetX, positionOffset, this.getChromakeyPositionBounds());
      if (this.positionOffsetX !== positionOffsetX || this.positionOffset !== positionOffset) {
        this.saveChromakeySettings();
      }
    },
    // Back to where the captions sit by default: centred at the bottom.
    resetChromakeyPosition() {
      this.setChromakeyPosition(0, 0, this.getChromakeyPositionBounds());
      this.saveChromakeySettings();
    },
    // Arrow keys move the captions in steps, with no snapping so they can be placed exactly.
    nudgeChromakeyPosition(dx, dy) {
      // Hidden captions have no window to measure, so the key press brings them back first.
      this.chromakeyTouch();
      this.$nextTick(() => {
        this.setChromakeyPosition(this.positionOffsetX + dx, this.positionOffset + dy, this.getChromakeyPositionBounds());
        this.saveChromakeySettings();
      });
    },
    // Where a dragged caption window lines up with: the screen centre and the safe area, 5% in
    // from the edges as in broadcast "action safe". Bottom only vertically, since the top is
    // the toolbar's.
    getChromakeySnapTargets(roomRect) {
      const safeX = roomRect.width * 0.05;
      const safeY = roomRect.height * 0.05;
      return {
        x: [
          { edge: 'center', value: roomRect.left + roomRect.width / 2 },
          { edge: 'start', value: roomRect.left + safeX },
          { edge: 'end', value: roomRect.right - safeX },
        ],
        y: [
          { edge: 'center', value: roomRect.top + roomRect.height / 2 },
          { edge: 'end', value: roomRect.bottom - safeY },
        ],
      };
    },
    // Pulls an offset onto the nearest snap target within reach, as long as that stays inside
    // the drag bounds. start/end are the window's edges on this axis when the drag began.
    snapChromakeyAxis(offset, origin, start, end, targets, min, max) {
      const SNAP_DISTANCE = 8;
      const shift = offset - origin;
      const edges = { start: start + shift, end: end + shift, center: (start + end) / 2 + shift };
      let best = null;
      targets.forEach((target) => {
        const delta = target.value - edges[target.edge];
        const snapped = offset + delta;
        if (Math.abs(delta) <= SNAP_DISTANCE && snapped >= min && snapped <= max
            && (!best || Math.abs(delta) < Math.abs(best.delta))) {
          best = { delta, guide: target.value };
        }
      });
      return best ? { offset: offset + best.delta, guide: best.guide } : { offset, guide: null };
    },
    startCaptionDrag(event) {
      // One pointer at a time: a second finger must not restart the drag.
      if (this._captionDrag || (event.pointerType === 'mouse' && event.button !== 0)) return;
      event.preventDefault();

      // A second tap (or click) right after a first one, in about the same place, resets.
      const lastTap = this._captionLastTap;
      this._captionLastTap = null;
      if (lastTap && event.timeStamp - lastTap.time < 350
          && Math.hypot(event.clientX - lastTap.x, event.clientY - lastTap.y) < 24) {
        this.resetChromakeyPosition();
        return;
      }

      event.currentTarget.setPointerCapture(event.pointerId);
      const roomRect = this.$refs.roomwrap.getBoundingClientRect();
      this._captionDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: this.positionOffsetX,
        originY: this.positionOffset,
        bounds: this.getChromakeyPositionBounds(),
        startRect: event.currentTarget.getBoundingClientRect(),
        snapTargets: this.getChromakeySnapTargets(roomRect),
        // The drag only shows (outline, hidden lines, guides) once the pointer actually moves,
        // so a plain click or tap doesn't flash it.
        active: false,
      };
      this.captionDragPointer = event.pointerType;
      // Keep the captions from idling out from under the pointer.
      this.chromakeyTouch();
    },
    moveCaptionDrag(event) {
      const drag = this._captionDrag;
      if (!drag || event.pointerId !== drag.pointerId) return;

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.active) {
        if (Math.hypot(dx, dy) < 4) return;
        drag.active = true;
        this.captionDragging = true;
      }

      const { bounds, startRect, snapTargets } = drag;
      const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
      let x = { offset: clamp(drag.originX + dx, bounds.minX, bounds.maxX), guide: null };
      let y = { offset: clamp(drag.originY + dy, bounds.minY, bounds.maxY), guide: null };
      // Alt (Option on a Mac) moves freely, without snapping.
      if (!event.altKey) {
        x = this.snapChromakeyAxis(x.offset, drag.originX, startRect.left, startRect.right, snapTargets.x, bounds.minX, bounds.maxX);
        y = this.snapChromakeyAxis(y.offset, drag.originY, startRect.top, startRect.bottom, snapTargets.y, bounds.minY, bounds.maxY);
      }
      this.setChromakeyPosition(x.offset, y.offset, bounds);
      this.captionSnapGuides = { x: x.guide, y: y.guide };
    },
    endCaptionDrag(event) {
      const drag = this._captionDrag;
      if (!drag || (event && event.pointerId !== drag.pointerId)) return;
      this._captionDrag = null;
      this.captionSnapGuides = { x: null, y: null };
      // A press that never turned into a drag is a tap, which may be the first of a double tap.
      this._captionLastTap = !drag.active && event && event.type === 'pointerup'
          ? { time: event.timeStamp, x: event.clientX, y: event.clientY }
          : null;
      if (!drag.active) return;
      this.captionDragging = false;
      this.saveChromakeySettings();
    },
    preventContextMenu(event) {
      event.preventDefault();
      return false;
    },
    saveChromakeySettings() {
      const chromaSettings = {
        chroma_overlay_color: this.settings.chroma_overlay_color,
        margin: this.mode.margin,
        align: this.mode.align,
        fontSize: this.fontSize,
        chromakeyMaxLines: this.chromakeyMaxLines,
        positionOffset: this.positionOffset,
        positionOffsetX: this.positionOffsetX,
        fontFamily: this.fontFamily,
      };
      localStorage.setItem('chromakeySettings', JSON.stringify(chromaSettings));
    },
    loadChromakeySettings() {
      const saved = localStorage.getItem('chromakeySettings');
      if (saved) {
        const chromaSettings = JSON.parse(saved);
        if (chromaSettings.chroma_overlay_color) this.settings.chroma_overlay_color = chromaSettings.chroma_overlay_color;
        if (chromaSettings.margin) this.mode.margin = chromaSettings.margin;
        if (chromaSettings.align) this.mode.align = chromaSettings.align;
        if (chromaSettings.fontSize) this.fontSize = chromaSettings.fontSize;
        if (chromaSettings.chromakeyMaxLines) this.chromakeyMaxLines = chromaSettings.chromakeyMaxLines;
        if (chromaSettings.positionOffset !== undefined) this.positionOffset = chromaSettings.positionOffset;
        if (chromaSettings.positionOffsetX !== undefined) this.positionOffsetX = chromaSettings.positionOffsetX;
        if (chromaSettings.fontFamily) this.fontFamily = chromaSettings.fontFamily;
      }
    },
    startRoom() {
      if (this.started) {
        return;
      }
      this.started = true;
      this.sessionStartTime = Date.now();
    },
    generateMessageId() {
      return uuidv4();
    },
    setMessageId() {
      if (!this.partial.message_id) {
        this.partial.message_id = this.generateMessageId();
      }

      return this.partial.message_id;
    },
    getMessageSpeakerId(message) {
      const sender = message && message.sender ? message.sender : null;
      if (!sender) return '';
      return sender.sender_id || `${sender.name || ''}|${sender.language || ''}`;
    },
    getMessageSpeakerName(message) {
      const sender = message && message.sender ? message.sender : null;
      if (!sender) return '';
      return sender.name || '';
    },
    getMessageSpeakerLanguage(message) {
      const sender = message && message.sender ? message.sender : null;
      if (!sender) return '';
      return sender.language || '';
    },
    getLatestLiveSpeaker() {
      // Prefer most recent partial (active speech), then latest final transcript.
      const latestPartial = this.partials.length ? this.partials[this.partials.length - 1] : null;
      if (latestPartial) {
        const name = this.getMessageSpeakerName(latestPartial);
        if (name) {
          return {
            name,
            language: this.getMessageSpeakerLanguage(latestPartial),
          };
        }
      }

      const latestTranscript = this.transcripts.length ? this.transcripts[this.transcripts.length - 1] : null;
      if (latestTranscript) {
        const name = this.getMessageSpeakerName(latestTranscript);
        if (name) {
          return {
            name,
            language: this.getMessageSpeakerLanguage(latestTranscript),
          };
        }
      }

      return {
        name: this.currentTranscriptSpeakerName || '',
        language: this.currentTranscriptSpeakerLanguage || '',
      };
    },
    getFlagSrc(language) {
      if (!language) return '';
      return '/data/flags/' + language + '.png';
    },
    syncCurrentTranscriptSpeakerName(message) {
      const speakerName = this.getMessageSpeakerName(message);
      const speakerLanguage = this.getMessageSpeakerLanguage(message);
      if (!speakerName && !speakerLanguage) return;

      if (speakerName) {
        this.currentTranscriptSpeakerName = speakerName;
      }
      if (speakerLanguage) {
        this.currentTranscriptSpeakerLanguage = speakerLanguage;
      }
    },
    mergeCaptionMessage(existing = {}, incoming = {}) {
      return {
        ...existing,
        ...incoming,
        original: {
          ...(existing.original || {}),
          ...(incoming.original || {}),
        },
        sender: {
          ...(existing.sender || {}),
          ...(incoming.sender || {}),
        },
        translations: {
          ...(existing.translations || {}),
          ...(incoming.translations || {}),
        },
      };
    },
    didTranslationArriveForUser(previousMessage, nextMessage) {
      return this.isTranslationExpectedForUser(nextMessage) &&
        !this.hasTranslationForUser(previousMessage) &&
        this.hasTranslationForUser(nextMessage);
    },
    getDefaultTranscriptMessageElement(messageId) {
      const trackEl = this.getTranscriptTrackElement();
      if (!trackEl || !messageId) {
        return null;
      }

      const messageIdString = String(messageId);
      const messageElements = trackEl.querySelectorAll('.transcripts__item[data-message-id]');
      return Array.from(messageElements).find((element) => element.dataset.messageId === messageIdString) || null;
    },
    getDefaultTranscriptTranslationElement(messageId) {
      const messageEl = this.getDefaultTranscriptMessageElement(messageId);
      return messageEl ? messageEl.querySelector('.transcript__translation') : null;
    },
    prepareTranslationReveal(messageId) {
      if (this.mode.mode !== 'default' || !messageId) {
        return null;
      }

      const translationEl = this.getDefaultTranscriptTranslationElement(messageId);
      if (!translationEl) {
        return null;
      }

      const height = Math.max(0, Math.ceil(translationEl.getBoundingClientRect().height));
      const reveal = {
        messageId,
        previousHeight: height,
        followMode: this.transcriptFollowMode,
      };

      this.translationRevealStates = {
        ...this.translationRevealStates,
        [messageId]: {
          height,
        },
      };

      return reveal;
    },
    finishTranslationReveal(reveal) {
      if (!reveal || !reveal.messageId) {
        return;
      }

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const translationEl = this.getDefaultTranscriptTranslationElement(reveal.messageId);
          if (!translationEl) {
            this.clearTranslationReveal(reveal.messageId);
            return;
          }

          const nextHeight = Math.max(reveal.previousHeight, Math.ceil(translationEl.scrollHeight));
          this.translationRevealStates = {
            ...this.translationRevealStates,
            [reveal.messageId]: {
              height: nextHeight,
            },
          };

          setTimeout(() => {
            this.clearTranslationReveal(reveal.messageId);

            if (reveal.followMode !== 'review') {
              this.scrollTranscripts();
            }
          }, 260);
        });
      });
    },
    clearTranslationReveal(messageId) {
      if (!messageId || !this.translationRevealStates[messageId]) {
        return;
      }

      const nextStates = { ...this.translationRevealStates };
      delete nextStates[messageId];
      this.translationRevealStates = nextStates;
    },
    updateTranscriptMessage(message) {
      if (!message || !message.message_id) return false;

      const transcriptIndex = this.transcripts.findIndex((transcript) => transcript.message_id === message.message_id);
      if (transcriptIndex < 0) {
        return false;
      }

      const nextTranscript = this.mergeCaptionMessage(this.transcripts[transcriptIndex], message);
      const translationReveal = this.didTranslationArriveForUser(this.transcripts[transcriptIndex], nextTranscript)
        ? this.prepareTranslationReveal(message.message_id)
        : null;

      Object.assign(
        this.transcripts[transcriptIndex],
        nextTranscript,
      );
      this.finishTranslationReveal(translationReveal);
      this.scrollTranscripts();
      return true;
    },
    pushTranscript(message) {
      if (!message || !message.message_id) return false;
      if (this.finalMessages.includes(message.message_id)) {
        return this.updateTranscriptMessage(message);
      }

      if (this.pendingFinalMessages[message.message_id]) {
        clearTimeout(this.pendingFinalMessages[message.message_id].timeoutId);
        delete this.pendingFinalMessages[message.message_id];
      }

      this.finalMessages.push(message.message_id);
      this.clearStalePartialTimer(message.message_id);

      let partialIndex = this.partials.findIndex((part => part.message_id == message.message_id));

      if (message.original.text === '_INVALID_' || message.original.text == '_crosstalk_is_detected_') {
        if (partialIndex >= 0) {
          this.partials.splice(partialIndex, 1)
        }
        this.clearPartialPlaybackState(message.message_id);

        return false;
      }

      const now = new Date();

      let minutesRaw = now.getMinutes();
      if (minutesRaw < 10) {
        minutesRaw = '0' + minutesRaw;
      }

      let hoursRaw = now.getHours();
      if (hoursRaw < 10) {
        hoursRaw = '0' + hoursRaw;
      }
      const hoursAndMinutes = hoursRaw + ':' + minutesRaw;

      const previousPartial = partialIndex > -1 ? this.partials[partialIndex] : null;
      const transcriptMessage = this.mergeCaptionMessage(previousPartial || {}, message);
      const translationReveal = previousPartial && this.didTranslationArriveForUser(previousPartial, transcriptMessage)
        ? this.prepareTranslationReveal(message.message_id)
        : null;
      if (previousPartial) {
        Object.assign(previousPartial, transcriptMessage);
      }
      const transcriptEntry = previousPartial || transcriptMessage;
      Object.assign(transcriptEntry, {
        time: hoursAndMinutes,
        status: "sent",
      });

      this.transcripts.push(transcriptEntry);

      if (partialIndex > -1) { // only splice array when item is found
        this.partials.splice(partialIndex, 1); // 2nd parameter means remove one item only
      }
      this.clearPartialPlaybackState(message.message_id);
      this.finishTranslationReveal(translationReveal);

      this.scroller.hasNew = true;
      this.scrollTranscripts();

      this.messagesCount++;
      if (this.isOwnMessage(message)) {
        this.clearCurrenPartial();
      }
    },
    scheduleFinalTranscript(message) {
      if (!message || !message.message_id) return;
      if (this.finalMessages.includes(message.message_id)) return;

      if (this.finalTranscriptDelayMs <= 0) {
        this.pushTranscript(message);
        this.partialIncoming = false;

        if (this.isOwnMessage(message)) {
          this.clearCurrenPartial();
        }

        this.chromakeyTouch();
        return;
      }

      if (this.pendingFinalMessages[message.message_id]) {
        this.pendingFinalMessages[message.message_id].message = message;
        return;
      }

      const pendingEntry = {
        message,
        timeoutId: null,
      };

      pendingEntry.timeoutId = setTimeout(() => {
        const currentPending = this.pendingFinalMessages[message.message_id];
        if (!currentPending) return;

        const latestFinalMessage = currentPending.message;
        delete this.pendingFinalMessages[message.message_id];
        this.pushTranscript(latestFinalMessage);
        this.partialIncoming = false;

        if (this.isOwnMessage(latestFinalMessage)) {
          this.clearCurrenPartial();
        }

        this.chromakeyTouch();
      }, this.finalTranscriptDelayMs);

      this.pendingFinalMessages[message.message_id] = pendingEntry;
    },
    isSameLanguageAudio(message_id) {
      let messageFound = this.transcripts.find((part => part.message_id == message_id));
      if (messageFound && messageFound.sender) {
        return this.participant.language.slice(0, 2) == messageFound.sender.language.slice(0,2);
      }

      return false;
    },
    sendHoldToTalkPartial(newPartial) {
      console.log("partial send")
      let tmpMessage = {
        message_id: newPartial.message_id,
        original: {}
      };
      if (this.partial.original.text) {
        let concatPrev = "";
        if (this.partial.original.text && this.partial.concat) {
          concatPrev = this.partial.original.text;
        }
        tmpMessage.original.text = concatPrev + " " + newPartial.original.text ;

        tmpMessage.is_final = false;
        this.sendMessage(tmpMessage);
      } else {
        newPartial.is_final = false;
        this.sendMessage(newPartial);
      }
    },
    sendKeyboardMessage(is_final) {
      let text = "...";
      if (this.keyboardInputValue.length > 0) {
        text = this.keyboardInputValue;
      } else if (is_final) {
        return false;
      }
      let message = {
        message_id: this.setMessageId(),
        original: {
          text: text
        },
        is_final: is_final,
      };
      this.sendMessage(message);

      if (is_final) {
        this.keyboardInputValue = '';
      }

      // keep the caret (and the mobile keyboard) in the field after sending
      this.$nextTick(this.focusKeyboardInput);
    },
    clearCurrenPartial(deletemsg = false) {
      if (this.partial.message_id) {
        if (deletemsg) {
          this.deletedMessages.push(this.partial.message_id);
          this.sendMessage({
            message_id: this.partial.message_id,
            original: {
              text: "_INVALID_"
            },
            is_final: true
          });
        }

        this.partial = {
          message_id: false,
          original: {
            text: ''
          },
          concat: ''
        };

        this.keyboardInputValue = "";
      }
    },
    isOwnMessage(message) {
      return this.user_id && this.user_id == message.sender.sender_id;
    },
    hasOwnMessage() {
      return this.partial.original && (this.partial.original.text.length > 0 || this.partial.concat.length > 0);
    },
    getPartialDisplayText(partial) {
      if (!partial) return '';

      const originalText = (partial.original && partial.original.text) ? String(partial.original.text).trim() : '';
      const concatText = partial.concat ? String(partial.concat).trim() : '';

      if (originalText && concatText) {
        return `${originalText} ${concatText}`;
      }

      return originalText || concatText;
    },
    updatePartialPlaybackState(partial, previousText = '') {
      const messageId = partial && partial.message_id;
      if (!messageId) return;

      const fullText = this.getPartialDisplayText(partial);
      const existingState = this.partialPlaybackStates[messageId] || {
        displayed: '',
        buffered: '',
        committed: '',
        intervalId: null,
        flushTimeoutId: null,
      };

      if (!previousText) {
        this.setPartialPlaybackState(messageId, {
          ...existingState,
          displayed: '',
          buffered: fullText,
          committed: '',
        });
        this.schedulePartialPlaybackFlush(messageId);
        this.tryStartPartialPlayback(messageId);
        return;
      }

      if (fullText === previousText) {
        return;
      }

      if (fullText.startsWith(previousText) && fullText.length > previousText.length) {
        const appendedText = fullText.slice(previousText.length);
        const nextBuffered = `${existingState.buffered || ''}${appendedText}`;

        this.setPartialPlaybackState(messageId, {
          ...existingState,
          buffered: nextBuffered,
          committed: fullText,
        });

        this.schedulePartialPlaybackFlush(messageId);
        this.tryStartPartialPlayback(messageId);
      } else {
        // If ASR rewrites earlier text, snap to latest state and continue from there.
        this.stopPartialPlayback(messageId);
        this.setPartialPlaybackState(messageId, {
          ...existingState,
          displayed: fullText,
          buffered: '',
          committed: fullText,
        });
      }
    },
    setPartialPlaybackState(messageId, nextState) {
      this.partialPlaybackStates = {
        ...this.partialPlaybackStates,
        [messageId]: nextState,
      };
    },
    stopPartialPlayback(messageId) {
      const state = this.partialPlaybackStates[messageId];
      if (!state) return;

      if (state.intervalId) {
        clearInterval(state.intervalId);
      }
      if (state.flushTimeoutId) {
        clearTimeout(state.flushTimeoutId);
      }

      this.setPartialPlaybackState(messageId, {
        ...state,
        intervalId: null,
        flushTimeoutId: null,
      });
    },
    clearPartialPlaybackState(messageId) {
      if (!messageId || !this.partialPlaybackStates[messageId]) return;

      this.stopPartialPlayback(messageId);

      const nextStates = { ...this.partialPlaybackStates };
      delete nextStates[messageId];
      this.partialPlaybackStates = nextStates;
    },
    countWords(text) {
      if (!text) return 0;
      return text.trim().split(/\s+/).filter(Boolean).length;
    },
    getPartialPlaybackMode() {
      // Green Screen captions are read off a video overlay, so they reveal by word:
      // character-by-character typing is too restless at the sizes this mode is used at.
      if (this.mode.mode === 'chromakey') {
        return 'word';
      }

      return (this.settings && this.settings.partial_playback_mode) || 'character';
    },
    getPartialBufferWordThresholdValue() {
      const value = Number(this.settings && this.settings.partial_buffer_word_threshold);
      if (Number.isFinite(value) && value >= 1) {
        return Math.floor(value);
      }

      return this.partialBufferWordThreshold;
    },
    getPartialTypeIntervalMsValue() {
      const value = Number(this.settings && this.settings.partial_type_interval_ms);
      if (Number.isFinite(value) && value >= 5) {
        return Math.floor(value);
      }

      if (this.mode.mode === 'chromakey') {
        return this.chromakeyTypeIntervalMs;
      }

      return this.partialTypeIntervalMs;
    },
    takeNextPlaybackUnit(text) {
      if (!text) {
        return { unit: '', rest: '' };
      }

      if (this.getPartialPlaybackMode() === 'word') {
        // Keep leading whitespace attached to the revealed word so spacing stays natural.
        const match = text.match(/^\s*\S+/);
        if (match) {
          return {
            unit: match[0],
            rest: text.slice(match[0].length),
          };
        }

        // Whitespace-only remainder.
        return {
          unit: text,
          rest: '',
        };
      }

      return {
        unit: text.charAt(0),
        rest: text.slice(1),
      };
    },
    tryStartPartialPlayback(messageId) {
      const state = this.partialPlaybackStates[messageId];
      if (!state || state.intervalId || !state.buffered) return;

      if (this.countWords(state.buffered) < this.getPartialBufferWordThresholdValue()) {
        return;
      }

      this.startPartialPlayback(messageId);
    },
    schedulePartialPlaybackFlush(messageId) {
      const state = this.partialPlaybackStates[messageId];
      if (!state) return;

      if (state.flushTimeoutId) {
        clearTimeout(state.flushTimeoutId);
      }

      if (this.partialBufferDelayMs <= 0) {
        this.setPartialPlaybackState(messageId, {
          ...state,
          flushTimeoutId: null,
        });
        return;
      }

      const flushTimeoutId = setTimeout(() => {
        this.startPartialPlayback(messageId);
      }, this.partialBufferDelayMs);

      this.setPartialPlaybackState(messageId, {
        ...state,
        flushTimeoutId,
      });
    },
    startPartialPlayback(messageId) {
      const state = this.partialPlaybackStates[messageId];
      if (!state) return;

      if (state.flushTimeoutId) {
        clearTimeout(state.flushTimeoutId);
      }

      if (state.intervalId) {
        return;
      }

      const intervalId = setInterval(() => {
        const tickState = this.partialPlaybackStates[messageId];
        if (!tickState) {
          clearInterval(intervalId);
          return;
        }

        if (!tickState.buffered || tickState.buffered.length === 0) {
          clearInterval(intervalId);
          this.setPartialPlaybackState(messageId, {
            ...tickState,
            intervalId: null,
          });
          return;
        }

        const { unit, rest } = this.takeNextPlaybackUnit(tickState.buffered);

        if (!unit) {
          clearInterval(intervalId);
          this.setPartialPlaybackState(messageId, {
            ...tickState,
            intervalId: null,
          });
          return;
        }

        this.setPartialPlaybackState(messageId, {
          ...tickState,
          displayed: `${tickState.displayed}${unit}`,
          buffered: rest,
          intervalId,
        });

        // Keep following the transcript list while partial text animates in.
        this.scrollTranscripts();
      }, this.getPartialTypeIntervalMsValue());

      this.setPartialPlaybackState(messageId, {
        ...state,
        intervalId,
        flushTimeoutId: null,
      });
    },
    getPartialPlaybackText(partial) {
      const messageId = partial && partial.message_id;
      if (!messageId) {
        return this.getPartialDisplayText(partial);
      }

      const state = this.partialPlaybackStates[messageId];
      if (!state) {
        return this.getPartialDisplayText(partial);
      }

      return state.displayed;
    },
    pushPartial(partial) {
      if (this.finalMessages.includes(partial.message_id)) {
        return this.updateTranscriptMessage(partial);
      }

      let senderId = partial.sender_id ?? partial.sender.sender_id;
      if (this.partials.length > 0) {
        const supersededPartials = this.partials.filter((message) =>
          message.sender &&
          message.sender.sender_id == senderId &&
          message.message_id != partial.message_id &&
          !this.pendingFinalMessages[message.message_id]
        );

        supersededPartials.forEach((existingPartial) => {
          const existingMessageId = existingPartial.message_id;

          // With segmentation the final for the previous partial may still be on its way
          // (viewers can receive partial Y before final X). Dropping it here makes the
          // caption vanish and then jump back in once the final lands, so keep it and
          // only remove it if no final ever arrives. An incoming final never evicts
          // anyone either: it only moves its own message into the transcripts.
          if (partial.is_final || this.isSegmentedCaptionMessage(partial) || this.isSegmentedCaptionMessage(existingPartial)) {
            this.scheduleStalePartialRemoval(existingMessageId);
            return;
          }

          this.removePartialById(existingMessageId);
        });
      }

      // The message is alive again (updated or re-added), so it is not stale.
      this.clearStalePartialTimer(partial.message_id);

      let partialIndex = this.partials.findIndex((message => message.message_id == partial.message_id));
      if (partial.original.text === '_INVALID_' || partial.original.text == '_crosstalk_is_detected_') {
        if (partialIndex >= 0) {
          this.partials.splice(partialIndex, 1)
        }
        this.clearPartialPlaybackState(partial.message_id);

        return false;
      }

      if (partialIndex >= 0) {
        const previousText = this.getPartialDisplayText(this.partials[partialIndex]);
        const nextPartial = this.mergeCaptionMessage(this.partials[partialIndex], partial);
        const translationReveal = this.didTranslationArriveForUser(this.partials[partialIndex], nextPartial)
          ? this.prepareTranslationReveal(partial.message_id)
          : null;
        Object.assign(this.partials[partialIndex], nextPartial);
        this.updatePartialPlaybackState(this.partials[partialIndex], previousText);
        this.finishTranslationReveal(translationReveal);
      } else {
        this.markNewPartial(partial.message_id);
        this.partials.push(partial);
        this.updatePartialPlaybackState(partial, '');
      }

      this.scrollTranscripts();
    },
    pushOwnPartial(partial) {
      if (this.finalMessages.includes(partial.message_id)) {
        return false;
      }

      //let partialIndex = this.partials.findIndex((message => message.message_id == partial.message_id));

      if (partial.original.text === '_INVALID_' || partial.original.text == '_crosstalk_is_detected_') {
        return false;
      }

      if (this.isHoldToTalk) {

        this.partial.concat = partial.concat;
        if (partial.is_final) {
          this.partial.original.text += " " + partial.original.text;
          this.partial.concat = "";
        }
      } else {
        this.partial.original.text = partial.original.text;
      }

      this.scrollTranscripts();
    },
    shouldShowOriginalWithTranslation() {
      return !this.settings || this.settings.show_original_with_translation !== false;
    },
    toggleShowOriginal() {
      this.$store.commit('event/setShowOriginalWithTranslation', !this.shouldShowOriginalWithTranslation());
    },
    isAsrContinuationMessage(message) {
      const segment = message && message.asr_segment;
      if (!segment) {
        return false;
      }

      return segment.continues_previous_segment === true ||
        segment.starts_new_sentence === false ||
        segment.start_reason === 'segmenter_continuation' ||
        segment.start_reason === 'aws_result_continuation';
    },
    isSegmentedCaptionMessage(message) {
      return !!(message && message.asr_segment);
    },
    removePartialById(messageId) {
      this.clearStalePartialTimer(messageId);

      const partialIndex = this.partials.findIndex((message) => message.message_id == messageId);
      if (partialIndex < 0) {
        return false;
      }

      this.clearPartialPlaybackState(messageId);
      this.partials.splice(partialIndex, 1);
      return true;
    },
    scheduleStalePartialRemoval(messageId) {
      if (!messageId || this.stalePartialTimers[messageId]) {
        return;
      }

      this.stalePartialTimers[messageId] = setTimeout(() => {
        delete this.stalePartialTimers[messageId];

        if (this.finalMessages.includes(messageId) || this.pendingFinalMessages[messageId]) {
          return;
        }

        if (this.removePartialById(messageId)) {
          this.scrollTranscripts();
        }
      }, this.stalePartialTimeoutMs);
    },
    clearStalePartialTimer(messageId) {
      const timeoutId = messageId ? this.stalePartialTimers[messageId] : null;
      if (!timeoutId) {
        return;
      }

      clearTimeout(timeoutId);
      delete this.stalePartialTimers[messageId];
    },
    getLanguageRoot(language) {
      return language ? String(language).slice(0, 2) : '';
    },
    isTranslationExpectedForUser(transcript) {
      const selectedLanguage = this.participant && this.participant.language;
      const senderLanguage = transcript && transcript.sender && transcript.sender.language;

      if (!selectedLanguage || !senderLanguage) {
        return false;
      }

      return this.getLanguageRoot(selectedLanguage) !== this.getLanguageRoot(senderLanguage);
    },
    hasTranslationForUser(transcript) {
      const selectedLanguage = this.participant && this.participant.language;
      const translations = transcript && transcript.translations;

      if (!this.isTranslationExpectedForUser(transcript) || !selectedLanguage || !translations || typeof translations !== 'object') {
        return false;
      }

      const selectedTranslation = translations[selectedLanguage];

      return typeof selectedTranslation === 'object' && selectedTranslation !== null;
    },
    isSameLanguage(lang1, lang2) {
      const mainLanguage = this.getLanguageRoot(lang1);
      const originalLanguage = this.getLanguageRoot(lang2);

      return !!mainLanguage && !!originalLanguage && mainLanguage === originalLanguage;
    },
    getTranslationForUser(transcript) {
      const selectedLanguage = this.participant && this.participant.language;
      const translations = transcript && transcript.translations;

      if (!selectedLanguage || !translations || typeof translations !== 'object') {
        return null;
      }

      return translations[selectedLanguage] || null;
    },
    shouldShowOriginalForTranscript(transcript) {
      if (!this.isTranslationExpectedForUser(transcript)) {
        return true;
      }

      return this.shouldShowOriginalWithTranslation();
    },
    shouldUseTranslationOriginalStyle(transcript) {
      return this.isTranslationExpectedForUser(transcript);
    },
    shouldShowPartialOriginal(partial) {
      if (!this.isTranslationExpectedForUser(partial)) {
        return true;
      }

      return this.shouldShowOriginalWithTranslation();
    },
    shouldShowCaptionOriginal(message, type) {
      return type === 'partial'
        ? this.shouldShowPartialOriginal(message)
        : this.shouldShowOriginalForTranscript(message);
    },
    getCaptionOriginalText(message, type) {
      if (type === 'partial') {
        return this.getPartialPlaybackText(message);
      }

      return message && message.original ? message.original.text : '';
    },
    shouldShowPendingTranslation(message) {
      return this.isTranslationExpectedForUser(message) &&
        !this.hasTranslationForUser(message) &&
        !this.shouldShowOriginalWithTranslation();
    },
    shouldReserveTranslationSlot(message) {
      return this.isTranslationExpectedForUser(message) &&
        !this.hasTranslationForUser(message) &&
        this.shouldShowOriginalWithTranslation();
    },
    shouldShowTranslationSlot(message) {
      return this.hasTranslationForUser(message) ||
        this.shouldShowPendingTranslation(message) ||
        this.shouldReserveTranslationSlot(message);
    },
    isTranslationRevealActive(message) {
      return !!(message && message.message_id && this.translationRevealStates[message.message_id]);
    },
    getTranslationRevealStyle(message) {
      if (!message || !message.message_id) {
        return null;
      }

      const revealState = this.translationRevealStates[message.message_id];
      if (!revealState) {
        return null;
      }

      return {
        height: `${revealState.height}px`,
      };
    },
    getTranslationPlaceholderText(message, type) {
      const placeholderText = this.getCaptionOriginalText(message, type);

      return placeholderText || ' ';
    },
    markNewPartial(messageId) {
      if (!messageId) return;

      this.newPartialMessageIds = {
        ...this.newPartialMessageIds,
        [messageId]: true,
      };

      setTimeout(() => {
        if (!this.newPartialMessageIds[messageId]) return;

        const nextIds = { ...this.newPartialMessageIds };
        delete nextIds[messageId];
        this.newPartialMessageIds = nextIds;
      }, 500);
    },
    isNewPartial(messageId) {
      return !!(messageId && this.newPartialMessageIds[messageId]);
    },
    shouldAnimateNewPartial(partial) {
      return !!(partial && this.isNewPartial(partial.message_id) && !this.isTranslationExpectedForUser(partial));
    },
    handleIncomingCaptionMessage(data) {
      if (!data) return;

      this.syncCurrentTranscriptSpeakerName(data);

      if (!this.started) {
        this.startRoom();
      }

      const newPartial = data;
      const isOwn = this.isOwnMessage(newPartial);

      if (this.mode.mode !== 'default') {
        if (this.isSameLanguage(newPartial.sender.language, this.participant.language)) {
          this.pushPartial(newPartial);
        }
        if (!isOwn) {
          this.partialIncoming = true;
        }
      } else {
        this.pushPartial(newPartial);
        if (!isOwn) {
          this.partialIncoming = true;
        }
      }

      if (newPartial.is_final) {
        this.scheduleFinalTranscript(newPartial);
      }
    },
    async sendMessage(message) {
      this.emitSocketEvent("message", message);
    },
    logSocket(type, event, payload = null, meta = null) {
      try {
        this.sessionLogger?.log(type, event, payload, meta);
      } catch (e) {
        console.warn('[SessionLogger] log failed:', e);
      }
    },
    getSocketSnapshot() {
      const socket = this.apiSocket;
      return {
        hasSocket: Boolean(socket),
        connected: socket ? Boolean(socket.connected) : false,
        active: socket ? Boolean(socket.active) : false,
        visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        recording: this.awsIsRecording,
      };
    },
    emitSocketEvent(eventName, payload = {}) {
      if (!this.apiSocket || typeof this.apiSocket.emit !== 'function') {
        this.logSocket('socket:error', 'emit_without_socket', { event: eventName, payload });
        return false;
      }

      this.logSocket('socket:outgoing', eventName, payload);

      try {
        this.apiSocket.emit(eventName, payload);
        return true;
      } catch (e) {
        this.logSocket('socket:error', 'emit_failed', { event: eventName, payload, error: { message: e?.message, name: e?.name } });
        console.warn(`[Socket] Failed to emit "${eventName}"`, e);
        return false;
      }
    },
    teardownSocketInstance(reason) {
      const socket = this.apiSocket;
      if (!socket) {
        return;
      }

      this.logSocket('socket:connection', 'socket_teardown', { reason, ...this.getSocketSnapshot() });
      this.connectionQuality?.unbindSocket();

      try {
        if (typeof socket.offAny === 'function') {
          socket.offAny();
        }
        if (socket.io && this._socketManagerHandlers) {
          Object.entries(this._socketManagerHandlers).forEach(([name, handler]) => {
            socket.io.off(name, handler);
          });
        }
        socket.removeAllListeners();
        socket.disconnect();
      } catch (e) {
        console.warn('Socket teardown failed:', e);
      }

      this._socketManagerHandlers = null;
      this.apiSocket = false;
    },
    closeSocket() {
      if (this.apiSocket) {
        this.emitSocketEvent("close_connection");
        this.teardownSocketInstance('close_socket');
      }

      this.sessionEndTime = Date.now();

      const trackEventData = {
        duration: this.sessionDuration,
        messages_number: this.finalMessages.length,
        room_code: this.eventData.code,
        title: this.eventData.title,
        role: this.socketRoomOwner ? "Host" : "Participant",
        room_type: this.eventData.room_type,
        participants_number: this.room.count,
        room_protected: this.eventData.is_room_protected ? "Yes" : "No",
        particicipation: this.eventData.is_participating_disabled ? "disabled" : "enabled"
      };
      const pendo = getPendo(this.user);
      if (pendo) {
        pendo.track("session_exit", trackEventData);
      }

      if (trackEventData.messages_number >= 1) {
        this.setShowGuide(true);
      }

      this.releaseWakeLock();
    },
    closedRoomMessage() {
      // blocking: the room is already gone, so OK is the only way out
      this.$dialog.alert('service_event_ended', {
        title: 'session_ended',
        blocking: true
      }).then(() => {
        forgetRoom(this._roomCode);
        this.$store.dispatch("event/close");
        this.closeSocket();
        this.$router.replace("/");
      });
    },
    getUserActionTargetId(userLike = {}) {
      const source = userLike && typeof userLike === 'object' ? userLike : {};

      const socketId = source.socket_id || source.socketId || this.apiSocket?.id || source.id || source.sender_id || source.senderId;
      if (socketId) {
        return String(socketId);
      }

      const userId = source.user_id || source.userId || this.user_id || this.participant?.user_id || this.udid || 'local_user';
      return String(userId);
    },
    emitUserAction(action, payload = {}) {
      const actionName = typeof action === 'string' ? action.trim() : '';
      if (!actionName || !this.availableActions.includes(actionName)) {
        return false;
      }

      const eventPayload = {
        action: actionName,
        value: Object.prototype.hasOwnProperty.call(payload, 'value') ? payload.value : true,
        ...payload,
      };

      const emitted = this.emitSocketEvent('user_action', eventPayload);
      return emitted;
    },
    setTemporaryUserActionState(targetId, action, next, value) {
      const timestampKey = `${action}At`;

      if (value) {
        next[timestampKey] = Date.now();
        next[action] = true;

        window.setTimeout(() => {
          const state = this.userActionStateByUser[targetId] || {};
          if (!state[timestampKey] || Number(state[timestampKey]) !== Number(next[timestampKey])) {
            return;
          }

          const refreshedState = { ...(this.userActionStateByUser[targetId] || {}) };
          const refreshedActions = { ...(refreshedState.actions || {}) };
          delete refreshedActions[action];
          delete refreshedState.actions;
          delete refreshedState[action];
          delete refreshedState[timestampKey];
          refreshedState.actions = refreshedActions;
          this.userActionStateByUser = {
            ...this.userActionStateByUser,
            [targetId]: refreshedState,
          };
        }, 2000);
      } else {
        delete next[timestampKey];
        delete next[action];
      }
    },
    raiseHand() {
      return this.emitUserAction('raiseHand');
    },
    thumbsUp() {
      return this.emitUserAction('thumbsUp', { fireAndForget: true });
    },
    laugh() {
      return this.emitUserAction('laugh', { fireAndForget: true });
    },
    heart() {
      return this.emitUserAction('heart', { fireAndForget: true });
    },
    clap() {
      return this.emitUserAction('clap', { fireAndForget: true });
    },
    handleParticipantsAction(action) {
      if (action === 'raiseHand') {
        this.raiseHand();
      }
      else if (action === 'thumbsUp') {
        this.thumbsUp();
      }
      else if (action === 'laugh') {
        this.laugh();
      }
      else if (action === 'heart') {
        this.heart();
      }
      else if (action === 'clap') {
        this.clap();
      }
    },
    emitMuteAudience(enabled) {
      this.participation_enabled = enabled;
      this.emitSocketEvent('host_control', {
        global: {
          participation: {
            asr_allowed: enabled,
            keyboard_allowed: enabled
          }
        }
      })
    },
    preSetupRecorder() {
      // setup recorder
      navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {

        if (this.recorder && this.recorder.state !== 'inactive') {
          throw new Error('Stop the recorder first');
        }
        return stream;
      }).then((stream) => {
        let options = { mimeType: 'audio/ogg;codecs=opus' };
        // Start recording
        this.recorder = new MediaRecorder(stream, options, workerOptions);
        this.stopRecording(false);
      });
    },
    setupRecorder(sound = true) {
      // setup recorder
      const constraints = {
        audio: {
          deviceId: this.selectedMicrophoneId ? { exact: this.selectedMicrophoneId } : undefined
        }
      };
      navigator.mediaDevices.getUserMedia(constraints).then(stream => {

        if (this.recorder && this.recorder.state !== 'inactive') {
          throw new Error('Stop the recorder first');
        }
        return stream;

      }).then((stream) => {
        if (sound) {
          this.soundPlayerRecStart.play();
        }

        this.setRecording(true);
        this.setMicActivated(true);

        if (this.enableAsrTimer) {
          if (this.speaked) {
            this.asrTimer.resume();
          } else {
            this.asrTimer.start();
          }
        }
        this.speaked = true;
        let options = { mimeType: 'audio/ogg;codecs=opus' };
        // Start recording
        this.recorder = new MediaRecorder(stream, options, workerOptions);
        // Set record to <audio> when recording will be finished
        this.recorder.ondataavailable = (e) => {
          let blob = e.data;
          let stream = ss.createStream();

          // pipe the audio blob to the read stream
          ss.createBlobReadStream(blob).pipe(stream);
          if (!this.partial.message_id) {
            this.partial.message_id = this.generateMessageId();
          }
        };
        let combinedTerms = [];
        let langTerms = this.custom_terms.filter((term) => {
          return term.language === "all" ||
              term.language === this.participant.language ||
              this.participant.language.includes(term.language);
        });
        if (this.event.custom_vocabulary && this.event.custom_vocabulary.length > 0) {
          let tmpTerms = this.event.custom_vocabulary.filter((term) => {
            return term.language === "all" ||
                term.language === this.participant.language ||
                this.participant.language.includes(term.language);
          });
          let mergedTerms = tmpTerms.concat(langTerms.filter(
              item2 => !tmpTerms.some(item1 => item1.term.toLowerCase() === item2.term.toLowerCase())));
          combinedTerms = mergedTerms.map(a => a.term);
        } else {
          combinedTerms = langTerms.map(a => a.term);
          console.log("combinedTerms", combinedTerms);
        }

        this.recorder.start(100);

      }).catch(e => {
        console.log(`MediaRecorder is failed: ${e.message}`);
        //Promise.reject(new Error());
      });
    },
    askSecurePassword() {
      this.secureSessionPopup = true;
      this.roomOverlayed = true;
    },
    setPasswordProvided(pwd) {
      this.sessionPassword = pwd;
      this.setupApiSocket();
    },
    leaveSecuredSession() {
      // Сокет ещё не поднят: просто закрываем событие и уходим на главную.
      this.secureSessionPopup = false;
      this.roomOverlayed = false;
      emitter.emit('quit_room');
    },
    buildSocketAuth() {
      // Вызывается socket.io при каждом (пере)подключении, поэтому токен берётся актуальный:
      // для авторизованного пользователя из стора (Auth0 мог его обновить), для гостя из события.
      const socketAuth = {
        token: (this.authorized && this.token) ? this.token : this.room_token,
        room_code: this.event.room_code,
        udid: this.udid
      };

      if (this.user && this.authorized) {
        socketAuth.language = this.user.language;
        socketAuth.display_name = this.user.name;
        socketAuth.gender = this.user.voice;
      } else if (this.guest) {
        socketAuth.language = this.guest.language;
        socketAuth.display_name = this.guest.name;
        socketAuth.gender = this.guest.voice;
      }

      if (this.event.is_room_protected) {
        socketAuth.password = this.sessionPassword;
      }

      return socketAuth;
    },
    setupApiSocket() {
      if (this.apiSocket) {
        this.teardownSocketInstance('setup_api_socket');
      }

      // Запоминаем код комнаты отдельно: при выходе RoomView обнуляет event в сторе
      // раньше, чем до нас доходит close_room.
      this._roomCode = this.event.room_code;

      if (this.event.is_room_protected && !this.sessionPassword) {
        this.askSecurePassword();
        return false;
      }

      if (!this.sessionLogger) {
        this.sessionLogger = createSessionLogger({
          roomCode: this.event.room_code,
          userId: this.udid,
        });
      }

      const apiSocketObject = io(this.event.socket_server, {
        path: "/socket.io/",
        auth: (cb) => cb(this.buildSocketAuth()),
        transports: ["websocket"]
      });

      this.apiSocket = apiSocketObject;
      this.socketConnectedOnce = false;
      this.connectionQuality?.bindSocket(apiSocketObject);
      this.logSocket('socket:connection', 'socket_created', {
        server: this.event.socket_server,
        visibility: document.visibilityState,
      });

      apiSocketObject.onAny((event, ...args) => {
        // partial-фрагменты распознавания приходят по несколько раз в секунду,
        // каждая запись — это перезапись всего лога в localStorage, поэтому пишем только финальные.
        if (event === 'message' && args[0] && args[0].is_final === false) {
          return;
        }
        this.logSocket('socket:incoming', event, args);
      });

      apiSocketObject.on('connect', () => {
        const isReconnect = this.socketConnectedOnce;
        this.logSocket('socket:connection', isReconnect ? 'reconnected' : 'connected', {
          id: apiSocketObject.id,
          recovered: apiSocketObject.recovered,
          visibility: document.visibilityState,
        });
        console.log(isReconnect ? "reconnected api" : "connected api");
      });

      apiSocketObject.on('disconnect', (reason, details) => {
        // 'io client disconnect' — мы сами закрыли сокет; всё остальное — потеря соединения или
        // разрыв со стороны сервера ('io server disconnect'), после которого socket.io сам не переподключается.
        const isClientInitiated = reason === 'io client disconnect';
        this.logSocket(isClientInitiated ? 'socket:connection' : 'socket:error', 'disconnect', {
          reason,
          details,
          active: apiSocketObject.active,
          visibility: document.visibilityState,
          online: navigator.onLine,
          recording: this.awsIsRecording,
        });
        console.warn('[Socket] disconnected:', reason, details);
      });

      // Manager (socket.io) общий для всех сокетов на один URL, поэтому его обработчики
      // снимаем адресно в teardownSocketInstance, а не через removeAllListeners.
      this._socketManagerHandlers = {
        reconnect_attempt: (attempt) => {
          this.logSocket('socket:connection', 'reconnect_attempt', {
            attempt,
            visibility: document.visibilityState,
            online: navigator.onLine,
          });
        },
        reconnect_error: (err) => {
          this.logSocket('socket:error', 'reconnect_error', {
            message: err?.message,
            name: err?.name,
            type: err?.type,
            description: err?.description,
          });
        },
        reconnect_failed: () => {
          this.logSocket('socket:error', 'reconnect_failed', this.getSocketSnapshot());
        },
      };

      Object.entries(this._socketManagerHandlers).forEach(([name, handler]) => {
        apiSocketObject.io.on(name, handler);
      });

      apiSocketObject.on("connection", (data) => {
        this.secureSessionPopup = false;
        this.roomOverlayed = false;

        const userPayload = data?.user || {};
        const actionMap = userPayload && typeof userPayload.actions === 'object' && userPayload.actions
          ? userPayload.actions
          : {};

        this.userSocketActions = Object.keys(actionMap).filter((action) => typeof action === 'string' && action.trim());

        this.is_room_owner = Boolean(userPayload.is_room_owner ?? data?.is_room_owner);
        this.socketRoomOwner = this.is_room_owner;

        this.setParticipant(userPayload);

        if (this.socketConnectedOnce) {
          // Повторное подключение: состояние с сервера уже применено выше,
          // приветственные попапы второй раз не показываем.
          this.logSocket('socket:connection', 'connection_restored', {
            is_room_owner: data.is_room_owner,
            session_role: data.user?.session_role,
          });
          return;
        }

        this.socketConnectedOnce = true;

        if (hasJoinedRoom(this._roomCode)) {
          // Вкладку перезагрузили (например, мобильный браузер выгрузил её в фоне),
          // а вход в эту комнату уже был — возвращаем в сессию без попапов.
          this.logSocket('socket:connection', 'room_rejoined_after_reload', {
            room_code: this.event.room_code,
          });
          return;
        }

        if (this.is_room_owner || this.socketRoomOwner) {
          markRoomJoined(this._roomCode);
          this.showMuteAudiencePopup();
        } else {
          this.showWelcomePopup();
        }
      });

      apiSocketObject.on("conference", (data) => {
        this.conference = data;
      });

      apiSocketObject.on("room_users", (data) => {
        this.participants = data.users;
        console.log('[RoomUsers] received room_users', data);
      });

      apiSocketObject.on("custom_vocabulary_list", (data) => {
        console.log('[CustomVocabulary] received custom_vocabulary_list', data);
        this.customVocabularyList = data || {};
      });

      apiSocketObject.on("features", (data) => {
        console.log("feature got")
        console.log(data)
        this.sessionFeatures = data;
        if (data.asr) {
          if (data.asr.allowed) {
            if (this.mode.mode === 'default') {
              this.$toast.info('host_enabled_participation', { group: 'alerts'});
            }
          } else {
            if (this.mode.mode === 'default') {
              this.$toast.info(this.sessionFeatures.asr.reason);
            }
            if (this.awsIsRecording) {
              this.stopRecording(true);
              this.stopFn();
              this.awsIsRecording = false;
            }
            if (this.typing) {
              this.typing = false;
              this.clearCurrenPartial(true);
            }
          }
        }
      });

      apiSocketObject.on("connect_error", (err) => {
        this.logSocket('socket:error', 'connect_error', err);

        if (err.message === 'password_invalid') {
          this.$toast.info(err.message);
        } else {
          this.$toast.info(err.message + ' socket error');
        }
      });

      apiSocketObject.on("notification", (notification) => {
        if (this.mode.mode === 'default') {
          this.$toast.info(notification.message, { timeout: 30000 });
        }
      });

      apiSocketObject.on("user_action", (data) => {
        if (!data || typeof data !== 'object') {
          return;
        }

        console.log("user_action", data);
        const user = data.user || {};
        const action = typeof data.action === 'string' ? data.action : null;
        const value = Object.prototype.hasOwnProperty.call(data, 'value') ? data.value : true;

        if (!action) {
          return;
        }

        const targetId = this.getUserActionTargetId({ ...user, ...data });
        const next = { ...(this.userActionStateByUser[targetId] || {}) };
        const actions = { ...(next.actions || {}) };
        actions[action] = value;
        next.actions = actions;
        next[action] = value;

        if (action === 'raiseHand') {
          if (value) {
            next.raisedHandAt = Date.now();
          } else {
            delete next.raisedHandAt;
          }
        }

        if (['thumbsUp', 'laugh', 'heart', 'clap'].includes(action)) {
          this.setTemporaryUserActionState(targetId, action, next, value);
        }

        this.userActionStateByUser = { ...this.userActionStateByUser, [targetId]: next };
      });

      apiSocketObject.on("connected_guests", (data) => {
        this.room.count = data.count;
      });

      apiSocketObject.on("message", (data) => {
        this.handleIncomingCaptionMessage(data);
      });

      apiSocketObject.on("room_was_closed", () => {
        this.logSocket('socket:connection', 'room_was_closed_received', {
          is_room_owner: this.socketRoomOwner,
          ...this.getSocketSnapshot(),
        });

        this.closedRoomMessage();
        this.closeSocket();
      });

      apiSocketObject.on("tts", (data) => {
        if (this.tts_muted || !permissions.can("tts", this.participant) || (this.settings.tts_same_muted && this.isSameLanguageAudio(data.message_id))) {
          return;
        }

        const audio = data.audio;
        if (audio) {
          this.pushTTSqueue(this.getTTSsrc(audio));
        }
      });


    },
    pushTTSqueue(ttsSrc)  {
      if (!this.recording) {
        this.ttsQueue.push(ttsSrc);
        this.playTTS();
      }
    },
    playTTS() {
      if (this.ttsQueue.length <= 0) {
        return false;
      }
      if (!this.ttsPlaying) {
        const firstAudio = this.ttsQueue.shift();
        this.soundPlayerTTS.src = firstAudio;
        this.soundPlayerTTS.playbackRate = this.settings.tts_speed / 100;
        this.soundPlayerTTS.play();
        this.ttsPlaying = true;
        const context = this;
        this.soundPlayerTTS.onended = function(){
          context.ttsPlaying = false;
          context.playTTS();
        };
      }
    },
    stopTTS() {
      if (this.ttsPlaying) {
        this.soundPlayerTTS.pause();
        this.soundPlayerTTS.currentTime = 0;
        this.ttsPlaying = false;
      }
    },
    setParticipant(socketUser) {
      if (socketUser) {
        this.participant.features = socketUser.features;
        this.participant.session_role = socketUser.session_role;
        this.sessionFeatures.asr = this.participant.features.asr;
        return;
      }
      if (this.user && this.authorized) {
        this.participant = this.user;
        this.isGuest = false;
      } else {
        this.participant = this.guest;
        this.isGuest = true;
      }
    },
    setCustomTerms() {
      GlossaryService.getTerms().then((data) => {
        let termsObjectsArray = data.data.result;
        termsObjectsArray.forEach((term) => {
          this.custom_terms.push(term);
        })
      }).catch((error) => {
        console.log(error);
      })
    },
    updateParticipant(profile) {
      this.participant.language = profile.language;
      this.participant.name = profile.name;
      this.participant.voice = profile.voice;

      if (this.isGuest) {
        // participant — это тот же объект, что и event.guest; без commit vuex-persist
        // не запишет новое имя в localStorage.
        this.$store.commit('event/setGuest', this.participant);
      }

      this.emitSocketEvent("update_profile", {
        display_name: this.participant.name ? this.participant.name : "Guest",
        language: this.participant.language,
        gender: this.participant.voice
      });

      this.openLanguageSelector = false;
    },
    updateParticipantLanguage(language) {
      this.updateParticipant({
        language: language,
        voice: this.participant.voice,
        name: this.participant.name,
      })

      this.restartRecording(true);
    },
    setKeyboardShortcuts() {
      const spaceKey = 'Space'; // space
      const escapeKey = 'Escape';
      const altEscapeKey = "Esc";

      window.addEventListener('keydown', (e) => {
        if(this.$route.name == 'room') {
          // ? opens the shortcuts cheat sheet. Matched by the character rather than a key code,
          // since ? sits on different keys across layouts.
          const tag = e.target && e.target.tagName;
          const isTyping = e.target && (e.target.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT');
          const hasModifier = e.ctrlKey || e.metaKey || e.altKey;

          if (e.key === '?' && !hasModifier) {
            if (!isTyping) {
              e.preventDefault();
              this.shortcutsModal = !this.shortcutsModal;
            }
            return;
          }

          // + / − step the font size in both views, as on YouTube. = is the + key without
          // Shift; the numpad sends + and - too. Ctrl/Cmd with them stay the browser's zoom.
          if (['+', '=', '-'].includes(e.key) && !hasModifier) {
            if (!isTyping && !this.anyPopupShown && !this.shortcutsModal) {
              e.preventDefault();
              this.changeFontSize(e.key === '-' ? -1 : 1);
              this.showFontSizeIndicator();
            }
            return;
          }

          if (e.code == spaceKey) {
            const targetTag = e.target && e.target.tagName;
            const isEditableTarget = e.target && (
              e.target.isContentEditable ||
              targetTag === 'INPUT' ||
              targetTag === 'TEXTAREA' ||
              targetTag === 'SELECT'
            );

            if (isEditableTarget) {
              // let the browser handle the keystroke normally (typing a space in a message, a settings field, etc.)
              return false;
            }

            // stop the browser from also activating whatever button currently has focus (e.g. the mic button),
            // which would otherwise double-toggle recording alongside the logic below
            e.preventDefault();

            if (this.anyPopupShown || this.typing || this.sessionSettings || this.captionSettings || this.captionSettingsBar || this.participantsDrawerOpen || this.shortcutsModal) {
              return false;
            }

            if (this.holding) {
              if (this.awsIsRecording) {
                return false;
              } else {
                this.holding = false;
              }
            } else {
              this.holding = true;
            }

            if (!this.awsIsRecording && !this.typing) {
              this.startRecording();
            } else {
              this.stopRecording();
            }
          }

          if (e.code == escapeKey || e.code == altEscapeKey) {
            if (this.recording) {
              this.stopRecording(false, true);
            } else if (this.typing) {
              //this.keyboardInput();
              this.keyboardInputValue = "";
            }
          }
        }
      });

      window.addEventListener('keyup', (e) => {
        if(this.$route.name == 'room') {
          if (e.code == spaceKey && this.awsIsRecording) {
            this.holding = false;
          }
        }
      });

      // Not command+h too: on a Mac that hides the browser before the page ever sees it.
      hotkeys('alt+h', (event) => {
        event.preventDefault();

        if (this.$route.name !== 'room') return;
        this.toggleChromakeyCaptions();
      });

      hotkeys('alt+b, command+b', (event) => {
        event.preventDefault();

        if (this.$route.name !== 'room') return;
        this.toggleSessionBars();
      });

      // Green Screen: arrows move the captions, Shift for bigger steps. Left alone in the
      // Regular view, and while the toolbar or one of its menus has the keyboard.
      hotkeys(CAPTION_NUDGE_KEYS, (event) => {
        if (this.$route.name !== 'room' || this.mode.mode !== 'chromakey' || this.anyBarMenuOpened || this.shortcutsModal) return;
        if (event.target && event.target.closest && event.target.closest('.green-screen-bar')) return;

        event.preventDefault();
        const step = event.shiftKey ? 50 : 10;
        const [dx, dy] = {
          ArrowLeft: [-step, 0],
          ArrowRight: [step, 0],
          ArrowUp: [0, -step],
          ArrowDown: [0, step],
        }[event.key] || [0, 0];
        this.nudgeChromakeyPosition(dx, dy);
      });
    },
    async acquireWakeLock() {
      try {
        if ('wakeLock' in navigator) {
          if (this.wakeLock) {
            return true;
          }

          // Requesting a wake lock while hidden throws in browsers.
          if (document.visibilityState !== 'visible' || document.hidden) {
            return false;
          }

          this.wakeLock = await navigator.wakeLock.request('screen');

          this.wakeLock.addEventListener('release', () => {
            this.wakeLock = null;
          });

          return true;
        }

        if (!this.noSleepEnabled) {
          noSleep.enable();
          this.noSleepEnabled = true;
        }

        return true;
      } catch (e) {
        const message = (e && e.message ? String(e.message) : '').toLowerCase();
        if (e && e.name === 'NotAllowedError' && message.includes('not visible')) {
          return false;
        }

        console.warn('Wake lock request failed:', e);

        try {
          if (!this.noSleepEnabled) {
            noSleep.enable();
            this.noSleepEnabled = true;
          }
          return true;
        } catch (fallbackError) {
          console.warn('NoSleep fallback failed:', fallbackError);
          return false;
        }
      }
    },

    async releaseWakeLock() {
      if (this.wakeLock) {
        try {
          await this.wakeLock.release();
        } catch (e) {
          console.warn('Wake lock release failed:', e);
        }
        this.wakeLock = null;
      }

      if (this.noSleepEnabled) {
        try {
          noSleep.disable();
        } catch (e) {
          console.warn('NoSleep disable failed:', e);
        }
        this.noSleepEnabled = false;
      }
    },

    async ensureSocketAliveAfterResume(source = 'unknown') {
      if (!this.user && !this.guest) {
        return;
      }

      // visibilitychange, focus, pageshow и online срабатывают почти одновременно;
      // вторую и последующие проверки, пока идёт первая, пропускаем.
      if (this.resumeCheckInFlight) {
        this.logSocket('custom', 'resume_check_skipped', { source, ...this.getSocketSnapshot() });
        return;
      }

      this.resumeCheckInFlight = true;

      try {
        if (!this.apiSocket) {
          this.logSocket('custom', 'resume_socket_missing', { source });
          this.setupApiSocket();
          return;
        }

        if (this.apiSocket.connected) {
          return;
        }

        if (this.apiSocket.active) {
          // socket.io сам ведёт переподключение (сеть, ping timeout) — не мешаем ему.
          this.logSocket('custom', 'resume_socket_reconnecting', { source, ...this.getSocketSnapshot() });
          return;
        }

        // Сокет неактивен: соединение разорвал сервер или оно было закрыто вручную.
        // Переиспользуем тот же экземпляр, чтобы не плодить подключения с одним udid.
        this.logSocket('custom', 'resume_socket_connect_manual', {
          source,
          disconnectReason: this.connectionDisconnectReason,
          ...this.getSocketSnapshot(),
        });

        const shouldRestartRecording = this.awsIsRecording;

        try {
          if (shouldRestartRecording) {
            await this.stopFn();
          }
        } catch (e) {
          console.warn('Failed stopping transcribe before reconnect:', e);
        }

        this.apiSocket.connect();

        if (shouldRestartRecording) {
          try {
            await this.restartRecording(false);
          } catch (e) {
            console.warn('Recording restart failed after reconnect:', e);
          }
        }
      } finally {
        this.resumeCheckInFlight = false;
      }
    },
    pauseTranscriptTeleprompterForVisibility() {
      if (this.mode.mode !== 'default') {
        return;
      }

      this.cancelTranscriptTeleprompterAnimation();

      if (this.transcriptTeleprompterResizeFrame) {
        cancelAnimationFrame(this.transcriptTeleprompterResizeFrame);
        this.transcriptTeleprompterResizeFrame = null;
      }
    },
    resumeTranscriptTeleprompterAfterVisibilityChange() {
      if (this.mode.mode !== 'default') {
        return;
      }

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.transcriptTeleprompterTarget = this.getTranscriptMaxOffset();

          if (this.transcriptFollowMode === 'review') {
            const nextOffset = this.clampTranscriptTeleprompterOffset(this.transcriptTeleprompterOffset);
            this.setTranscriptTeleprompterOffset(nextOffset);
            this.scroller.scrollTop = nextOffset;

            if (this.isTranscriptAtLiveEdge()) {
              this.transcriptFollowMode = 'live';
              this.scroller.scrolled = false;
              this.scroller.hasNew = false;
            }

            return;
          }

          this.syncDefaultTranscriptsToBottom();
        });
      });
    },

    async handleVisibilityChange() {
      const isVisible = document.visibilityState === 'visible';

      this.logSocket('custom', 'visibility_changed', { visible: isVisible, ...this.getSocketSnapshot() });

      if (!isVisible) {
        this.pageWasHidden = true;
        this.pauseTranscriptTeleprompterForVisibility();
        return;
      }

      await this.handleResume('visibilitychange');
    },

    // Единая точка входа для всех событий «вкладка снова активна».
    async handleResume(source) {
      if (document.visibilityState !== 'visible') {
        return;
      }

      try {
        await this.acquireWakeLock();
      } catch (e) {
        console.warn(`Failed to reacquire wake lock on ${source}:`, e);
      }

      await this.ensureSocketAliveAfterResume(source);

      if (this.pageWasHidden) {
        this.resumeTranscriptTeleprompterAfterVisibilityChange();
      }
      this.pageWasHidden = false;
    },

    handlePageShow() {
      return this.handleResume('pageshow');
    },

    handleWindowFocus() {
      return this.handleResume('focus');
    },

    handleWindowOnline() {
      return this.handleResume('online');
    },

    logConnectionChanged(fromLabel, toLabel) {
      const qualityByBars = (bars) => {
        switch (bars) {
          case 0: return { name: 'offline', score: 0 };
          case 1: return { name: 'poor', score: 1 };
          case 2: return { name: 'weak', score: 2 };
          case 3: return { name: 'fair', score: 3 };
          case 4: return { name: 'good', score: 4 };
          default: return { name: 'unknown', score: null };
        }
      };

      const fromQuality = qualityByBars(this.lastLoggedConnectionBars);
      const toQuality = qualityByBars(this.connectionBars);

      let direction = 'unknown';
      if (fromQuality.score !== null && toQuality.score !== null) {
        direction = toQuality.score > fromQuality.score ? 'improved'
          : toQuality.score < fromQuality.score ? 'degraded'
          : 'changed';
      }

      this.logSocket('custom', 'connection_changed', {
        label: { from: fromLabel, to: toLabel },
        offline: this.connectionState === 'offline' || this.connectionBars === 0,
        direction,
        quality: {
          from: fromQuality.name,
          to: toQuality.name,
          score: toQuality.score,
          bars: this.connectionBars,
          state: this.connectionState,
          latency: this.connectionLatency,
          disconnectReason: this.connectionDisconnectReason,
        },
      });

      this.lastLoggedConnectionBars = this.connectionBars;
    },

    getTTSsrc(arrayBuffer) {
      let arrayBufferView = new Uint8Array(arrayBuffer);
      let blob = new Blob( [ arrayBufferView ], { type: 'audio/mp3' } );
      return URL.createObjectURL(blob);
    },
    async _unlockAudio() {
      try {
        await this.acquireWakeLock();
      } catch (e) {
        console.warn('Initial wake lock failed:', e);
      }

      try {
        await this.soundPlayerRecStart.play();
        this.soundPlayerRecStart.pause();
        this.soundPlayerRecStart.currentTime = 0;
      } catch (e) {
        console.warn('Rec start audio unlock failed:', e);
      }

      try {
        await this.soundPlayerRecStop.play();
        this.soundPlayerRecStop.pause();
        this.soundPlayerRecStop.currentTime = 0;
      } catch (e) {
        console.warn('Rec stop audio unlock failed:', e);
      }

      try {
        await this.soundPlayerTTS.play();
        this.soundPlayerTTS.pause();
        this.soundPlayerTTS.currentTime = 0;
      } catch (e) {
        console.warn('TTS audio unlock failed:', e);
      }

      document.body.removeEventListener('click', this._unlockAudio)
      document.body.removeEventListener('touchstart', this._unlockAudio)
    },
    resetScroller() {
      this.scroller.scrolled = false;
      this.scroller.hasNew = false;
      this.scroller.scrollTop = 0;
      this.transcriptManualScrollHoldUntil = 0;

      if (this.mode.mode === 'default') {
        this.transcriptFollowMode = 'live';
        this.scrollTranscripts(true);
        return;
      }

      this.scrollTranscripts(true);
    },
    scrollTranscripts(force = false) {
      if (this.mode.mode === 'default') {
        if (force && !this.isTranscriptManualScrollHeld()) {
          this.transcriptFollowMode = 'live';
        }

        this.scrollDefaultTranscriptsToLive(force);
        return;
      }

      const scrollEl = this.$refs.transcriptsWrap;
      if (!scrollEl) {
        return false;
      }

      if (this.scroller.scrolled && !force) {
        return false;
      }

      this.$nextTick(() => {
        const maxScrollTop = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
        const isReverseLayout = window.getComputedStyle(scrollEl).flexDirection === 'column-reverse';
        scrollEl.scrollTop = isReverseLayout ? 0 : maxScrollTop;
      });
    },
    scrollDefaultTranscriptsToLive(force = false) {
      if (this.mode.mode !== 'default' || this.isTranscriptPageHidden()) {
        return false;
      }

      if (this.isTranscriptManualScrollHeld()) {
        this.scroller.hasNew = true;
        return false;
      }

      if (this.transcriptFollowMode === 'review' && !force) {
        this.scroller.hasNew = true;
        return false;
      }

      const wrapEl = this.$refs.transcriptsWrap;
      if (!wrapEl) {
        return false;
      }

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          if (this.mode.mode !== 'default' || this.isTranscriptPageHidden()) {
            return;
          }

          if (this.isTranscriptManualScrollHeld()) {
            this.scroller.hasNew = true;
            return;
          }

          if (this.transcriptFollowMode === 'review' && !force) {
            this.scroller.hasNew = true;
            return;
          }

          this.startDefaultTranscriptsToBottom(force, wrapEl);
        });
      });

      return true;
    },
    startDefaultTranscriptsToBottom(force = false, wrapEl = this.$refs.transcriptsWrap) {
      if (!wrapEl) {
        return false;
      }

      this.syncDefaultTranscriptTopSpacer(wrapEl, force);

      if (this.transcriptTopSpacerTarget > 0) {
        this.cancelTranscriptTeleprompterAnimation();
        this.transcriptTeleprompterOffset = 0;
        this.transcriptTeleprompterTarget = 0;
        wrapEl.scrollTop = 0;
        this.transcriptFollowMode = 'live';
        this.scroller.scrolled = false;
        this.scroller.hasNew = false;
        this.scroller.scrollTop = 0;
        return true;
      }

      const nextTarget = this.getTranscriptMaxOffset(wrapEl);
      const currentOffset = this.clampTranscriptTeleprompterOffset(wrapEl.scrollTop, wrapEl);

      this.transcriptTeleprompterOffset = currentOffset;
      this.transcriptTeleprompterTarget = nextTarget;

      if (force || nextTarget <= currentOffset + 0.5 || this.shouldSnapTranscriptTeleprompter(force)) {
        return this.syncDefaultTranscriptsToBottom(wrapEl, force);
      }

      this.transcriptFollowMode = 'live';
      this.scroller.scrolled = false;
      this.scroller.hasNew = false;
      this.scroller.scrollTop = currentOffset;
      this.transcriptAutoScrollActive = true;

      if (!this.transcriptTeleprompterFrame) {
        this.transcriptTeleprompterLastFrameAt = 0;
        this.transcriptTeleprompterFrame = requestAnimationFrame(this.animateTranscriptTeleprompter);
      }

      return true;
    },
    syncDefaultTranscriptsToBottom(wrapEl = this.$refs.transcriptsWrap, force = false) {
      if (!wrapEl) {
        return false;
      }

      this.cancelTranscriptTeleprompterAnimation();
      this.syncDefaultTranscriptTopSpacer(wrapEl, force);

      const trackEl = this.getTranscriptTrackElement();
      if (trackEl) {
        trackEl.style.transform = '';
      }

      const nextOffset = this.getTranscriptMaxOffset(wrapEl);
      wrapEl.scrollTop = nextOffset;

      const appliedOffset = this.clampTranscriptTeleprompterOffset(wrapEl.scrollTop, wrapEl);
      this.transcriptTeleprompterOffset = appliedOffset;
      this.transcriptTeleprompterTarget = nextOffset;
      this.transcriptFollowMode = 'live';
      this.scroller.scrolled = false;
      this.scroller.hasNew = false;
      this.scroller.scrollTop = appliedOffset;

      return true;
    },
    syncDefaultTranscriptTopSpacer(wrapEl = this.$refs.transcriptsWrap, force = false) {
      const trackEl = this.getTranscriptTrackElement();
      const feedEl = trackEl && trackEl.querySelector('.transcripts__feed-list');

      if (this.mode.mode !== 'default' || !wrapEl || !trackEl || !feedEl) {
        return 0;
      }

      const previousVisualTop = feedEl.getBoundingClientRect().top;
      const previousSpacer = Number.parseFloat(trackEl.style.getPropertyValue('--default-transcript-top-spacer')) || 0;
      const trackStyle = window.getComputedStyle(trackEl);
      const currentTopPadding = Number.parseFloat(trackStyle.paddingTop) || 18;
      const baseTopPadding = Math.max(0, currentTopPadding - previousSpacer);
      const baseBottomPadding = Number.parseFloat(trackStyle.paddingBottom) || 22;
      const availableHeight = wrapEl.clientHeight - baseTopPadding - baseBottomPadding;
      const nextSpacer = Math.max(0, availableHeight - feedEl.offsetHeight);

      this.transcriptTopSpacerTarget = nextSpacer;

      if (force || this.prefersReducedTranscriptMotion()) {
        this.setDefaultTranscriptTopSpacerOffset(nextSpacer);
        this.cancelDefaultTranscriptTopSpacerAnimation();
        return nextSpacer;
      }

      if (Math.abs(previousSpacer - nextSpacer) <= 0.5) {
        this.setDefaultTranscriptTopSpacerOffset(nextSpacer);
        return nextSpacer;
      }

      this.setDefaultTranscriptTopSpacerOffset(nextSpacer);
      this.setDefaultTranscriptTopSpacerVisualOffset(0);

      const nextNaturalTop = feedEl.getBoundingClientRect().top;
      const nextVisualOffset = previousVisualTop - nextNaturalTop;

      if (Math.abs(nextVisualOffset) <= 0.5) {
        this.setDefaultTranscriptTopSpacerVisualOffset(0);
        return nextSpacer;
      }

      this.setDefaultTranscriptTopSpacerVisualOffset(nextVisualOffset);

      if (!this.transcriptTopSpacerFrame) {
        this.transcriptTopSpacerLastFrameAt = 0;
        this.transcriptTopSpacerFrame = requestAnimationFrame(this.animateDefaultTranscriptTopSpacer);
      }

      return nextSpacer;
    },
    setDefaultTranscriptTopSpacerOffset(offset) {
      const nextOffset = Math.max(0, Number(offset) || 0);
      const trackEl = this.getTranscriptTrackElement();

      if (trackEl) {
        trackEl.style.setProperty('--default-transcript-top-spacer', `${nextOffset}px`);
      }
    },
    setDefaultTranscriptTopSpacerVisualOffset(offset) {
      const nextOffset = Number(offset) || 0;
      const trackEl = this.getTranscriptTrackElement();
      const feedEl = trackEl && trackEl.querySelector('.transcripts__feed-list');

      this.transcriptTopSpacerVisualOffset = nextOffset;

      if (!feedEl) {
        return;
      }

      feedEl.style.transform = Math.abs(nextOffset) <= 0.5
        ? ''
        : `translate3d(0, ${nextOffset}px, 0)`;
    },
    // Scroll speeds are tuned in px/s for the default 20px font (29px line box). Scale them
    // with the line box so a wrapped line takes the same time to catch up at any font size.
    getTranscriptLineHeightScale() {
      return Math.max(1, this.defaultTranscriptLineHeightPx / 29);
    },
    getDefaultTranscriptTopSpacerStep(distance, elapsed) {
      const absoluteDistance = Math.abs(distance);
      const baseSpeed = Math.max(220, this.getTranscriptScrollSpeedValue() * 4) * this.getTranscriptLineHeightScale();
      let speed = baseSpeed;

      if (absoluteDistance > 260) {
        speed = baseSpeed * 2.1;
      } else if (absoluteDistance > 96) {
        speed = baseSpeed * 1.45;
      }

      return Math.min(absoluteDistance, Math.max(0.5, elapsed * speed / 1000));
    },
    animateDefaultTranscriptTopSpacer(timestamp) {
      if (this.mode.mode !== 'default' || this.isTranscriptPageHidden()) {
        this.cancelDefaultTranscriptTopSpacerAnimation();
        return;
      }

      const distance = -this.transcriptTopSpacerVisualOffset;

      if (Math.abs(distance) <= 0.5) {
        this.setDefaultTranscriptTopSpacerVisualOffset(0);
        this.cancelDefaultTranscriptTopSpacerAnimation(false);
        return;
      }

      const elapsed = this.transcriptTopSpacerLastFrameAt ? Math.min(48, timestamp - this.transcriptTopSpacerLastFrameAt) : 16;
      this.transcriptTopSpacerLastFrameAt = timestamp;
      const direction = distance > 0 ? 1 : -1;
      const nextOffset = this.transcriptTopSpacerVisualOffset + (direction * this.getDefaultTranscriptTopSpacerStep(distance, elapsed));

      this.setDefaultTranscriptTopSpacerVisualOffset(nextOffset);
      this.transcriptTopSpacerFrame = requestAnimationFrame(this.animateDefaultTranscriptTopSpacer);
    },
    cancelDefaultTranscriptTopSpacerAnimation(resetTarget = true) {
      if (this.transcriptTopSpacerFrame) {
        cancelAnimationFrame(this.transcriptTopSpacerFrame);
      }

      this.transcriptTopSpacerFrame = null;
      this.transcriptTopSpacerLastFrameAt = 0;

      if (resetTarget) {
        this.setDefaultTranscriptTopSpacerVisualOffset(0);
      }
    },
    prefersReducedTranscriptMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },
    isTranscriptPageHidden() {
      return typeof document !== 'undefined' && (document.hidden || document.visibilityState !== 'visible');
    },
    holdTranscriptManualScroll() {
      this.transcriptManualScrollHoldUntil = Date.now() + 1400;
    },
    isTranscriptManualScrollHeld() {
      return this.transcriptManualScrollHoldUntil > Date.now();
    },
    scheduleTranscriptTeleprompter(force = false) {
      if (this.mode.mode === 'default' && this.isTranscriptPageHidden()) {
        if (this.transcriptTeleprompterResizeFrame) {
          cancelAnimationFrame(this.transcriptTeleprompterResizeFrame);
          this.transcriptTeleprompterResizeFrame = null;
        }

        return;
      }

      if (this.transcriptTeleprompterResizeFrame) {
        cancelAnimationFrame(this.transcriptTeleprompterResizeFrame);
      }

      this.$nextTick(() => {
        if (this.transcriptTeleprompterResizeFrame) {
          cancelAnimationFrame(this.transcriptTeleprompterResizeFrame);
        }

        this.transcriptTeleprompterResizeFrame = requestAnimationFrame(() => {
          this.transcriptTeleprompterResizeFrame = null;
          this.updateTranscriptTeleprompter(force);
        });
      });
    },
    getTranscriptTrackElement() {
      return this.$refs.defaultTranscriptsTrack || null;
    },
    getTranscriptMaxOffset(wrapEl = this.$refs.transcriptsWrap) {
      if (!wrapEl) {
        return 0;
      }

      return Math.max(0, wrapEl.scrollHeight - wrapEl.clientHeight);
    },
    clampTranscriptTeleprompterOffset(offset, wrapEl = this.$refs.transcriptsWrap) {
      const nextOffset = Math.max(0, Number(offset) || 0);

      if (!wrapEl) {
        return nextOffset;
      }

      return Math.min(this.getTranscriptMaxOffset(wrapEl), nextOffset);
    },
    getTranscriptAnchorElement(trackEl) {
      if (!trackEl) {
        return null;
      }

      const feedItems = trackEl.querySelectorAll('.transcripts__feed-list .transcripts__item:not(.transcripts__item--tip)');
      if (feedItems.length) {
        return feedItems[feedItems.length - 1];
      }

      return null;
    },
    getTranscriptReadingLine(wrapEl) {
      const rect = wrapEl.getBoundingClientRect();
      const upperLimit = rect.top + Math.min(260, Math.max(160, rect.height * 0.34));
      const preferredLine = rect.top + rect.height * 0.48;
      const lowerLimit = Math.max(upperLimit, rect.bottom - Math.min(220, Math.max(140, rect.height * 0.28)));

      return Math.max(upperLimit, Math.min(preferredLine, lowerLimit));
    },
    measureTranscriptTeleprompterTarget() {
      const wrapEl = this.$refs.transcriptsWrap;
      const trackEl = this.getTranscriptTrackElement();
      const anchorEl = this.getTranscriptAnchorElement(trackEl);

      if (!wrapEl || !trackEl || !anchorEl || this.mode.mode !== 'default') {
        return null;
      }

      const readingLine = this.getTranscriptReadingLine(wrapEl);
      const anchorRect = anchorEl.getBoundingClientRect();
      const anchorBottomWithoutOffset = anchorRect.bottom + this.transcriptTeleprompterOffset;
      const maxScrollTop = this.getTranscriptMaxOffset(wrapEl);

      return Math.min(maxScrollTop, Math.max(0, anchorBottomWithoutOffset - readingLine));
    },
    updateTranscriptTeleprompter(force = false) {
      if (this.mode.mode === 'default' && this.isTranscriptPageHidden()) {
        this.cancelTranscriptTeleprompterAnimation();
        return;
      }

      if (this.mode.mode === 'default' && this.isTranscriptManualScrollHeld()) {
        this.scroller.hasNew = true;
        return;
      }

      if (this.mode.mode === 'default' && (force || this.transcriptFollowMode !== 'review')) {
        this.startDefaultTranscriptsToBottom(force);
        return;
      }

      if (this.mode.mode === 'default') {
        const wrapEl = this.$refs.transcriptsWrap;
        const previousTarget = this.clampTranscriptTeleprompterOffset(this.transcriptTeleprompterTarget, wrapEl);
        const nextTarget = this.getTranscriptMaxOffset(wrapEl);
        this.transcriptTeleprompterTarget = nextTarget;

        if (this.transcriptTeleprompterOffset > nextTarget + 1) {
          this.setTranscriptTeleprompterOffset(nextTarget);
        }

        const isAtLiveEdge = this.isTranscriptAtLiveEdge();
        this.scroller.scrolled = !isAtLiveEdge;
        this.scroller.hasNew = isAtLiveEdge ? false : this.scroller.hasNew || nextTarget > previousTarget + 1;
        this.scroller.scrollTop = this.transcriptTeleprompterOffset;
        return;
      }

      const measuredTarget = this.measureTranscriptTeleprompterTarget();

      if (measuredTarget === null) {
        this.cancelTranscriptTeleprompterAnimation();
        return;
      }

      const wrapEl = this.$refs.transcriptsWrap;
      const previousTarget = this.clampTranscriptTeleprompterOffset(this.transcriptTeleprompterTarget, wrapEl);
      const nextTarget = this.clampTranscriptTeleprompterOffset(measuredTarget, wrapEl);

      const shouldFollowLive = force || this.transcriptFollowMode !== 'review';
      this.transcriptTeleprompterTarget = shouldFollowLive
          ? nextTarget
          : Math.max(previousTarget, nextTarget);
      const targetIncreased = this.transcriptTeleprompterTarget > previousTarget + 1;

      if (this.transcriptTeleprompterOffset > this.transcriptTeleprompterTarget + 1) {
        this.setTranscriptTeleprompterOffset(this.transcriptTeleprompterTarget);
      }

      if (this.transcriptFollowMode === 'review' && !force) {
        const isAtLiveEdge = this.isTranscriptAtLiveEdge();
        this.scroller.scrolled = !isAtLiveEdge;
        this.scroller.hasNew = isAtLiveEdge ? false : this.scroller.hasNew || targetIncreased;
        this.scroller.scrollTop = this.transcriptTeleprompterOffset;
        return;
      }

      this.transcriptFollowMode = 'live';
      this.scroller.scrolled = false;
      this.scroller.hasNew = false;
      this.scroller.scrollTop = this.transcriptTeleprompterTarget;
      this.cancelTranscriptTeleprompterAnimation();
      this.setTranscriptTeleprompterOffset(this.transcriptTeleprompterTarget);
    },
    shouldSnapTranscriptTeleprompter(force = false) {
      if (force || this.prefersReducedTranscriptMotion()) {
        return true;
      }

      const wrapEl = this.$refs.transcriptsWrap;
      const distance = Math.abs(this.transcriptTeleprompterTarget - this.transcriptTeleprompterOffset);

      if (!wrapEl) {
        return distance > 420;
      }

      if (this.mode.mode === 'default') {
        // Snap once the live edge is more than half a viewport or two large-font lines away:
        // animating that far just keeps the newest line hidden below the fold.
        const lagLimit = Math.max(this.defaultTranscriptLineHeightPx * 2, wrapEl.clientHeight * 0.5);
        return distance > Math.min(Math.max(900, wrapEl.clientHeight * 1.8), lagLimit);
      }

      return distance > Math.max(360, wrapEl.clientHeight * 0.72);
    },
    isTranscriptAtLiveEdge() {
      return this.transcriptTeleprompterOffset >= this.transcriptTeleprompterTarget - 2;
    },
    refreshLiveTranscriptTeleprompterTarget() {
      if (this.mode.mode !== 'default' || this.transcriptFollowMode === 'review') {
        return false;
      }

      if (this.mode.mode === 'default') {
        const wrapEl = this.$refs.transcriptsWrap;
        if (!wrapEl || this.isTranscriptManualScrollHeld()) {
          return false;
        }

        this.transcriptTeleprompterTarget = this.getTranscriptMaxOffset(wrapEl);
        return true;
      }

      const measuredTarget = this.measureTranscriptTeleprompterTarget();
      if (measuredTarget === null) {
        return false;
      }

      this.transcriptTeleprompterTarget = this.clampTranscriptTeleprompterOffset(measuredTarget);
      return true;
    },
    getTranscriptTeleprompterStep(distance, elapsed) {
      const absoluteDistance = Math.abs(distance);
      const baseSpeed = this.getTranscriptScrollSpeedValue() *
        (this.mode.mode === 'default' ? this.getTranscriptLineHeightScale() : 1);
      let speed = baseSpeed;

      if (absoluteDistance > 420) {
        speed = baseSpeed * 4.45;
      } else if (absoluteDistance > 180) {
        speed = baseSpeed * 3.05;
      } else if (absoluteDistance > 72) {
        speed = baseSpeed * 1.95;
      }

      return Math.min(absoluteDistance, Math.max(1, elapsed * speed / 1000));
    },
    getTranscriptScrollSpeedValue() {
      const value = Number(this.settings && this.settings.transcript_scroll_speed);

      if (!Number.isFinite(value)) {
        return 72;
      }

      return Math.min(180, Math.max(40, value));
    },
    animateTranscriptTeleprompter(timestamp) {
      if (this.mode.mode === 'default' && this.isTranscriptPageHidden()) {
        this.cancelTranscriptTeleprompterAnimation();
        return;
      }

      this.refreshLiveTranscriptTeleprompterTarget();

      const distance = this.transcriptTeleprompterTarget - this.transcriptTeleprompterOffset;

      if (Math.abs(distance) <= 0.5) {
        this.setTranscriptTeleprompterOffset(this.transcriptTeleprompterTarget);
        this.cancelTranscriptTeleprompterAnimation();
        return;
      }

      if (this.shouldSnapTranscriptTeleprompter()) {
        this.setTranscriptTeleprompterOffset(this.transcriptTeleprompterTarget);
        this.cancelTranscriptTeleprompterAnimation();
        return;
      }

      const elapsed = this.transcriptTeleprompterLastFrameAt ? Math.min(48, timestamp - this.transcriptTeleprompterLastFrameAt) : 16;
      this.transcriptTeleprompterLastFrameAt = timestamp;
      const direction = distance > 0 ? 1 : -1;
      const nextOffset = this.transcriptTeleprompterOffset + (direction * this.getTranscriptTeleprompterStep(distance, elapsed));

      this.setTranscriptTeleprompterOffset(nextOffset);
      this.transcriptTeleprompterFrame = requestAnimationFrame(this.animateTranscriptTeleprompter);
    },
    setTranscriptTeleprompterOffset(offset) {
      const wrapEl = this.$refs.transcriptsWrap;
      const trackEl = this.getTranscriptTrackElement();

      if (trackEl) {
        trackEl.style.transform = '';
      }

      if (!wrapEl) {
        this.transcriptTeleprompterOffset = Math.max(0, offset);
        return;
      }

      const nextOffset = this.clampTranscriptTeleprompterOffset(offset, wrapEl);

      wrapEl.scrollTop = nextOffset;
      const appliedOffset = wrapEl.scrollTop;
      const scrollWasApplied = Math.abs(appliedOffset - nextOffset) <= 1 || nextOffset <= 1;

      if (trackEl && !scrollWasApplied) {
        trackEl.style.transform = `translate3d(0, ${-nextOffset}px, 0)`;
        this.transcriptTeleprompterOffset = nextOffset;
        return;
      }

      this.transcriptTeleprompterOffset = appliedOffset;
    },
    cancelTranscriptTeleprompterAnimation() {
      if (this.transcriptTeleprompterFrame) {
        cancelAnimationFrame(this.transcriptTeleprompterFrame);
      }

      this.transcriptTeleprompterFrame = null;
      this.transcriptTeleprompterLastFrameAt = 0;
      this.transcriptAutoScrollActive = false;
    },
    resetTranscriptTeleprompter() {
      this.cancelTranscriptTeleprompterAnimation();
      this.cancelDefaultTranscriptTopSpacerAnimation(false);

      if (this.transcriptTeleprompterResizeFrame) {
        cancelAnimationFrame(this.transcriptTeleprompterResizeFrame);
        this.transcriptTeleprompterResizeFrame = null;
      }

      this.transcriptTeleprompterTarget = 0;
      this.setDefaultTranscriptTopSpacerOffset(0);
      this.transcriptTopSpacerTarget = 0;
      this.transcriptFollowMode = 'live';
      this.transcriptAutoScrollActive = false;
      this.transcriptManualScrollHoldUntil = 0;
      this.transcriptReviewTouchY = null;
      this.setTranscriptTeleprompterOffset(0);
    },
    setupTranscriptTeleprompterObserver() {
      this.teardownTranscriptTeleprompterObserver();

      const trackEl = this.getTranscriptTrackElement();
      if (!trackEl || typeof ResizeObserver === 'undefined') {
        return;
      }

      this.transcriptTeleprompterObserver = new ResizeObserver(() => {
        this.scheduleTranscriptTeleprompter();
      });
      this.transcriptTeleprompterObserver.observe(trackEl);
    },
    setupToolbarResizeObserver() {
      const toolbarEl = this.$refs.toolbarwrap;
      if (!toolbarEl || typeof ResizeObserver === 'undefined') {
        return;
      }

      // the keyboard input bar sits right above the toolbar; while it's hidden via v-show
      // its offsetHeight is 0, so the observer tracks open/close and multi-line growth for free
      const keyboardBarEl = this.$refs.keyboardinputbar;
      const measure = () => {
        this.toolbarHeight = toolbarEl.offsetHeight;
        this.keyboardBarHeight = keyboardBarEl ? keyboardBarEl.offsetHeight : 0;
      };

      measure();
      this.toolbarResizeObserver = new ResizeObserver(measure);
      this.toolbarResizeObserver.observe(toolbarEl);
      if (keyboardBarEl) {
        this.toolbarResizeObserver.observe(keyboardBarEl);
      }
    },
    teardownToolbarResizeObserver() {
      if (this.toolbarResizeObserver) {
        this.toolbarResizeObserver.disconnect();
        this.toolbarResizeObserver = null;
      }
    },
    teardownTranscriptTeleprompterObserver() {
      if (this.transcriptTeleprompterObserver) {
        this.transcriptTeleprompterObserver.disconnect();
        this.transcriptTeleprompterObserver = null;
      }
    },
    syncTranscriptReviewTarget() {
      if (this.mode.mode === 'default') {
        this.transcriptTeleprompterTarget = this.getTranscriptMaxOffset();
        return this.transcriptTeleprompterTarget > 0;
      }

      const measuredTarget = this.measureTranscriptTeleprompterTarget();

      if (measuredTarget === null) {
        return false;
      }

      this.transcriptTeleprompterTarget = this.clampTranscriptTeleprompterOffset(measuredTarget);
      return this.transcriptTeleprompterTarget > 0;
    },
    handleTranscriptWheel(event) {
      if (this.mode.mode !== 'default') {
        return;
      }

      const wrapEl = this.$refs.transcriptsWrap;
      if (!wrapEl) {
        return;
      }

      const maxOffset = this.getTranscriptMaxOffset(wrapEl);
      const isMovingAwayFromLive = event.deltaY < 0;
      const isAlreadyReviewing = wrapEl.scrollTop < maxOffset - 2;

      if (isMovingAwayFromLive || isAlreadyReviewing) {
        this.cancelTranscriptTeleprompterAnimation();
        this.cancelDefaultTranscriptTopSpacerAnimation();
        this.holdTranscriptManualScroll();
        this.transcriptFollowMode = 'review';
        this.scroller.scrolled = true;
        this.scroller.hasNew = this.scroller.hasNew || wrapEl.scrollTop < maxOffset - 2;
      }
    },
    handleTranscriptPointerDown(event) {
      if (this.mode.mode !== 'default') {
        return;
      }

      const wrapEl = this.$refs.transcriptsWrap;
      if (!wrapEl) {
        return;
      }

      if (event.pointerType === 'mouse') {
        const rect = wrapEl.getBoundingClientRect();
        const scrollbarHitArea = 16;
        if (event.clientX < rect.right - scrollbarHitArea) {
          return;
        }
      }

      this.cancelTranscriptTeleprompterAnimation();
      this.cancelDefaultTranscriptTopSpacerAnimation();
      this.holdTranscriptManualScroll();
      this.transcriptFollowMode = 'review';
      this.scroller.scrolled = true;
    },
    handleTranscriptTouchStart(event) {
      if (this.mode.mode !== 'default' || !event.touches || event.touches.length !== 1) {
        return;
      }

      this.transcriptReviewTouchY = event.touches[0].clientY;
    },
    handleTranscriptTouchMove(event) {
      if (this.mode.mode !== 'default' || this.transcriptReviewTouchY === null || !event.touches || event.touches.length !== 1) {
        return;
      }

      const nextTouchY = event.touches[0].clientY;
      const deltaY = this.transcriptReviewTouchY - nextTouchY;
      this.transcriptReviewTouchY = nextTouchY;

      if (Math.abs(deltaY) < 2) {
        return;
      }

      const wrapEl = this.$refs.transcriptsWrap;
      if (!wrapEl) {
        return;
      }

      const maxOffset = this.getTranscriptMaxOffset(wrapEl);
      this.cancelTranscriptTeleprompterAnimation();
      this.cancelDefaultTranscriptTopSpacerAnimation();
      this.holdTranscriptManualScroll();
      this.transcriptFollowMode = 'review';
      this.scroller.scrolled = true;
      this.scroller.hasNew = this.scroller.hasNew || wrapEl.scrollTop < maxOffset - 2;
    },
    handleTranscriptTouchEnd() {
      this.transcriptReviewTouchY = null;
    },
    handleTranscriptScroll(e) {
      const scroller = e.target;
      const bottomTolerance = 16;
      const atBottomByReverseTop = Math.abs(scroller.scrollTop) <= bottomTolerance;
      const atBottomByDistance = Math.abs(scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop) <= bottomTolerance;
      const isAtBottom = this.mode.mode === 'default'
          ? atBottomByDistance
          : atBottomByReverseTop || atBottomByDistance;

      if (this.mode.mode === 'default') {
        const currentOffset = this.clampTranscriptTeleprompterOffset(scroller.scrollTop, scroller);
        const currentTarget = this.getTranscriptMaxOffset(scroller);

        this.transcriptTeleprompterOffset = currentOffset;
        this.transcriptTeleprompterTarget = currentTarget;

        if (this.transcriptAutoScrollActive && this.transcriptFollowMode === 'live' && !this.isTranscriptManualScrollHeld()) {
          if (isAtBottom) {
            this.transcriptAutoScrollActive = false;
            this.transcriptFollowMode = 'live';
            this.scroller.scrolled = false;
            this.scroller.hasNew = false;
          }

          this.scroller.scrollTop = currentOffset;
          return;
        }

        if (this.transcriptFollowMode !== 'review' && !this.isTranscriptManualScrollHeld()) {
          this.transcriptFollowMode = 'live';
          this.scroller.scrolled = false;
          this.scroller.hasNew = false;
          this.scroller.scrollTop = currentOffset;

          if (!isAtBottom) {
            this.scrollDefaultTranscriptsToLive();
          }

          return;
        }

        if (isAtBottom) {
          this.transcriptManualScrollHoldUntil = 0;
          this.transcriptFollowMode = 'live';
          this.scroller.scrolled = false;
          this.scroller.hasNew = false;
        } else {
          this.transcriptFollowMode = 'review';
          this.scroller.scrolled = true;
        }

        this.scroller.scrollTop = currentOffset;
        return;
      }

      if (isAtBottom) {
        this.scroller.scrolled = false;
        this.scroller.hasNew = false;
      } else {
        this.scroller.scrolled = true;
      }

      this.scroller.scrollTop = scroller.scrollTop;
    },
    restartIdleTimer(autostart) {
      const autostop_timeout = this.settings.autostop_timeout ?? 1800;

      const idleTime = new Date();
      idleTime.setSeconds(idleTime.getSeconds() + autostop_timeout);

      this.idleTimer = useTimer(idleTime, false);
      // Restarts to 5 minutes timer
      this.idleTimer.restart(idleTime, autostart);
    },
    stopIdleTimer() {
      if (this.idleTimer) {
        this.idleTimer.restart(false);
      }
    },
    getParticipantLangObject(langCode) {
      let selectedLang = false;

      this.languages.forEach(lang => {
        lang.dialects.forEach(dialect => {
          if (dialect.code == langCode) {
            selectedLang = dialect;
            return;
          }
        });
      });
      if (!selectedLang || !selectedLang.aws_asr_code) {
        return langCode;
      }

      return selectedLang.aws_asr_code;
    },
    handleBackButton() {
      console.log("Back Button Pressed.  Session status: ", this.sessionActive);
      if (this.sessionActive) {
        this.leaveSessionPopup = true;
        history.pushState(null, "", window.location.href); // re-insert current page into history
      }
    },
    async getAvailableMicrophones() {
      try {
        let permissionGranted = false;
        await navigator.mediaDevices.getUserMedia({ audio: true })
            .then(stream => {
              stream.getTracks().forEach(track => track.stop());
              permissionGranted = true;
            })
            .catch(error => {
              console.warn('Permission denied, will show default device names:', error);
              permissionGranted = false;
            });

        if (permissionGranted) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          this.availableMicrophones = devices.filter(device => {
            if (device.kind !== 'audioinput') return false;

            const label = device.label.toLowerCase();
            // Filter out Windows virtual devices
            if (label.startsWith('communications') || label.startsWith('default')) {
              return false;
            }

            return true;
          });

          // If no microphone is selected yet, use the default one
          if (!this.selectedMicrophoneId && this.availableMicrophones.length > 0) {
            this.selectedMicrophoneId = this.availableMicrophones[0].deviceId;
          }
        } else{
          this.availableMicrophones = [];
          this.selectedMicrophoneId = null;
        }
      } catch (error) {
        console.error('Error getting microphones:', error);
        this.$toast.info('Unable to access microphones');
      }
    },
    getMicrophoneName(mic) {
      const name = mic.label || `Microphone ${this.availableMicrophones.indexOf(mic) + 1}`;
      return name.length > 30 ? name.substring(0, 30) + '...' : name;
    },
    async selectMicrophone(deviceId) {
      this.selectedMicrophoneId = deviceId

      this.$store.dispatch("event/saveSettings", {
        ...this.settings,
        selectedMicrophoneId: deviceId
      })

      if (this.awsIsRecording) {
        await this.restartRecording(false)
      }
    },
    async getAvailableSpeakers() {
      try {
        let permissionGranted = false;
        await navigator.mediaDevices.getUserMedia({ audio: true })
            .then(stream => {
              stream.getTracks().forEach(track => track.stop());
              permissionGranted = true;
            })
            .catch(error => {
              console.warn('Permission denied for speakers, will show default device names:', error);
              permissionGranted = false;
            });

        if (permissionGranted) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          this.availableSpeakers = devices.filter(device => {
            if (device.kind !== 'audiooutput') return false;

            const label = device.label.toLowerCase();
            // Filter out Windows virtual devices
            if (label.startsWith('communications') || label.startsWith('default')) {
              return false;
            }

            return true;
          });

          // If no speaker is selected yet, use the default one
          if (!this.selectedSpeakerId && this.availableSpeakers.length > 0) {
            this.selectedSpeakerId = this.availableSpeakers[0].deviceId;
          }

        } else {
          this.availableSpeakers = [];
          this.selectedSpeakerId = null;
        }

      } catch (error) {
        console.error('Error getting speakers:', error);
        this.$toast.info('Unable to access speakers');
      }
    },

    getSpeakerName(speaker) {
      const name = speaker.label || `Speaker ${this.availableSpeakers.indexOf(speaker) + 1}`;
      return name.length > 30 ? name.substring(0, 30) + '...' : name;
    },

    selectSpeaker(deviceId) {
      this.selectedSpeakerId = deviceId;

      this.$store.dispatch("event/saveSettings", {
        ...this.settings,
        selectedSpeakerId: deviceId
      });

      if (this.soundPlayerTTS && typeof this.soundPlayerTTS.setSinkId !== 'undefined') {
        this.soundPlayerTTS.setSinkId(deviceId).catch(error => {
          console.error('Error setting speaker:', error);
        });
      }
    },
    incrementSpeed() {
      if (this.settings.tts_speed < this.constraints.tts_speed_max) {
        this.settings.tts_speed += this.constraints.tts_speed_step;
      }
    },
    decrementSpeed() {
      if (this.settings.tts_speed > this.constraints.tts_speed_min) {
        this.settings.tts_speed -= this.constraints.tts_speed_step
      }
    },
    resetBarMenuState() {
      this.microphoneMenuOpen = false;
      this.speakerMenuOpen = false;
      this.viewsMenuOpen = false;
      this.chromakeyMenuOpen = false;
    },
    toggleSessionBars() {
      this.barsManuallyHidden = !this.barsManuallyHidden;
      this.resetBarMenuState();

      if (this.barsManuallyHidden) {
        this.clearInactivityTimer();
        this.barsVisible = false;
        return;
      }

      this.barsVisible = true;
      this.startInactivityTimer();
    },
    startInactivityTimer() {
      this.clearInactivityTimer();

      if (this.barsManuallyHidden) {
        return;
      }

      this.inactivityTimer = setTimeout(() => {
        if (!this.barsManuallyHidden && (!this.anyBarMenuOpened || !this.recording || !this.typing)) {
          this.barsVisible = false;
        }
      }, this.inactivityTimeout);
    },

    clearInactivityTimer() {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
        this.inactivityTimer = null;
      }
    },

    handleUserActivity() {
      if (this.barsManuallyHidden) {
        return;
      }

      if (!this.anyBarMenuOpened) {
        this.barsVisible = true;
        this.startInactivityTimer();
      }
    },

    setupInactivityListeners() {
      document.addEventListener('mousemove', this.handleUserActivity);
      document.addEventListener('touchstart', this.handleUserActivity);
      document.addEventListener('keydown', this.handleUserActivity);
      document.addEventListener('click', this.handleUserActivity);
    },

    removeInactivityListeners() {
      document.removeEventListener('mousemove', this.handleUserActivity);
      document.removeEventListener('touchstart', this.handleUserActivity);
      document.removeEventListener('keydown', this.handleUserActivity);
      document.removeEventListener('click', this.handleUserActivity);
    },
    getTipMessage() {
      if (this.mode.mode === 'chromakey') {
        return this.$t('tip_chromakey_mode') !== 'tip_chromakey_mode'
            ? this.$t('tip_chromakey_mode')
            : 'Tip: Green Screen view provides captions on top of a video similar to closed captions.';
      }
      if (!this.isAllowedToSpeak && !this.isHost) {
        return this.$t('tip_general_info') !== 'tip_general_info'
            ? this.$t('tip_general_info')
            : 'Welcome! Forum provides live captions and translations so everyone can follow the conversation easily. You can read along in your language — or even have the captions read aloud for you. Have a great session!';
      }

      return this.$t('tip_general_info') !== 'tip_general_info'
          ? this.$t('tip_general_info')
          : 'Welcome! Forum provides live captions and translations so everyone can follow the conversation easily. You can read along in your language — or even have the captions read aloud for you. Have a great session!';
    },
    getDefaultTipMessageParagraphs() {
      const message = this.getTipMessage();
      const closing = 'Have a great session!';
      if (message.includes('\n')) {
        return message.split(/\n{2,}/).map((paragraph) => paragraph.trim()).filter(Boolean);
      }

      const closingIndex = message.indexOf(closing);
      if (closingIndex > 0) {
        return [message.slice(0, closingIndex).trim(), closing];
      }

      return [message];
    },

    chromakeyTouch() {
      if (this.mode.mode !== 'chromakey') return;

      this.chromakeyHideCaptions = false;

      if (this.chromakeyIdleTimer) {
        clearTimeout(this.chromakeyIdleTimer);
        this.chromakeyIdleTimer = null;
      }

      this.chromakeyIdleTimer = setTimeout(() => {
        // Nothing has been said yet, so the caption is still the tip: keep it on screen
        // until the first speech arrives, which restarts this timer via chromakeyTouch.
        if (this.mode.mode === 'chromakey' && this.combinedMessages.length > 0) {
          this.chromakeyHideCaptions = true;
        }
      }, this.chromakeyIdleTimeoutMs);
    },

    chromakeyIdleStop() {
      if (this.chromakeyIdleTimer) {
        clearTimeout(this.chromakeyIdleTimer);
        this.chromakeyIdleTimer = null;
      }
      this.chromakeyHideCaptions = false;
    },

    resetChromakeySlide() {
      this.captionSliding = false;
      this.captionSlideOffset = 0;
      this.chromakeyTrackHeight = 0;
    },

    measureChromakeyMetrics() {
      const track = this.$refs.chromakeyTrack;
      const caption = track && track.firstElementChild;
      if (!caption) return;

      const rects = caption.getClientRects();
      if (!rects.length) return;

      // Client rects of an inline box are padding boxes, so the last one reaching past the
      // track — whose height is exactly its line boxes — measures how far a pill spills
      // below the line it belongs to. Depends on the font's metrics, so it is measured
      // rather than derived.
      const overhang = Math.max(1, Math.ceil(rects[rects.length - 1].bottom - track.getBoundingClientRect().bottom));
      if (overhang !== this.chromakeyPillOverhang) {
        this.chromakeyPillOverhang = overhang;
      }

      if (rects.length > 1) {
        // The real line box is not reliably font-size * 1.5: browsers clamp font sizes below
        // their configured minimum, and fractional sizes round per line. Deriving the window
        // height from the rendered value keeps it correct in both cases.
        const lineBox = (rects[rects.length - 1].top - rects[0].top) / (rects.length - 1);
        if (lineBox > 0 && lineBox !== this.chromakeyLineBox) {
          this.chromakeyLineBox = lineBox;
        }
      }
    },

    updateChromakeySlide() {
      if (this.mode.mode !== 'chromakey') return;

      const track = this.$refs.chromakeyTrack;
      if (!track) {
        this.chromakeyTrackHeight = 0;
        return;
      }

      this.measureChromakeyMetrics();

      const height = track.offsetHeight;
      const previousHeight = this.chromakeyTrackHeight;
      this.chromakeyTrackHeight = height;

      // The track is pinned to the bottom of the window, so gaining a line pushes everything
      // up by that line's height in one frame. Only that growth needs animating: put the text
      // back where it just was, then let it travel to its new place.
      if (!previousHeight || height <= previousHeight) {
        this.captionSliding = false;
        this.captionSlideOffset = 0;
        return;
      }

      this.captionSliding = false;
      this.captionSlideOffset = height - previousHeight;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.mode.mode !== 'chromakey') return;
          this.captionSliding = true;
          this.captionSlideOffset = 0;
        });
      });
    },

    toggleChromakeyCaptions() {
      if (this.mode.mode !== 'chromakey') return;

      this.chromakeyHideCaptions = !this.chromakeyHideCaptions;

      if (this.chromakeyHideCaptions) {
        if (this.chromakeyIdleTimer) {
          clearTimeout(this.chromakeyIdleTimer);
          this.chromakeyIdleTimer = null;
        }
        return;
      }

      this.chromakeyTouch();
    },
  },
  computed: {
    ...mapState('auth', ['agreed', 'authorized', 'token']),
    ...mapState('lang', ['languages']),
    ...mapState('event', ['event', 'is_room_owner', 'guest', 'settings', 'preRoomPassed']),
    ...mapGetters('auth', [
      'udid',
    ]),
    ...mapState('nonpersist', ['user', 'asrTimeLeft', 'sessionPassword']),
    ...mapState('hidden', ['hidden_settings']),
    anyPopupShown() {
      return this.roomCodePopup || this.roomSettingsPopup || this.secureSessionPopup || this.muteAudiencePopup || this.participantsModalOpen || this.roomOverlayed;
    },
    // Actions the local user currently has "on" (e.g. own hand is raised).
    // Keyed the same way the `user_action` socket handler stores state.
    activeActions() {
      const state = this.userActionStateByUser[this.getUserActionTargetId()] || {};
      const actionMap = state.actions && typeof state.actions === 'object' ? state.actions : {};

      return this.availableActions.filter((action) => Boolean(actionMap[action] || state[action]));
    },
    sessionParticipants() {
      const rawParticipants = Array.isArray(this.participants) ? this.participants : [];

      const mappedParticipants = rawParticipants
        .map((participant, index) => {
          if (!participant || typeof participant !== 'object') {
            return null;
          }

          const id = participant.socket_id || participant.socketId || participant.sender_id || participant.user_id || participant.userId || participant.id || `participant_${index}`;
          const name = participant.display_name || participant.name || 'Guest';
          const language = participant.language || this.participant.language || 'en-US';
          const actionState = this.userActionStateByUser[id] || {};
          const actionMap = actionState.actions && typeof actionState.actions === 'object' ? actionState.actions : {};
          const raisedHand = Boolean(actionMap.raiseHand || actionState.raiseHand);
          const raisedHandAt = Number(actionState.raisedHandAt || actionMap.raisedHandAt || 0);
          const thumbsUpAt = Number(actionState.thumbsUpAt || actionMap.thumbsUpAt || 0);
          const thumbsUp = Boolean(actionMap.thumbsUp || actionState.thumbsUp) && Date.now() - thumbsUpAt < 2000;
          const laughAt = Number(actionState.laughAt || actionMap.laughAt || 0);
          const laugh = Boolean(actionMap.laugh || actionState.laugh) && Date.now() - laughAt < 2000;
          const heartAt = Number(actionState.heartAt || actionMap.heartAt || 0);
          const heart = Boolean(actionMap.heart || actionState.heart) && Date.now() - heartAt < 2000;
          const clapAt = Number(actionState.clapAt || actionMap.clapAt || 0);
          const clap = Boolean(actionMap.clap || actionState.clap) && Date.now() - clapAt < 2000;

          return {
            id,
            name,
            language,
            raisedHand,
            raisedHandAt,
            thumbsUp,
            thumbsUpAt,
            laugh,
            laughAt,
            heart,
            heartAt,
            clap,
            clapAt,
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (a.raisedHand !== b.raisedHand) {
            return Number(b.raisedHand) - Number(a.raisedHand);
          }

          if (!a.raisedHand) {
            return 0;
          }

          return (a.raisedHandAt || 0) - (b.raisedHandAt || 0);
        });

      let raisedIndex = 0;
      return mappedParticipants.map((participant) => {
        if (!participant.raisedHand) {
          return { ...participant, raisedHandOrder: 0 };
        }

        raisedIndex += 1;
        return { ...participant, raisedHandOrder: raisedIndex };
      });
    },
    showOriginalToggleLabel() {
      const key = this.shouldShowOriginalWithTranslation() ? 'captions_hide_original' : 'captions_show_original';
      const fallback = this.shouldShowOriginalWithTranslation() ? 'Hide original' : 'Show original';
      return this.$t(key) !== key ? this.$t(key) : fallback;
    },
    // Only the Regular view is themed; Green Screen keeps its own look.
    isLightCaptionTheme() {
      return this.mode.mode === 'default' && this.defaultTranscriptTheme === 'light';
    },
    defaultTranscriptFontFamilyCss() {
      return Constants.CAPTION_FONTS[this.defaultTranscriptFont] || Constants.CAPTION_FONTS.sans;
    },
    settingsDrawerTitle() {
      if (this.captionSettings) {
        return this.$t('caption_settings') !== 'caption_settings' ? this.$t('caption_settings') : 'Caption Settings';
      }
      return this.$t('session_settings');
    },
    anyBarMenuOpened() {
      return this.microphoneMenuOpen || this.speakerMenuOpen || this.viewsMenuOpen || this.captionSettingsBar || this.chromakeyMenuOpen;
    },
    connectionIndicatorOffset() {
      return this.barsVisible ? this.toolbarHeight + this.keyboardBarHeight : 0;
    },
    // Line box of a default-mode caption line (.transcript__text has line-height 1.45).
    defaultTranscriptLineHeightPx() {
      return Math.round(this.fontSize * 1.45);
    },
    defaultTranscriptLineHeightCss() {
      return `${this.defaultTranscriptLineHeightPx}px`;
    },
    defaultTranscriptGroups() {
      const groups = [{
        key: 'welcome',
        speakerId: 'welcome',
        kind: 'welcome',
        name: 'Sorenson Forum',
        language: '',
        items: [{
          key: 'welcome_message',
          type: 'welcome',
        }],
      }];

      const appendMessage = (message, type, index) => {
        if (!message) {
          return;
        }

        const speakerId = this.getMessageSpeakerId(message) || `${type}_${index}`;
        const lastGroup = groups[groups.length - 1];
        const group = lastGroup && lastGroup.kind !== 'welcome' && lastGroup.speakerId === speakerId
          ? lastGroup
          : {
              key: `speaker_group_${speakerId}_${groups.length}`,
              speakerId,
              kind: type,
              name: this.getMessageSpeakerName(message),
              language: this.getMessageSpeakerLanguage(message),
              items: [],
            };

        if (group !== lastGroup) {
          groups.push(group);
        }

        group.items.push({
          key: message.message_id ? `message_${message.message_id}` : `${type}_${index}`,
          type,
          message,
        });
      };

      this.transcripts.forEach((transcript, index) => appendMessage(transcript, 'final', index));
      this.partials.forEach((partial, index) => appendMessage(partial, 'partial', index));

      return groups;
    },
    isAllowedToSpeak() {
      return this.sessionFeatures.asr.allowed ||
          ((this.participant.session_role === 'sponsor' ||
              this.participant.session_role === 'host') && this.sessionFeatures.asr.reason !== 'sponsor_run_out_resources');
    },
    isHost() {
      return this.participant.session_role === 'sponsor' ||
          this.participant.session_role === 'host';
    },
    combinedMessages() {
      let messages = [];

      if (this.mergedFinalMessages.length > 0) {
        messages.push(this.mergedFinalMessages);
      }

      const latestPartials = this.partials.slice(-1);
      latestPartials.forEach(partial => {
        // Go through the playback buffer so captions reveal progressively, the same way
        // the default view does, instead of snapping to each raw ASR update.
        messages.push(this.getPartialPlaybackText(partial));
      });

      if (this.partial && this.hasOwnMessage() && this.recording) {
        messages.push(this.mergeStringsIf(this.partial.original.text, this.partial.concat));
      }
      return messages.join('\n');
    },
    mergedFinalMessages() {
      const messages = this.transcripts.slice(-3);
      return messages.map(tr => {
        return this.hasTranslationForUser(tr)
            ? this.getTranslationForUser(tr).text
            : tr.original.text;
      }).join('\n');
    },
    chromakeyCaptionText() {
      return this.combinedMessages.length > 0 ? this.combinedMessages : this.getTipMessage();
    },
    chromakeyLineHeight() {
      // Measured once the captions have rendered; the ratio matches the 150% line-height
      // and only stands in until then.
      return this.chromakeyLineBox || this.fontSize * 1.5;
    },
    chromakeyBottomGutter() {
      // The pill is taller than its line box — that overlap is what keeps consecutive lines
      // touching — so it hangs below the last line by ~0.12em and would be shaved without
      // this slack. Nothing is reserved above on purpose: headroom there would expose a
      // sliced strip of the line that just scrolled out instead of cutting cleanly along
      // the line boundary. The floor covers the small sizes, where the pill's rem padding
      // floor makes the overhang bigger than any share of the font size would suggest.
      return Math.ceil(Math.max(10, this.fontSize * 0.14));
    },
    chromakeyBottomGutterPx() {
      return `${this.chromakeyBottomGutter}px`;
    },
    chromakeyViewportHeight() {
      // Rounded down: the measured line box is fractional, and rounding up would lift the
      // window's top edge into the pill of the line above by up to a pixel.
      const lines = Math.floor(this.chromakeyLineHeight * this.chromakeyMaxLines);
      // Trimmed by the pill overhang so the line that just scrolled out is clipped whole:
      // its pill hangs below its own line box, and any of it left inside the window paints
      // as a black bar wherever that line was wider than the one now on top. The extra pixel
      // keeps the clip edge off the pill's edge, which at fractional device pixel ratios
      // (125%/150% Windows scaling) is antialiased into a faint line even when they coincide.
      return `${lines + this.chromakeyBottomGutter - this.chromakeyPillOverhang - 1}px`;
    },
    defaultText() {
      let modeTitle = "Hold To Talk";

      switch (this.settings.speech_mode) {
        case "custom_pause":
          modeTitle = "Click To Talk";
          break;
        case "live_translate":
          modeTitle = "Click To Talk";
          break;
      }

      return modeTitle;
    },
    transcriptsWidth() {
      if (this.mode.mode === 'default') {
        return '100%';
      }
      return `calc(70% + ${this.fontSize * 8}px)`;
    },
    // Phones always show the captions wide: a narrow column only wastes the little width they
    // have and breaks words apart. The saved choice is kept for larger screens.
    effectiveTranscriptWidth() {
      return this.isMobile ? 'wide' : this.defaultTranscriptWidth;
    },
    chromakeyMargin() {
      return this.isMobile ? 'wider' : this.mode.margin;
    },
    fontSizeIndicatorLabel() {
      return translate('font_size_indicator', 'Font size');
    },
    captionDragHint() {
      if (this.captionDragPointer !== 'mouse') {
        return translate('green_screen_drag_hint_touch', 'Double-tap to reset position');
      }
      return translate('green_screen_drag_hint', `Hold ${ALT_KEY} to move freely · Double-click to reset · Arrow keys to nudge`);
    },
    transcriptsJustify() {
      return this.mode.align === 'left' ? 'flex-start' : this.mode.align === 'right' ? 'flex-end' : 'center';
    },
    sessionDuration() {
      const diffMs = this.sessionEndTime - this.sessionStartTime;
      return Math.round(((diffMs % 86400000) % 3600000) / 60000); // minutes
    },
    enableAsrTimer() {
      return !this.event.is_countdown_disabled && !this.conference.is_countdown_disabled;
    },
    isParticipationEnabled() {
      return !this.conference.is_participating_disabled;
    },
    recordButtonId(){
      if (this.isHoldToTalk){
        return "Record_Hold_to_talk";
      }
      if (this.isCustomPause){
        return "Record_Pause_to_translate";
      }
      if (this.isLiveTranslate){
        return "Record_Live_Translate";
      }
      return 'Record_Unknown_Mode';
    },
    isHoldToTalk() {
      return this.settings.speech_mode == "hold_to_talk";
    },
    isCustomPause() {
      return this.settings.speech_mode == "custom_pause";
    },
    isLiveTranslate() {
      return this.settings.speech_mode == "live_translate";
    },
    ttsSpeedDisplay() {
      return (this.settings.tts_speed / 100).toFixed(2) + 'x';
    },
    getLanguageAsrCode() {
      let selectedLang = false;

      this.languages.forEach(lang => {
        lang.dialects.forEach(dialect => {
          if (dialect.code == this.participant.language) {
            selectedLang = dialect;
            return;
          }
        });
      });
      if (!selectedLang || !selectedLang.aws_asr_code) {
        return this.participant && this.participant.language ? this.participant.language : Constants.DEFAULT_LANGUAGE;
      }

      return selectedLang.aws_asr_code;
    },
    customVocabularyName() {
      const entry = this.customVocabularyList && this.customVocabularyList[this.getLanguageAsrCode];
      return entry && entry.status === 'ready' && entry.id ? entry.id : null;
    }
  },
  watch:{
    // showRoomCodePopup() turns the room overlay on, but the sheet modal closes itself
    // via v-model (X, swipe, outside click) and only resets roomCodePopup. Drop the
    // overlay here so it doesn't linger and swallow the next click.
    roomCodePopup(open) {
      if (!open && !this.secureSessionPopup && !this.welcomePopup) {
        this.roomOverlayed = false;
      }
    },
    'settings.chroma_overlay_color': function() {
      this.saveChromakeySettings();
    },
    'idleTimer.isExpired': function (value){
      if (!this.isHoldToTalk && value) {
        this.stopIdleTimer();
        this.stopRecording();
      }
    },
    'participant.language': function (value) {
      const langObj = this.getParticipantLangObject(value);
      const isLiveTranslateAvailable = langObj && langObj.auto_punctuation;
      if (!isLiveTranslateAvailable && this.isLiveTranslate) {
        this.$store.dispatch("event/saveSpeechMode", "custom_pause");
      }
    },
    'mode.margin': function() { this.saveChromakeySettings(); },
    // Also changes when a resize crosses the phone breakpoint, which forces wide.
    chromakeyMargin() {
      this.resetChromakeySlide();
      this.$nextTick(this.clampChromakeyPosition);
    },
    'mode.align': function() { this.saveChromakeySettings(); },
    'fontSize': function() {
      this.saveChromakeySettings();
      this.scheduleTranscriptTeleprompter(true);
      this.resetChromakeySlide();
      this.$nextTick(() => {
        this.updateChromakeySlide();
        this.clampChromakeyPosition();
      });
    },
    // Both rewrap the captions, so the teleprompter has to re-measure like it does for font size.
    effectiveTranscriptWidth() {
      this.scheduleTranscriptTeleprompter(true);
    },
    defaultTranscriptFont() {
      this.scheduleTranscriptTeleprompter(true);
    },
    'chromakeyMaxLines': function() {
      this.saveChromakeySettings();
      this.resetChromakeySlide();
      this.$nextTick(this.clampChromakeyPosition);
    },
    chromakeyCaptionText() {
      if (this.mode.mode !== 'chromakey') return;
      this.$nextTick(this.updateChromakeySlide);
    },
    chromakeyHideCaptions() {
      this.resetChromakeySlide();
      // The caption window (and with it the pointer capture) goes away mid-drag.
      this.endCaptionDrag();
    },
    'fontFamily': function() {
      this.saveChromakeySettings();
      // Different metrics, so the pill spills past its line box by a different amount.
      this.$nextTick(this.updateChromakeySlide);
    },

    anyBarMenuOpened(isOpen) {
      if (this.barsManuallyHidden) {
        this.clearInactivityTimer();
        this.barsVisible = false;
        return;
      }

      if (isOpen) {
        this.clearInactivityTimer();
        this.barsVisible = true;
      } else {
        this.startInactivityTimer();
      }
    },
    transcripts() {
      this.scheduleTranscriptTeleprompter();
    },
    partials() {
      this.scheduleTranscriptTeleprompter();
    },
    'mode.mode'(value) {
      if (value !== 'default') {
        this.teardownTranscriptTeleprompterObserver();
        this.resetTranscriptTeleprompter();
        return;
      }
      this.$nextTick(() => {
        this.setupTranscriptTeleprompterObserver();
        this.scrollTranscripts(true);
      });
    },
  },
  async mounted() {
    history.pushState(null, "", window.location.href);
    window.addEventListener('resize', this.onResize)
    window.addEventListener("popstate", this.handleBackButton);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('focus', this.handleWindowFocus);
    window.addEventListener('online', this.handleWindowOnline);
    this.connectionQuality.setupBrowserListeners();
    this.setRoomToken();
    this.setParticipant();
    if (!this.isGuest) {
      this.setCustomTerms();
    }
    this.user_id = this.udid;

    // default chromakey lines for large screens
    if (window.innerWidth >= 1440) {
      this.chromakeyMaxLines = 1;
    }

    this.loadChromakeySettings();
    this.applyStoredFontSize();
    this.applyStoredCaptionSettings();

    if (this.user || this.guest) {
      this.setupApiSocket();
    }
    this.setKeyboardShortcuts();

    const transcriptsWrapEl = this.$refs.transcriptsWrap;
    if (transcriptsWrapEl) {
      transcriptsWrapEl.addEventListener("scroll", this.handleTranscriptScroll, { passive: true });
      transcriptsWrapEl.addEventListener("wheel", this.handleTranscriptWheel, { passive: true });
      transcriptsWrapEl.addEventListener("pointerdown", this.handleTranscriptPointerDown, { passive: true });
      transcriptsWrapEl.addEventListener("touchstart", this.handleTranscriptTouchStart, { passive: true });
      transcriptsWrapEl.addEventListener("touchmove", this.handleTranscriptTouchMove, { passive: true });
      transcriptsWrapEl.addEventListener("touchend", this.handleTranscriptTouchEnd, { passive: true });
      transcriptsWrapEl.addEventListener("touchcancel", this.handleTranscriptTouchEnd, { passive: true });
    }
    this.$nextTick(() => {
      this.setupTranscriptTeleprompterObserver();
      this.scrollTranscripts(true);
    });

    this.$refs.roomwrap.addEventListener("mouseup", () => {
      if (this.isHoldToTalk && this.recording) {
        this.stopRecording();
      }
    })

    this.$refs.toolbarwrap.addEventListener("mouseup", () => {
      if (this.isHoldToTalk && this.recording) {
        this.stopRecording();
      }
    })
    this.setupToolbarResizeObserver();

    if (this.enableAsrTimer) {
      const time = new Date();
      time.setSeconds(time.getSeconds() + this.asrTimeLeft);
      this.asrTimer = useTimer(time, false);
    }


    this.soundPlayerRecStart = this.$refs.start_rec_audio;
    this.soundPlayerRecStop = this.$refs.stop_rec_audio;
    this.soundPlayerTTS = this.$refs.tts_rec_audio;

    document.body.addEventListener('click', this._unlockAudio);
    document.body.addEventListener('touchstart', this._unlockAudio);

    this.eventData = {
      title: this.event.title,
      code: this.event.room_code,
      is_room_protected: this.event.is_room_protected,
      is_participating_disabled: this.event.is_participating_disabled,
      room_type: this.event.room_type
    };

    await this.getAvailableMicrophones();

    if (this.settings.selectedMicrophoneId) {
      this.selectedMicrophoneId = this.settings.selectedMicrophoneId;
    }

    navigator.mediaDevices.addEventListener('devicechange', this.getAvailableMicrophones);

    await this.getAvailableSpeakers();

    if (this.settings.selectedSpeakerId) {
      this.selectedSpeakerId = this.settings.selectedSpeakerId;
      if (this.soundPlayerTTS && typeof this.soundPlayerTTS.setSinkId !== 'undefined') {
        this.soundPlayerTTS.setSinkId(this.selectedSpeakerId).catch(error => {
          console.error('Error setting speaker:', error);
        });
      }
    }

    navigator.mediaDevices.addEventListener('devicechange', this.getAvailableSpeakers);

    this.setupInactivityListeners();
    this.startInactivityTimer();
  },
  created() {
    const transcribe = useTranscribe()
    const { transcripts, isRecording } = toRefs(transcribe)

    this.awsTranscripts = transcripts
    this.startFn = transcribe.start
    this.stopFn = transcribe.stop

    watch(isRecording, (val) => {
      this.awsIsRecording = val
    })

    const connectionQuality = useConnectionQuality({
      poorLatencyMs: 1500,
      staleHeartbeatMs: 60000,
      checkIntervalMs: 5000,
    })

    // Не реактивное поле намеренно: composable хранит ссылку на сокет.
    this.connectionQuality = connectionQuality

    watch(connectionQuality.connectionState, (value) => {
      this.connectionState = value
    }, { immediate: true })

    watch(connectionQuality.connectionLatency, (value) => {
      this.connectionLatency = value
    }, { immediate: true })

    watch(connectionQuality.connectionBars, (value) => {
      this.connectionBars = value
    }, { immediate: true })

    watch(connectionQuality.disconnectReason, (value) => {
      this.connectionDisconnectReason = value
    }, { immediate: true })

    watch(connectionQuality.connectionLabel, (value, oldValue) => {
      this.connectionLabel = value

      if (oldValue === undefined || value === oldValue) {
        this.lastLoggedConnectionBars = this.connectionBars
        return
      }

      this.$nextTick(() => {
        this.logConnectionChanged(oldValue, value)
      })
    }, { immediate: true })

    this._onCloseRoom = () => {
      forgetRoom(this._roomCode);
      this.emitSocketEvent("close_room", {});
      this.$store.dispatch("event/close");
      this.$router.replace("/");
      this.closeSocket();
    };

    this._onQuitRoom = () => {
      // Пользователь сам вышел — при следующем входе в эту комнату окно входа покажем снова.
      forgetRoom(this._roomCode);
      this.$store.dispatch("event/close");
      this.$router.replace("/");
      this.closeSocket();
    };

    this._onTriggerLanguageSelector = () => {
      this.showLanguagePicker();
    };

    emitter.on("close_room", this._onCloseRoom);
    emitter.on("quit_room", this._onQuitRoom);
    emitter.on('trigger_language_selector', this._onTriggerLanguageSelector);
    emitter.on('open-leave-session-modal', this.closeConference);
  },
  async beforeUnmount() {
    console.log("unmounting");
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener("popstate", this.handleBackButton);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('focus', this.handleWindowFocus);
    window.removeEventListener('online', this.handleWindowOnline);
    this.connectionQuality?.removeBrowserListeners();
    document.body.removeEventListener('click', this._unlockAudio);
    document.body.removeEventListener('touchstart', this._unlockAudio);
    const transcriptsWrapEl = this.$refs.transcriptsWrap;
    if (transcriptsWrapEl) {
      transcriptsWrapEl.removeEventListener("scroll", this.handleTranscriptScroll);
      transcriptsWrapEl.removeEventListener("wheel", this.handleTranscriptWheel);
      transcriptsWrapEl.removeEventListener("pointerdown", this.handleTranscriptPointerDown);
      transcriptsWrapEl.removeEventListener("touchstart", this.handleTranscriptTouchStart);
      transcriptsWrapEl.removeEventListener("touchmove", this.handleTranscriptTouchMove);
      transcriptsWrapEl.removeEventListener("touchend", this.handleTranscriptTouchEnd);
      transcriptsWrapEl.removeEventListener("touchcancel", this.handleTranscriptTouchEnd);
    }
    this.teardownTranscriptTeleprompterObserver();
    this.teardownToolbarResizeObserver();
    this.resetTranscriptTeleprompter();
    this.closeSocket();
    navigator.mediaDevices.removeEventListener('devicechange', this.getAvailableMicrophones);
    navigator.mediaDevices.removeEventListener('devicechange', this.getAvailableSpeakers);
    this.removeInactivityListeners();
    this.clearInactivityTimer();
    this.chromakeyIdleStop();
    Object.values(this.pendingFinalMessages).forEach((pendingEntry) => {
      if (pendingEntry && pendingEntry.timeoutId) {
        clearTimeout(pendingEntry.timeoutId);
      }
    });
    this.pendingFinalMessages = {};
    Object.values(this.stalePartialTimers).forEach((timeoutId) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
    this.stalePartialTimers = {};
    Object.values(this.partialPlaybackStates).forEach((state) => {
      if (state && state.intervalId) {
        clearInterval(state.intervalId);
      }
      if (state && state.flushTimeoutId) {
        clearTimeout(state.flushTimeoutId);
      }
    });
    this.partialPlaybackStates = {};
    if (this._onCloseRoom) {
      emitter.off('close_room', this._onCloseRoom);
    }
    if (this._onQuitRoom) {
      emitter.off('quit_room', this._onQuitRoom);
    }
    if (this._onTriggerLanguageSelector) {
      emitter.off('trigger_language_selector', this._onTriggerLanguageSelector);
    }
    emitter.off('open-leave-session-modal', this.closeConference);
    hotkeys.unbind('alt+h');
    hotkeys.unbind('alt+b, command+b');
    hotkeys.unbind(CAPTION_NUDGE_KEYS);
    clearTimeout(this.fontSizeIndicatorTimer);
    await this.releaseWakeLock();
  }
}
</script>

<style lang="scss">

.conference-meta {
  font-family: 'Inter', sans-serif;
  position: absolute;
  top: 50px;
  width: 100%;
  z-index: 5;

  &__title {
    font-weight: 700;
    font-size: 1.875rem;
    line-height: 2.2rem;
    text-transform: uppercase;
    text-align: center;

    /* White Audience */

    color: #FFFFFF;
    margin-bottom: 4px;
  }

  &__speaker {
    text-align: center;
    font-weight: 400;
    font-size: 1.4rem;
    line-height: 1.7rem;
    text-transform: capitalize;

    color: #C2C2C2;
  }

  &__logo {
    display: flex;

    img {
      width: 125px;
    }
  }
}

.room {
  height: 100%;
  width: 100%;
  padding: 0 0 30px;


  &-overlay {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    background: rgb(0,0,0);
    background: linear-gradient(90deg, rgba(0,0,0,0.6727065826330532) 60%, rgba(0,0,0,1) 97%, rgba(0,0,0,1) 100%);
    z-index: 4;
  }
}

.conference-room-layout {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}

.conference-room-layout > .room {
  flex: 1 1 auto;
  min-height: 0;
}

.screen {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-top: 30px;
  width: 70%;
  margin: auto;
  position: relative;

  @media all and (max-width: 1100px) {
    width: 100%;
  }

  &__scroll {
    width: 100%;
    margin: auto auto 0 auto;
    opacity: 0;
    visibility: hidden;

    &.visible {
      opacity: 1;
      visibility: visible;
      position: relative !important;
    }

    .scrollbar__content {
      width: 100%;
      height: 100%;
      min-height: 600px;
      display: flex;
    }

  }
  .screen__default {
    font-weight: 700;
    text-align: center;

    color: #FFFFFF;

    opacity: 0.2;
    margin: auto;
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-transform: uppercase;
    flex-direction: column;

    img {
      width: 5rem;
    }
  }
}

.toolbar {
  display: flex;
  margin-top: auto;
  width:100%;
  align-items: flex-end;

  &-left, &-right {
    display: flex;
  }

  &-filler {
    width: 75px;
    height: 75px;
    display: flex;
    margin: 0 20px;
  }

  .mute-control {
    border: 2px solid #464646;
    border-radius: 50%;
    width: 60px;
    height: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }

  .participants-info {
    background: rgba(43, 43, 43, 0.8);
    border: 2px solid #464646;
    border-radius: 50%;
    width: 75px;
    height: 75px;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.5s ease;

    @media (max-width: 1100px) {
      width: 60px;
      height: 60px;
    }

    &__icon {
      width: 32px;
      margin-bottom: 4px;
    }

    &__count {
      font-weight: 700;
      font-size: 0.875rem;
      line-height: 1.1rem;
      color: #FFFFFF;

      @media all and (max-width: 1100px) {
        font-size: 0.65rem;
        line-height: 0.9rem;
      }
    }

    &:hover {
      border-color: #6CBFFF;
    }
  }

  .settings-control {
    width: 48px;
    height: 48px;


    display: flex;
    align-items: flex-end;
    flex-direction: column;
    position: relative;
    z-index: 5;
    /*right: 30px;*/

    &__threedots {
      border: none;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;

      outline: none;
      cursor: pointer;
    }
  }

  .settings-menu {
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;
    position: absolute;
    bottom: 0;
    border-radius: 47px;

    &__options {
      padding-top: 26px;
    }

    &-item {
      display: flex;
      position: relative;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 20px;

      &:first-child {
        margin-top: 0;
      }

      img {
        max-width: 30px;
      }

      &__text {
        width: 146px;
        text-align: right;
        font-weight: 400;
        font-size: 1.125rem;
        line-height: 1.7rem;
        color: #FFFFFF;
        position: absolute;
        left: -160px;
      }
    }

    .font-changer {
      border-radius: 27.4641px;
      display: flex;
      flex-direction: column;
      width: 54px;
      align-items: center;
      &__bigger {
        text-align: center;
        padding: 18px 0 15px;
        border-bottom: 2px solid rgba(255, 255, 255, 0.25);
        display: inline-flex;
        justify-content: center;
      }

      &__smaller {
        text-align: center;
        padding: 15px 0 18px;
        display: inline-flex;
        justify-content: center;
      }
    }
  }

  .speech-control {
    position: relative;

    &__status {
      font-weight: 400;
      font-size: 0.75rem;
      line-height: 1rem;
      color: #FFFFFF;
      text-transform: uppercase;
      text-align: center;
      position: absolute;
      width: 100%;
      top: -28px;
    }

    .mic {
      width: 92px;
      height: 92px;
      justify-content: center;
      align-items: center;
      border-radius: 50%;
      cursor: pointer;
      margin: 0 24px;

      @media all and (max-width: 800px) {
        width: 67px;
        height: 67px;

        img {
          max-width: 32px;
          max-height: 32px;
        }
      }
    }

    .mic-stop, .mic.loader {
      background: #EA4335;
    }

    .mic-wave {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #EA4335;
    }

    #wave {
      height: 70px;
      width: 70px;
      fill: #fff;
    }

    @for $i from 1 through 9 {
      #Line_#{$i} {
        animation: micpulse 1s infinite;
        animation-delay: $i * .15s;
      }
    }

    @keyframes micpulse {
      0% {
        transform: scaleY(1);
        transform-origin: 50% 50%;
      }

      50% {
        transform: scaleY(.7);
        transform-origin: 50% 50%;
      }

      100% {
        transform: scaleY(1);
        transform-origin: 50% 50%;
      }
    }


    .timer {
      font-weight: 700;
      font-size: 1.375rem;
      line-height: 1.7rem;
      color: rgba(255, 255, 255, 0.5);
      text-align: center;
      margin-top: 10px;
      height: 27px;
      margin-bottom: 10px;

      span {
        min-width: 70px;
      }

      @media all and (max-width: 768px) {
        display: none;
      }
    }

    &.notAllowed {
      .mic {
        animation: none !important;
        opacity: 0.6;
        cursor: default;
      }

      .keyboard-input-btn {
        opacity: 0.6;
        cursor: default;
      }
    }
  }
}

.transcripts-wrap {
  gap: 20px;
  margin: auto;
  flex-grow: 1;
  display: flex;
  flex-direction: column-reverse;
  max-width: 100%;
}

.room.default .transcripts-wrap {
  --default-transcript-safe-top-inset: clamp(128px, 15vh, 190px);
  width: 100% !important;
  margin: 0;
  padding: 0 clamp(16px, 5vw, 72px);
  flex: 0 0 clamp(380px, 58vh, 720px);
  flex-direction: column;
  min-height: 0;
  height: clamp(380px, 58vh, 720px);
  max-height: calc(100% - 170px);
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  overscroll-behavior: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
  scroll-padding-top: calc(18px + var(--default-transcript-safe-top-inset));
}

.room.default .transcripts-wrap::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.room.default .transcripts-wrap::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 300px;
  z-index: 4;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(9, 12, 16, 0.98) 0,
    rgba(9, 12, 16, 0.98) 104px,
    rgba(9, 12, 16, 0.94) 142px,
    rgba(9, 12, 16, 0.74) 196px,
    rgba(9, 12, 16, 0.28) 256px,
    rgba(9, 12, 16, 0) 100%
  );
  transition: background 180ms ease, height 180ms ease, opacity 180ms ease;
}

.room.default .transcripts-wrap::after {
  content: '';
  position: fixed;
  top: 92px;
  left: 0;
  width: 100vw;
  height: 118px;
  z-index: 5;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(9, 12, 16, 0.96) 0,
    rgba(9, 12, 16, 0.94) 38px,
    rgba(9, 12, 16, 0.78) 78px,
    rgba(9, 12, 16, 0) 100%
  );
  transition: opacity 180ms ease;
}

.room.default .transcripts-wrap--review::before {
  height: 220px;
  background: linear-gradient(
    to bottom,
    rgba(9, 12, 16, 0.82) 0,
    rgba(9, 12, 16, 0.62) 78px,
    rgba(9, 12, 16, 0.28) 142px,
    rgba(9, 12, 16, 0) 100%
  );
}

.room.default .transcripts-wrap--review::after {
  opacity: 0;
}

.transcripts {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 30px;
  align-items: flex-end;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.125rem;
  gap: 20px;
  margin: auto;

  @media all and (max-width: 1100px) {
    padding: 0 15px;
  }

  color: #FFFFFF;

  &__item {
    padding: 15px;
    border-radius: 15px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    width: 100%;
    position: relative;

    &:first-child {
      margin-top: auto;
    }

    &--partial {
      background: #FFFFFF;
      color: #000000;

      .transcript__sendername {
        color: #000000;
      }

      .transcript__flag {
        margin-top: 5px;
      }
    }

    &.keyboard-input {
      padding-right: 60px;
      position: relative;
      display: flex;

      .keyboard-input__send {
        width: 30px;
        display: flex;
        position: absolute;
        top: 0;
        right: 15px;
        align-items: center;
        height: 100%;

        img {
          max-width: 100%;
        }
      }
    }

    .transcript__original.noTranslations {
      opacity: 0.5;
      border-bottom: 1px solid grey;
      padding-bottom: 4px;

      .transcript__text {
        font-size:  v-bind('fontSize * 0.6 + fontDim');
        line-height: 150%;
      }

      .transcript__flag {
        width: 24px;
        height: 24px;
      }
    }

    .transcript__original {

      .transcript__read {
        width: 24px;
        position: absolute;
        right: 15px;
        bottom: 10px;
      }
    }

    .transcript__translation {
      margin-top: 0px;
    }
  }
}

.room.default .transcripts {
  max-width: 100%;
  align-items: center;
  flex: 0 0 auto;
  flex-grow: 0 !important;
  justify-content: flex-start;
  gap: 0;
  height: auto;
  min-height: 100%;
  margin: 0;
  overflow: visible;
  /* One line of headroom at the bottom: while the teleprompter or the top-spacer animation
     is still catching up with a freshly wrapped line, that line stays inside the scroll box
     instead of being clipped by its overflow. */
  padding: calc(18px + var(--default-transcript-safe-top-inset, 0px) + var(--default-transcript-top-spacer, 0px)) 0 calc(22px + v-bind(defaultTranscriptLineHeightCss));
}

.room.default .transcripts__item {
  width: 100%;
  flex: 0 0 auto;
  background: transparent;
  border: 0;
  border-radius: 0;
  padding: 0;
}

.room.default .transcripts__item:first-child {
  margin-top: 0;
}

.room.default .transcripts__item--continuation {
  margin-top: -6px;
}

.room.default .transcripts__feed-list {
  width: min(100%, 1100px);
  max-width: 1100px;
  align-self: center;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 0;
}

.room.default .transcripts__feed-list--narrow {
  width: min(100%, 720px);
}

.room.default .transcripts__speaker-group {
  width: 100%;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
}

.room.default .transcripts__item--partial.transcripts__item--partial-new {
  animation: transcript-partial-fade-in 500ms ease both;
}

@keyframes transcript-partial-fade-in {
  from {
    opacity: 0.4;
  }

  to {
    opacity: 1;
  }
}

.room.default .transcripts__item--partial {
  background: transparent;
  color: #FFFFFF;
  opacity: 1;
}

.room.default .transcript__original,
.room.default .transcript__translation {
  background: transparent;
  padding: 8px 8px 8px 0;
}

.room.default .transcripts__item--continuation .transcript__original,
.room.default .transcripts__item--continuation .transcript__translation {
  padding-top: 0;
}

.room.default .transcripts__item--partial > .transcript__text {
  padding: 8px 8px 8px 0;
}

.room.default .transcript__sendername {
  justify-content: flex-start;
  margin-bottom: 16px;
  color: var(--color-text-neutral-high);
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0;
  width: 100%;
  gap: 8px;
}

.room.default .transcript__original.noTranslations {
  opacity: 1;
  border-bottom: 0;
  padding: 8px 8px 0 0;
}

.room.default .transcript__original.noTranslations + .transcript__translation {
  margin-top: 10px;
  padding: 0 8px 8px 0;
}

.room.default .transcript__translation--reserved {
  visibility: hidden;
  pointer-events: none;
}

.room.default .transcript__translation--revealing {
  box-sizing: border-box;
  overflow: hidden;
  transition: height 240ms ease;
}

.room.default .transcript__text--translation {
  animation: transcript-translation-fade-in 220ms ease both;
}

.room.default .transcript__text--translation-placeholder {
  text-shadow: none;
}

@keyframes transcript-translation-fade-in {
  from {
    opacity: 0;
    transform: translateY(2px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .room.default .transcript__translation--revealing {
    transition: none;
  }

  .room.default .transcript__text--translation {
    animation: none;
  }
}

.room.default .transcript__original.noTranslations .transcript__text {
  color: var(--color-text-neutral-medium);
  /* Follows the caption font chosen in Caption Settings. */
  font-family: inherit;
  /* Original shown next to a translation tracks the caption font size at 60%
     (12px at the default 20px), same ratio as the other views. */
  font-size: v-bind('fontSize * 0.6 + fontDim');
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0;
  text-shadow: none;
  vertical-align: middle;
}

.room.default .transcript__translation--pending {
  display: flex;
  align-items: center;
  min-height: 32px;
}

.room.default .transcript__typing-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 18px;
}

.room.default .transcript__typing-indicator span {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--color-text-neutral-low);
  opacity: 0.35;
  animation: transcript-typing-dot 1100ms ease-in-out infinite;
}

.room.default .transcript__typing-indicator span:nth-child(2) {
  animation-delay: 160ms;
}

.room.default .transcript__typing-indicator span:nth-child(3) {
  animation-delay: 320ms;
}

@keyframes transcript-typing-dot {
  0%,
  70%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }

  35% {
    opacity: 0.9;
    transform: translateY(-2px);
  }
}

.room.default .transcript__sender-flag {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.room.default .transcript__sender-flag--welcome {
  width: 20px;
  height: 20px;
  border-radius: 0;
  object-fit: contain;
}

.room.default .transcript__sendername--marker {
  position: sticky;
  top: 18px;
  z-index: 20;
  align-self: flex-start;
  width: fit-content;
  max-width: 100%;
  margin-top: 20px;
  margin-left: 0;
  margin-right: 0;
}

.room.default .transcript__sendername--welcome {
  margin-top: 0;
  margin-bottom: 22px;
  color: #fff;
  font-weight: 700;
}

.room.default .transcript__sendername--partial {
  opacity: 0.9;
}

.room.default .transcript__original--welcome {
  padding-top: 0;
}

.room.default .transcript__text.transcript__text--welcome {
  text-shadow: none;
}

.room.default .transcript__text--welcome p {
  margin: 0;
}

.room.default .transcript__text--welcome p + p {
  margin-top: 1.45em;
}

.room.default .transcript__flag {
  display: none;
}

.room.default .transcript__text {
  text-align: v-bind('defaultTranscriptAlign');
  line-height: 1.45;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}

/**
Caption Settings → Theme: Light (Regular view only), colors taken from the iOS app. Covers the
page background, the session title, the captions, the top bar, the bottom toolbar, the keyboard
input and the connection indicator; the popups / dropdown menus keep the dark UI.
 */
.conference-room-layout.caption-theme-light {
  background: #F3F6FB;

  .session-title-bar .session-title-text {
    color: #000000;
  }

  .session-title-bar .session-title-action {
    color: rgba(0, 0, 0, 0.45);

    &:hover {
      color: rgba(0, 0, 0, 0.8);
    }
  }

  /* White icons in the bars are drawn black. The language flag and the Leave icon keep
     their colors: top bar icons are opted in with .bar-icon, the toolbar ones are all mono. */
  .bar-icon,
  .bottom-bar-button img,
  .bottom-bar-button-split img,
  .bottom-bar-button-small-split img {
    filter: brightness(0);
  }
  /* Top bar */
  .topBar {
    background: #E6E9EE;

    .top-bar-button {
      color: #000000;

      &:hover,
      &.pressed {
        background: rgba(0, 0, 0, 0.06);
      }
    }

    .top-bar-badge {
      background: rgba(0, 0, 0, 0.55);
      color: #FFFFFF;
    }

    .top-bar-button--leave {
      background: #F7E7EA;

      &:hover {
        background: #F1D5DA;
      }

      .text-text-neutral-red {
        color: #DB5A62;
      }
    }
  }

  /* Mobile Caption Settings bar: its category row is a .topBar (styled above), these are
     the option row under it */
  .caption-settings-bar__options {
    background: #DCE0E6;
  }

  .caption-settings-bar__label {
    color: #000000;
  }

  .caption-settings-bar__chip {
    background: rgba(0, 0, 0, 0.06);
    color: #000000;
  }

  .caption-settings-bar__chip--active {
    background: rgba(0, 0, 0, 0.16);
  }

  .caption-settings-bar__step {
    background: #FFFFFF;
    border-color: #DCE0E6;
  }

  .caption-settings-bar__input {
    color: #000000;
    border-color: rgba(0, 0, 0, 0.25);
  }

  /* Bottom toolbar */
  .session-toolbar-panel {
    background: #CDD6DD;
  }

  .toolbar .timer {
    color: rgba(0, 0, 0, 0.5);
  }

  .bottom-bar-button,
  .bottom-bar-button-split,
  .bottom-bar-button-small-split {
    background: #FFFFFF;
    color: #000000;

    &:hover {
      background: #F5F6F7;
    }
  }

  .bottom-bar-button--active,
  .bottom-bar-button-small-split.pressed {
    background: #E2E6EB;
  }

  /* Chevron halves of the split buttons */
  .bottom-bar-button-split--secondary,
  .bottom-bar-button-small-split--secondary {
    background: #EFEFEF;
    border-left-color: #C7C8CC;

    &:hover,
    &.pressed {
      background: #E4E5E7;
    }
  }

  /* Recording keeps the primary blue so the "listening" state stays obvious. */
  .bottom-bar-button-split.pressed {
    background: var(--color-primary);
  }

  /* Keyboard input: same text color as the captions. Sits outside .room, so not under .room.default. */
  .keyboard-input-bar .keyboard-input {
    color: #0C1116;
    border-color: rgba(12, 17, 22, 0.5);

    .transcript__text {
      color: #0C1116;
    }
  }

  .connection-indicator {
    background: rgba(230, 233, 238, 0.8);

    .connection-indicator__bar {
      background: #000000;
    }

    .connection-indicator__label {
      color: rgba(0, 0, 0, 0.6);
    }
  }

  .room.default {
    .transcripts,
    .transcripts__item--partial {
      color: #0C1116;
    }

    .transcript__text {
      text-shadow: none;
    }

    .transcript__sendername,
    .transcript__sendername--welcome {
      color: rgba(12, 17, 22, 0.89);
    }

    .transcript__original.noTranslations .transcript__text {
      color: rgba(12, 17, 22, 0.75);
    }

    .transcript__typing-indicator span {
      background: rgba(12, 17, 22, 0.55);
    }

    /* Same fades as the dark theme, toward white instead of the dark page background. */
    .transcripts-wrap::before {
      background: linear-gradient(
        to bottom,
        rgba(243, 246, 251, 0.98) 0,
        rgba(243, 246, 251, 0.98) 104px,
        rgba(243, 246, 251, 0.94) 142px,
        rgba(243, 246, 251, 0.74) 196px,
        rgba(243, 246, 251, 0.28) 256px,
        rgba(243, 246, 251, 0) 100%
      );
    }

    .transcripts-wrap::after {
      background: linear-gradient(
        to bottom,
        rgba(243, 246, 251, 0.96) 0,
        rgba(243, 246, 251, 0.94) 38px,
        rgba(243, 246, 251, 0.78) 78px,
        rgba(243, 246, 251, 0) 100%
      );
    }

    .transcripts-wrap--review::before {
      background: linear-gradient(
        to bottom,
        rgba(243, 246, 251, 0.82) 0,
        rgba(243, 246, 251, 0.62) 78px,
        rgba(243, 246, 251, 0.28) 142px,
        rgba(243, 246, 251, 0) 100%
      );
    }
  }
}

.transcript__text {
  font-weight: 400;
  font-size: v-bind('fontSize + fontDim');
  line-height: 150%;
  word-break: break-word;
}

.transcript__time {
  position: absolute;
  right: 15px;
  font-weight: 400;
}

.transcript__sendername {
  font-weight: 700;
  margin-bottom: 10px;
  color: #fff;
  display: flex;
  align-items: center;
}

.transcript__flag {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  overflow: hidden;
  margin-right: 10px;
  float: left;
}

.chevron-to-bottom-scroller {
  position: absolute;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  background: rgba(43, 43, 43, 0.8);
  bottom: 8px;
  right: 14px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    max-width: 70%;
  }

  .new-indicator {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: flex;
    background: #EA4335;
    overflow: hidden;
    position: absolute;
    top: -2px;
    right: 2px;
  }
}

.keyboard-input-bar {
  flex: 0 0 auto;
  width: 100%;
  padding: 12px clamp(16px, 5vw, 72px) 0;

  /* below md the toolbar has no padding of its own, so keep the box off its edge */
  @media (max-width: 767px) {
    padding-bottom: 12px;
  }
}

/* the bar is a sibling of .room, not a descendant, so none of these selectors may be
   prefixed with .room — they only have to out-weigh .transcripts__item.keyboard-input,
   which they do on source order at equal specificity */
.keyboard-input-bar .keyboard-input {
  /* same column as .transcripts__feed-list, so the box lines up with the captions */
  width: min(100%, 1100px);
  margin: 0 auto;
  min-height: 64px;
  position: relative;
  align-items: center;
  padding: 10px 60px 10px 16px;
  background: transparent;
  color: #FFFFFF;
  border: 2px solid var(--color-elements-blue-translucent);
  border-radius: 12px;
}

.keyboard-input-bar .transcript__flag {
  display: none;
}

/* the input keeps a fixed size instead of tracking the caption font-size setting */
.keyboard-input-bar .transcript__text {
  font-size: 24px;
  text-align: v-bind('defaultTranscriptAlign');
  line-height: 1.45;
  text-shadow: none;
  padding: 0;
}

/* Hiding the bars slides them off-screen like the iOS app: the top bar up, the toolbar down.
   The session title row sits under the top bar, so it is revealed as the bar slides away. */
.session-bar {
  transition: transform 180ms cubic-bezier(0.4, 0, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.session-bar--top.session-bar--hidden {
  transform: translateY(-100%);
}

.session-bar--bottom.session-bar--hidden {
  transform: translateY(100%);
}

.connection-indicator {
  bottom: 1rem;
  /* Same timing as .session-bar, so it moves together with the toolbar. */
  transition: bottom 180ms cubic-bezier(0.4, 0, 0.2, 1);

  // on mobile the toolbar spans the full width, so lift the indicator above it while it's shown
  @media (max-width: 767px) {
    bottom: calc(1rem + var(--toolbar-offset, 0px));
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.listening-indicator {
  position: absolute;
  left: 50%;
  transform: translateX(-50%) scale(1);
  transform-origin: center center;
  bottom: 72px;
  z-index: 3;
  width: 12.5px;
  height: 12.5px;
  border-radius: 50%;
  background: #6CBFFF;
  pointer-events: none;
  animation: listening-breathe 1.75s ease-in-out infinite;
}

@keyframes listening-breathe {
  0% {
    transform: translateX(-50%) scale(1);
    opacity: 0.74;
  }

  50% {
    transform: translateX(-50%) scale(1.42);
    opacity: 1;
  }

  100% {
    transform: translateX(-50%) scale(1);
    opacity: 0.74;
  }
}

@media all and (max-width: 800px) {
  .listening-indicator {
    bottom: 56px;
    font-size: 0.88rem;
  }
}


.speech__wrap {
  display: flex;
  justify-content: space-evenly;
  align-items: flex-end;

  .speech-mode-selector {
    cursor: pointer;
  }
}

.user-language-selector {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.room-modals {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 4;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hidden-audio-players {
  display: none !important;
}

.pulsing {
  animation: micpulse 1.5s infinite;
}

.loader-4 {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: relative;
}
.loader-4 .spinner {
  position: relative;
  width: 100%;
  height: 100%;
}
.loader-4 .spinner .loader-circle-1 {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0.3rem #fff solid;
  border-radius: 50%;
  border-left: 0.3rem #6CBFFF solid;
  -webkit-animation: loaderspinner 1.5s infinite;
  animation: loaderspinner 1.5s infinite;
}
.loader-4 .spinner .loader-circle-2 {
  position: absolute;
  top: 10%;
  left: 10%;
  width: 80%;
  height: 80%;
  border: 0.3rem #fff solid;
  border-radius: 50%;
  border-left: 0.3rem #6CBFFF solid;
  -webkit-animation: loaderspinner 1.5s 200ms infinite;
  animation: loaderspinner 1.5s 200ms infinite;
}
.loader-4 .spinner .loader-circle-3 {
  position: absolute;
  top: 20%;
  left: 20%;
  width: 60%;
  height: 60%;
  border: 0.3rem #fff solid;
  border-radius: 50%;
  border-left: 0.3rem #6CBFFF solid;
  -webkit-animation: loaderspinner 1.5s 350ms infinite;
  animation: loaderspinner 1.5s 350ms infinite;
}
@-webkit-keyframes loaderspinner {
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(360deg);
  }
}
@keyframes loaderspinner {
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(360deg);
  }
}



@media all and (max-height: 900px) {
  .screen {
    padding-top: 10px;
  }

  .transcripts {

    &__item {
      margin-bottom: 16px;
      padding: 12px;
    }
  }

  .conference-meta {
    top: 0;

    &.mobileMeta {
      z-index: 5;
    }
  }

  .mic {
    width: 100px;
    height: 100px;
  }

  .timer {
    margin-bottom: 0;
  }
}


@media all and (max-height: 700px) {
  .screen {
    padding-top: 10px;
  }
}

@media all and (max-width: 800px) {

  .meta-footer {
    background: #3E3E3E;
    border-bottom: 1px solid #C2C2C2;
    box-shadow: 0px 8px 25px rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;

    .conference-meta__title {
      font-size: 0.8rem;
      margin: 0;
    }

    .conference-meta__speaker {
      font-size: 0.75rem;
    }
  }
}

.mobile-meta-header {
  display: flex;
  justify-content: space-between;
  padding: 20px 16px;
  background: #202020;
  border-bottom: 1px solid #C2C2C2;
  box-shadow: 0px 8px 25px rgba(0, 0, 0, 0.5);

  .mobile-info-btn {
    svg {
      width: 30px;
      height: 30px;
    }
  }
}

.forum-info-popup {
  font-family: "Inter";
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  position: absolute;
  z-index: 9;
  background: #202020;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;

  .conference-close {
    position: absolute;
    top: 16px;
    right: 16px;
  }

  .forum-info__text {
    color: #6CBFFF;
    font-size: 1.2rem;
    text-align: center;
    margin-bottom: 32px;
  }

  .forum-info__links {
    display: flex;
    justify-content: space-between;
    margin-bottom: 32px;
  }

  .forum-info__footer {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    justify-content: center;
    text-align: center;

    b {
      color: #fff;
      font-size: 1.1rem;
    }

    span {
      color: #fff;
      font-size: 0.9rem;
      opacity: 0.6;
    }
  }
}

.loader {
  position: relative;
  overflow: hidden;
}

.default-03 .loader-container {
  width: 80px;
  height: 80px;
  animation: loaderspin 2.5s infinite cubic-bezier(0.17, 0.72, 0.55, 1.66);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.default-03 .ball {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, 0);
  width: 11%;
  height: 50%;
  background-color: transparent;
  transform-origin: bottom center;
  overflow: visible;
}
.default-03 .ball:nth-of-type(2) {
  transform: translate(-50%, 0) rotate(36deg);
}
.default-03 .ball:nth-of-type(3) {
  transform: translate(-50%, 0) rotate(72deg);
}
.default-03 .ball:nth-of-type(4) {
  transform: translate(-50%, 0) rotate(108deg);
}
.default-03 .ball:nth-of-type(5) {
  transform: translate(-50%, 0) rotate(144deg);
}
.default-03 .ball:nth-of-type(6) {
  transform: translate(-50%, 0) rotate(180deg);
}
.default-03 .ball:nth-of-type(7) {
  transform: translate(-50%, 0) rotate(216deg);
}
.default-03 .ball:nth-of-type(8) {
  transform: translate(-50%, 0) rotate(252deg);
}
.default-03 .ball:nth-of-type(9) {
  transform: translate(-50%, 0) rotate(288deg);
}
.default-03 .ball:nth-of-type(10) {
  transform: translate(-50%, 0) rotate(324deg);
}
.default-03 .ball::before {
  position: absolute;
  content: "";
  top: 0;
  left: 0;
  width: 100%;
  height: 56%;
  border-radius: 70px;
  background-color: #fff;
}
.default-03 .ball::after {
  position: absolute;
  content: "";
  bottom: -100;
  left: 0;
  width: 100%;
  height: 56%;
  border-radius: 70px;
  background-color: #ffc7bd;
  animation: default3 2.5s infinite linear;
  opacity: 0;
}
@keyframes loaderspin {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
@keyframes default3 {
  20% {
    opacity: 1;
  }
  40% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.scroll-area {
  position: relative;
  margin: auto;
  width: 600px;
  height: 400px;
}

@keyframes micpulse
{
  0%
  {
    box-shadow: 0 0 0 0 rgba(255,255,255,.7), 0 0 0 0 rgba(255,255,255,.7);
  }

  100%
  {
    box-shadow: 0 0 0 0 rgba(255,255,255,.0), 0 0 0 100px rgba(255,255,255,0);
  }
}

#keyboard_input_field {
  caret-color: currentColor !important;
  outline: none !important;
  flex: 1;
  /* block, not flex: an empty contenteditable flex container generates no line box, so the
     caret has nothing to sit on and stays invisible until the first character is typed */
  display: block;
  min-height: calc(24px * 1.45);
  word-break: break-word;
}

.clear,
.clear:before,
.clear:after{
  content:"";
  display:block;
  clear:both;
}

/**
default mode
 */

/* green screen */
.room.chromakey {
  background: v-bind('settings.chroma_overlay_color');
  position: fixed;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100%;
  z-index: 1000;

  .toolbar {
    display: none;
  }

  .screen__default {
    display: none;
  }

  .screen {
    overflow: hidden;
  }

  .screen__scroll {
    min-height: v-bind('fontSize * 3 * 1.5 + fontDim + 30');
    max-height: v-bind('fontSize * 3 * 1.5 + fontDim + 30');
    overflow: hidden;
    margin-bottom: 20px;

    .scrollbar__content {
      min-height: v-bind('fontSize * 3 * 1.5 + fontDim + 30');
      overflow: hidden;

    }
  }

  .scrollbar__thumb--vertical {
    display: none;
  }

  .scrollbar__thumb {
    display: none;
  }

  .scrollbar__thumbPlaceholder {
    display: none;
  }

  .scrollbar__thumbPlaceholder--vertical {
    overflow: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;
    pointer-events: none;
    touch-action: none;
  }
  .scrollbar__thumbPlaceholder--vertical::-webkit-scrollbar {
    display: none;
  }

  .transcripts {
    &.narrower {
      padding-left: 20%;
      padding-right: 20%;
    }
  }

  .captions-wrapper {
    // Anchors the drag-time ghost track to the window's box.
    position: relative;
    width: 100%;
  }

  .transcripts__item {
    text-align: v-bind('mode.align');
    background: transparent;
    display: flex;
    width: 100%;
    align-items: flex-end;
    justify-content: v-bind(transcriptsJustify);
    margin-bottom: 0px;
    padding-bottom: 0px;
    padding-top: 0px;
    overflow: visible;
  }

  .chromakey-viewport {
    position: relative;
    width: 100%;
    height: v-bind(chromakeyViewportHeight);
    overflow: hidden;
    cursor: grab;
    // Dragged with a finger too, so the browser must not scroll or zoom on it.
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  }

  .chromakey-viewport--dragging {
    cursor: grabbing;
    // Marks the box the drag is limited by. Dashed dark over a light inset line, so it reads
    // on any background color.
    outline: 2px dashed rgba(0, 0, 0, 0.6);
    outline-offset: -2px;
    box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.6);
  }

  .chromakey-track--ghost {
    opacity: 0.4;
    pointer-events: none;
  }

  // Magenta, as in design tools: it stands out from every chroma color on offer.
  .chromakey-snap-guide {
    position: fixed;
    z-index: 400;
    pointer-events: none;
    background: #FF2BD6;

    &--vertical {
      top: 0;
      bottom: 0;
      width: 1px;
    }

    &--horizontal {
      left: 0;
      right: 0;
      height: 1px;
    }
  }

  .chromakey-track {
    position: absolute;
    left: 0;
    right: 0;
    bottom: v-bind(chromakeyBottomGutterPx);
    padding: 0 1rem;
    will-change: transform;
  }

  .chromakey-track--sliding {
    transition: transform 240ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .chromakey-caption {
    background: #000000;
    /* Vertical padding has to scale with the text: line-height is 150%, so a fixed rem
       stops covering the line box past ~48px and green shows through between lines.
       The rem floor keeps the smaller sizes looking exactly as they did. */
    padding: max(0.3rem, 0.16em) 1rem;
    color: #FFFFFF;
    display: inline;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    font-weight: 400;
    font-size: v-bind('fontSize + fontDim');
    line-height: 150%;
    word-break: break-word;
    white-space: pre-line;
  }

  @media (prefers-reduced-motion: reduce) {
    .chromakey-track--sliding {
      transition: none;
    }
  }

}

/* Short status chip under the top bar: the font size after a + / − key press, and how to use
   the Green Screen caption drag. */
.room-key-indicator {
  position: fixed;
  z-index: 450;
  top: 94px;
  left: 50%;
  transform: translateX(-50%);
  max-width: calc(100% - 32px);
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.7);
  color: #FFFFFF;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  pointer-events: none;
}

// Force close button to show in conference room
.wrapper .main-header .conference-close {
  top: 16px;
  right: 16px;
}

.vue-swatches__swatch--selected {
  position: relative;

  //default checkmark
  .vue-swatches__check__wrapper {
    display: none !important;
  }

  //custom checkmark
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 26px;
    height: 26px;
    border-radius: 100%;
    background-color: rgba(0, 0, 0, 0.2);
    background-image: url("@/assets/icons/white-checkmark.svg");
    background-size: 18px;
    background-position: center;
    background-repeat: no-repeat;
    pointer-events: none;
  }
}

.bottom-bar-button {
  //styles to prevent text selection and touch behaviors for the bottom bar
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  span {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    pointer-events: none;
  }

  img {
    user-select: none;
    -webkit-user-select: none;
    pointer-events: none;
  }
}

.room-view-container {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
